/**
 * Typesense-powered predictive search (2026-08-01, am26-typesense-search draft).
 *
 * Deliberately a STANDALONE file, not a change to custom-async.js — that asset is
 * ~270KB and this repo's docs/theme-changes.md documents a real corruption incident
 * from retranscribing a large theme file. This script does not modify or replace it;
 * it intercepts the same #search_main input in the capture phase (stopImmediatePropagation)
 * so the theme's own predictive-search fetch never fires, then renders its own results
 * into the same #livesearch target the theme already styles.
 *
 * Config: window.typesenseConfig must be set (see snippet include) with
 * { host, port, protocol, searchKey, collection }. searchKey is the SEARCH-ONLY
 * Typesense key — safe to expose client-side, cannot write or use the admin key's
 * privileges. If unset, this script no-ops and the theme's native search runs
 * untouched (fail open, never fail closed on search).
 */
(function () {
  "use strict";

  var CONFIG = window.typesenseConfig;
  /* `market` intră în validarea de pornire: fără o piață cunoscută nu putem
     construi filtrul de izolare, iar a ghici una ar putea servi produsele altei
     piețe. Fail-open ca restul fișierului — căutarea nativă a temei preia. */
  if (!CONFIG || !CONFIG.host || !CONFIG.searchKey ||
      !(CONFIG.market === "ro" || CONFIG.market === "bg")) {
    // Not configured — let the theme's own predictive search run unmodified.
    return;
  }

  var MIN_QUERY_LENGTH = 2;
  var DEBOUNCE_MS = 150;
  var RESULT_LIMIT = 8;
  /* Câte GRUPURI cerem de la Typesense, înainte de colapsarea pe familie.
     Mai mult decât RESULT_LIMIT, deliberat: din 2026-08-24 mai multe rânduri se
     pot uni într-unul singur (variantele de montare ale aceluiași model), deci un
     `per_page` egal cu limita de afișare ne lasă cu prea puține rânduri.
     Măsurat pe „beolab": Typesense întorcea 8 grupuri, dintre care 5 erau același
     Beolab 8 — după colapsare rămâneau 5 rânduri, iar Beolab 90, 18 și 28 (aflate
     pe pozițiile 10-15) nu ajungeau niciodată în răspuns.
     24 = 3× limita: acoperă chiar și un model cu multe montări, fără să încarce
     inutil un dropdown care tot 8 rânduri arată. */
  var FETCH_GROUP_LIMIT = 24;

  /**
   * Canonical vendor names, exactly as they appear in Typesense's `vendor` field.
   * Pulled live from Supabase `products.brand` on 2026-08-03 (98 distinct real
   * vendors; 4 non-brand rows excluded — "www.AudioMonkey.ro", "Records",
   * "Audio Monkey", "Vinil" — none of which is a product vendor).
   *
   * This is the brand-intent detection list for the "I want brand X" search
   * branch (docs/search-ranking-review-codex-response-2026-08-03.md, "Regula
   * generală propusă"). It intentionally does NOT special-case Audio-Technica —
   * that fix is the SAME general mechanism (normalize + exact match), not a
   * one-off rule, per the review's explicit instruction not to solve it only by
   * raising query_by_weights.vendor.
   */
  /* 2026-10-07, auditul de rutare („BRAND NERECUNOSCUT”): „Fosi Audio”, „JBL Synthesis”
     și „Sudio” lipseau deși au produse vizibile (Fosi era prins doar prin aliasul
     „fosi”, deci „fosi audio” cădea pe căutare de text); „SKNIPLAY” era o greșeală
     de scriere — vendorul din Shopify e „Skiniplay”, iar „SKNIPLAY” nu potrivea
     niciun produs.
     2026-10-19: „Rotel”, „Indiana Line” și „Audio Pro” — produse activate pe 19.10 (doar .ro),
     cu colecții de brand și marcă × categorie create aceeași zi.
     2026-10-22: „Hegel” și „Chord Company” — produse activate pe 22.10 (doar .ro), cu colecțiile de
     brand `hegel` și `chord-company` create aceeași zi. „Chord Electronics” se adaugă odată cu colecția lui. */
  var KNOWN_VENDORS = [
    "AIAIAI", "Acoustic Energy", "Amazon", "Anker", "AntLion Audio",
    "Architettura Sonora", "Argon Audio", "ArtSound", "Arylic", "Atacama",
    "Audio Pro", "Audio-Technica", "Austrian Audio", "Avax", "B-SYSTEM", "Bang & Olufsen",
    "Beats", "Beyerdynamic", "Bluesound", "Bose", "Bowers & Wilkins",
    "Cambridge Audio", "Chord Company", "Cyrus Audio", "DELL", "DEVIALET", "DVDO", "Dali",
    "Dan Clark Audio", "Denon", "EPOS", "EarMen", "Edifier", "Elac", "Epson",
    "EverSolo", "Fiio", "Focal", "Fosi Audio", "GP", "Genelec", "Goldring", "Google",
    "Harman Kardon", "Hegel", "HiFiMAN", "Huawei", "Indiana Line", "JBL", "JBL Synthesis", "JVC", "KEF", "Kanto Audio", "Kanto Mounts",
    "Klipsch", "LG", "LOEWE", "Lenco", "Magnat", "Marantz", "Meze Audio",
    "Milty", "Monitor Audio", "Musical Fidelity", "NAD", "NAGAOKA", "NAIM", "Occhio", "Onkyo",
    "Optoma", "POWERGRIP", "PSB Speakers", "Pioneer DJ", "Polk Audio",
    "Primare", "Pro-Ject", "Q Acoustics", "QED", "R_volution", "Realme",
    "Rotel", "Ruark Audio", "SCP", "SENNHEISER", "Samsung", "Skiniplay", "SilentPower",
    "Sonos", "Sony", "Stack Audio", "StormAudio", "Sudio", "Sumiko", "Taga Harmony",
    "Technics", "Triangle", "Velodyne Acoustics", "Visual Experience",
    "Volumio", "WIREWORLD", "We by Loewe", "Wharfedale", "WiiM", "Xgimi",
    "Xiaomi", "Yamaha", "Zidoo", "iFi Audio"
  ];

  /* Strips diacritics (NFD decompose + drop combining marks) so "Ölac"-style
   * accidental input still normalizes the same as "Olac" would — belt-and-braces,
   * since none of today's vendor names actually carry one, but a future brand
   * might. */
  function stripDiacritics(str) {
    return str.normalize ? str.normalize("NFD").replace(/[̀-ͯ]/g, "") : str;
  }

  /**
   * Normalizes a string for brand-intent comparison: lowercase, diacritics
   * stripped, every run of hyphens/punctuation/whitespace collapsed to a single
   * space, then trimmed. Both the typed query AND every KNOWN_VENDORS entry are
   * run through this same function before comparing — that symmetry is what
   * makes "audio technica" (no hyphen, lowercase) equal "Audio-Technica" (hyphen,
   * mixed case) without a per-brand special case.
   *
   * Deliberately COLLAPSES hyphens to a space rather than deleting them:
   * "Bowers-Wilkins" and "Bowers & Wilkins" both become "bowers wilkins", but so
   * does "Bowers Wilkins" typed plainly — all three are the same brand-intent
   * query, and none of them should accidentally glue two words together into an
   * unmatchable token.
   */
  function normalizeBrandQuery(str) {
    return stripDiacritics(String(str || ""))
      .toLowerCase()
      .replace(/[-_&,.'’]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  /**
   * Aliasuri de brand — cum scriu oamenii de fapt, nu cum se cheamă marca oficial.
   *
   * Fiecare intrare de aici a fost GĂSITĂ ÎN TRAFIC REAL, nu presupusă: 199 de
   * căutări din GA4 (5–19 august 2026) rulate prin motor, apoi filtrate după
   * „termen scurt care nu e recunoscut ca brand". Ce ieșea înainte, măsurat:
   *
   *   „b&o"     -> Bowers & Wilkins 804 D3 + cabluri SCP   (nu B&O!)
   *   „ifi"     -> mergea din întâmplare, prin potrivire pe titlu
   *   „meze"    -> idem; „psb", „svs", „fosi", „kanto" la fel
   *   „Tehnics" -> mergea prin toleranța la typo, nu prin recunoaștere
   *   „bowers"  -> mergea pe titlu, dar fără ruta de brand
   *
   * Diferența nu e „găsește sau nu", ci CE rută urmează: un alias recunoscut intră
   * pe ruta de brand — ordinea din colecție, rândul „Vezi toate cele N produse",
   * Enter care duce la colecție. Fără alias, rămâne o căutare de text oarecare.
   *
   * Cheile se scriu deja normalizate (litere mici, fără & și cratime), fiindcă
   * `normalizeBrandQuery` face aceeași transformare pe ce tastează utilizatorul.
   * De aceea „b&o" se scrie aici „b o" — normalizarea colapsează & la spațiu.
   */
  var VENDOR_ALIASES = {
    "b o": "Bang & Olufsen",          // „b&o", „B&O", „b & o"
    "bang olufsen": "Bang & Olufsen", // fără &, cum scrie multă lume
    "bo": "Bang & Olufsen",
    "b w": "Bowers & Wilkins",        // „b&w"
    "bowers": "Bowers & Wilkins",
    "bowers wilkins": "Bowers & Wilkins",
    "at": "Audio-Technica",
    "audio technika": "Audio-Technica",
    "audiotechnika": "Audio-Technica", // apare în GA4 cu 116 căutări
    "audiotechnica": "Audio-Technica",
    "tehnics": "Technics",             // fără „c”, cea mai frecventă scriere greșită
    "ifi": "iFi Audio",
    "meze": "Meze Audio",
    "psb": "PSB Speakers",
    "svs": "SVS",
    "fosi": "Fosi Audio",
    "kanto": "Kanto Audio",
    "dan clark": "Dan Clark Audio",
    "musical fidelity": "Musical Fidelity",
    "mf": "Musical Fidelity",
    "q acoustics": "Q Acoustics",
    "qacoustics": "Q Acoustics",
    "acoustic energy": "Acoustic Energy",
    "wharfdale": "Wharfedale",         // scrierea greșită frecventă, fără „e”
    "sennheiser": "SENNHEISER",
    "senheiser": "SENNHEISER",         // un singur „n”
    "beyer": "Beyerdynamic",
    "pro ject": "Pro-Ject",
    "project": "Pro-Ject",             // fără cratimă, exact ca handle-ul colecției
    "kef": "KEF",
    "jbl": "JBL",
    "b&o": "Bang & Olufsen",           // dacă normalizarea se schimbă vreodată
    /* Măsurat în `product_search_queries` pe 2026-09-04, pe brandul principal:
       „bang" 19 căutări, „bang and olufsen" 10. Ambele cădeau pe /search, fiindcă
       normalizarea păstrează „and" ca token („bang and olufsen" ≠ „bang olufsen")
       iar „bang" singur nu era cheie. Restul formelor mergeau deja: „b&o", „b & o",
       „bo", „bang olufsen", „bang & olufsen" — verificate, nu presupuse. */
    "bang": "Bang & Olufsen",
    "bang and olufsen": "Bang & Olufsen",
    /* Formele scurte ale mărcilor din lotul 2 de colecţii (2026-09-04). Nimeni
       nu scrie „Polk Audio" sau „Velodyne Acoustics" în caseta de căutare, iar
       fără alias „boxe polk" cădea pe /search în loc de raftul mărcii. */
    "polk": "Polk Audio",
    "velodyne": "Velodyne Acoustics",
    /* Formele SCURTE ale brandurilor barate pe .ro, adăugate 2026-08-26. Nimeni
       nu scrie „Cambridge Audio" în caseta de căutare — corpusul real are
       „cambridge" (26 de căutări, cel mai frecvent termen cu zero rezultate),
       „ruark" (10), „argon", „evers". Fără alias, VENDOR_LOOKUP nu le recunoaște,
       deci nici substituția de brand nu s-ar declanșa pentru ele — prins de un
       test, nu la citire. Aliasurile NU fac produsele vizibile (asta o decide
       `markets` pe document); doar dau motorului un nume canonic de care să se
       agațe. */
    "cambridge": "Cambridge Audio",
    "ruark": "Ruark Audio",
    "argon": "Argon Audio",
    "evers": "EverSolo",
  };

  var VENDOR_LOOKUP = (function () {
    var map = {};
    for (var i = 0; i < KNOWN_VENDORS.length; i++) {
      map[normalizeBrandQuery(KNOWN_VENDORS[i])] = KNOWN_VENDORS[i];
    }
    /* Aliasurile se adaugă DUPĂ numele canonice, dar nu le pot suprascrie: cheile
       normalizate ale brandurilor reale au prioritate, fiindcă un alias care ar
       coincide cu un nume oficial ar fi oricum același brand. */
    for (var alias in VENDOR_ALIASES) {
      if (!Object.prototype.hasOwnProperty.call(VENDOR_ALIASES, alias)) continue;
      var cheie = normalizeBrandQuery(alias);
      if (!map[cheie]) map[cheie] = VENDOR_ALIASES[alias];
    }
    return map;
  })();

  /**
   * Returns the canonical vendor name if `query`, once normalized, is an EXACT
   * match for a known vendor — otherwise null.
   *
   * Exact match only, deliberately: "kef ci200rr" normalizes to "kef ci200rr",
   * which is not a key in VENDOR_LOOKUP (only "kef" is), so it correctly returns
   * null and falls through to ordinary text-first search. This is the guard the
   * review requires — brand-intent must never fire on a model query. Verified
   * against the required non-triggers: "kef ci200rr", "audio technica m50x",
   * "pro-ject at3600l" all return null (their normalized forms contain the brand
   * as a mere prefix/substring, not an exact match to any lookup key).
   */
  function detectBrandIntent(query) {
    var normalized = normalizeBrandQuery(query);
    if (!normalized) return null;
    return VENDOR_LOOKUP[normalized] || null;
  }

  /* Cuvinte de legătură românești, scoase din interogare înainte de căutarea de
     text. Motivul e mecanic, nu stilistic: Typesense cere ca FIECARE token să se
     potrivească undeva, iar „pentru" nu apare în titluri — deci „boxe pentru
     birou" arunca un token (drop_tokens) și răspundea la altă întrebare decât
     cea pusă. Lista e scurtă intenționat: doar cuvinte care nu pot fi niciodată
     parte dintr-un nume de produs sau cod de model. Interogarea AFIȘATĂ rămâne
     cea originală; doar cea trimisă la Typesense e curățată. */
  var STOPWORDS_RO = { "pentru": 1, "de": 1, "cu": 1, "la": 1, "din": 1, "pe": 1,
    "si": 1, "și": 1, "sau": 1, "un": 1, "o": 1, "cel": 1, "cea": 1, "cele": 1 };

  function stripStopwords(query) {
    /* Doar pe .ro: lista e de cuvinte ROMÂNEȘTI. Pe .bg n-ar face nimic (niciun
       token bulgar nu e în ea), dar o rulare care nu poate schimba nimic e o
       promisiune falsă de acoperire — dacă .bg are nevoie de stopwords, se scrie
       o listă bulgară, măsurată la fel ca celelalte. */
    if (currentMarket() !== "ro") return String(query || "").trim();
    var tokens = String(query || "").trim().split(/\s+/);
    var kept = [];
    for (var i = 0; i < tokens.length; i++) {
      if (!STOPWORDS_RO[tokens[i].toLowerCase()]) kept.push(tokens[i]);
    }
    // Dacă TOT ce a scris omul e cuvinte de legătură, nu curăța nimic — o
    // interogare goală ar fi mai rea decât una stângace.
    return kept.length ? kept.join(" ") : String(query || "").trim();
  }

  /**
   * Caută un BRAND ÎNĂUNTRUL unei interogări mai lungi — „telecomanda b&o",
   * „boxe klipsch", „casti audio technica" — și îl desparte de restul cererii.
   *
   * De ce există (auditul din 2026-08-26, reprodus pe indexul live): un token de
   * brand pierdut într-o căutare de text obișnuită se strică pe DOUĂ căi:
   *   1. Ca ULTIM token e tratat ca prefix: „telecomanda b&o" → „b&o" devine
   *      „bo*" (simbolurile nu se indexează), care potrivește „boxe", „Bose",
   *      „Bowers", „Box" — 953 de documente. Intersectat cu „telecomandă" din
   *      descrieri, dropdown-ul arăta KEF/Klipsch/Marantz, nu telecomenzile B&O.
   *   2. Chiar scris întreg, brandul potrivit în DESCRIEREA altui produs bate
   *      potrivirea pe câmpul vendor: la „boxe klipsch", receiverele Onkyo
   *      (care au „boxe" și „Klipsch" în aceeași frază de compatibilitate)
   *      ieșeau înaintea oricărei boxe Klipsch, fiindcă Typesense punctează
   *      mai sus doi tokeni în ACELAȘI câmp decât unul în vendor și unul în
   *      subtitlu, iar bucketing-ul preda decizia lui rank_score.
   *
   * Nicio reglare de greutăți nu repară asta — răspunsul corect e să tratăm
   * brandul ca FILTRU (vendor:=X), nu ca text. Returnează
   * { vendor, rest } sau null. Folosește ACELAȘI VENDOR_LOOKUP ca brand-intent
   * (nume canonice + aliasuri, deci „b&o", „audio technica", „pro ject" merg),
   * pe spanuri de 1–3 tokeni, cel mai lung întâi. Fals-pozitivele sunt
   * autolimitate: apelantul cade înapoi pe căutarea normală când filtrul dă 0.
   */
  function findVendorScope(query) {
    var tokens = String(query || "").trim().split(/\s+/);
    if (tokens.length < 2) return null; // un singur token = brand-intent, nu scoping
    for (var len = Math.min(3, tokens.length - 1); len >= 1; len--) {
      for (var start = 0; start + len <= tokens.length; start++) {
        var span = tokens.slice(start, start + len).join(" ");
        var vendor = VENDOR_LOOKUP[normalizeBrandQuery(span)];
        if (vendor) {
          var rest = tokens.slice(0, start).concat(tokens.slice(start + len)).join(" ");
          if (rest) return { vendor: vendor, rest: rest };
        }
      }
    }
    return null;
  }

  /**
   * INTENȚIE DE CATEGORIE — „doze", „căști", „boxe" duc la raftul lor, nu la o
   * listă de produse care se întâmplă să conțină cuvântul.
   *
   * Cerut de Tudor (2026-08-20), cu modelul din piață: pe AVstore, „doze" duce
   * direct la /doze-pick-up/. Observația care a declanșat asta: în dropdown la
   * „doze" nu apărea NICIO doză — ieșeau Pro-Ject X2 (un pick-up „cu doză
   * inclusă"), Musical Fidelity M6xi (amplificator cu intrare phono) și trei
   * preamplificatoare Fosi. Toate conțineau cuvântul; niciunul nu era o doză.
   *
   * De ce potrivirea de text nu poate rezolva asta, oricât aș regla greutățile:
   * un pick-up al cărui SUBTITLU spune „cu doză inclusă" e un rezultat legitim
   * pentru cuvântul „doză". Întrebarea omului însă nu era „ce produse menționează
   * doze", ci „arată-mi dozele". Aia e o categorie, iar catalogul o are deja:
   * /doze-pickup, 83 de produse. Răspunsul corect nu e o listă mai bine ordonată,
   * e raftul.
   *
   * Aceeași structură ca la intenția de brand: potrivire EXACTĂ pe interogarea
   * normalizată, deci „doze" duce la raft, dar „doze sumiko" sau „doza mc" trec
   * mai departe la căutarea obișnuită — acolo omul restrânge, nu răsfoiește.
   *
   * Handle-urile sunt verificate în Admin API pe 2026-08-20, nu derivate: „pick-up"
   * are 143 de produse, dar există și „pick-up-old-f43fe4" cu 134 — un handle
   * derivat mecanic ar fi putut nimeri colecția veche.
   */
  /** Titlurile afișabile ale colecțiilor de categorie, pentru eticheta „Vezi toate
      produsele din …". Luate din Admin API pe 2026-08-20 — `products.json` nu
      întoarce titlul colecției, iar handle-ul („doze-pickup") citit ca atare ar
      arăta ca un URL, nu ca un raft. */
  var CATEGORY_TITLES = {
    "doze-pickup": "Doze pick-up", "ace-pick-up": "Ace pick-up",
    "pick-up": "Pick-up-uri", "accesorii-pick-up": "Accesorii pick-up",
    "pre-amp-phono-stage": "Preamplificatoare phono",
    "casti": "Căști", "amplificatoare-pentru-casti": "Amplificatoare de căști",
    "boxe-pasive": "Boxe pasive", "boxe-pasive-de-podea": "Boxe de podea",
    "boxe-amplificate": "Boxe active", "standuri-si-suporturi-boxe": "Standuri și suporturi",
    "amplificatoare-integrate": "Amplificatoare integrate",
    "amplificatoare-de-putere": "Amplificatoare de putere",
    "receivere-a-v": "Receivere A/V", "dac": "DAC-uri",
    "streamer-network-player": "Streamere", "subwoofere": "Subwoofere",
    "soundbar": "Soundbar-uri", "televizoare": "Televizoare",
    "videoproiector": "Videoproiectoare", "cabluri-audio": "Cabluri", "cabluri-de-subwoofer": "Cabluri de subwoofer",
    "cabluri-de-boxe": "Cabluri de boxe", "cabluri-hdmi": "Cabluri HDMI",
    "microfoane": "Microfoane", "discuri-vinil": "Discuri vinil",
    "baterii-si-acumulatori": "Baterii și acumulatori",
    "filtre-audio": "Filtre audio", "accesorii-boxe-active": "Accesorii boxe",
    "cd-playere": "CD playere", "preamplificatoare": "Preamplificatoare",
    "boxe-portabile": "Boxe portabile", "boxe-pentru-pc": "Boxe pentru PC",
    "suporturi-tv": "Suporturi TV",
  };

  var CATEGORY_INTENT = {
    // pick-up și tot ce ține de el
    "doze": "doze-pickup", "doza": "doze-pickup", "doze pick up": "doze-pickup",
    "doza pick up": "doze-pickup", "doze pickup": "doze-pickup", "capsule": "doze-pickup",
    "ace": "ace-pick-up", "ace pick up": "ace-pick-up", "ac pick up": "ace-pick-up",
    "pick up": "pick-up", "pickup": "pick-up", "pick ups": "pick-up",
    "picupuri": "pick-up", "pick up uri": "pick-up", "turntable": "pick-up",
    "accesorii pick up": "accesorii-pick-up",
    // suporturi TV (colecţia `suporturi-tv`, creată 08.10.2026)
    "suport tv": "suporturi-tv", "suporturi tv": "suporturi-tv",
    "suport televizor": "suporturi-tv", "suporturi televizor": "suporturi-tv",
    "suport perete tv": "suporturi-tv", "suport tv perete": "suporturi-tv",
    "suport tv de perete": "suporturi-tv", "suporturi tv de perete": "suporturi-tv",
    "suporturi tv perete": "suporturi-tv", "suporturi perete tv": "suporturi-tv",
    "stand tv": "suporturi-tv", "tv mount": "suporturi-tv",
    "preamplificator phono": "pre-amp-phono-stage", "preamp phono": "pre-amp-phono-stage",
    "phono": "pre-amp-phono-stage", "phono stage": "pre-amp-phono-stage",
    // căști
    "casti": "casti", "casca": "casti", "headphones": "casti",
    "casti audio": "casti", "amplificator casti": "amplificatoare-pentru-casti",
    "amplificatoare casti": "amplificatoare-pentru-casti",
    // boxe
    /* GENERIC vs PRECIS (10.09.2026, decis de Tudor). „boxe kef" e o cerere
       LARGĂ — acelaşi lucru cu un clic pe meniu „Boxe > Branduri > KEF", care
       duce la `boxe-kef` (98 de produse: pasive + active + subwoofere). „boxe
       pasive kef" e o cerere PRECISĂ şi merge la raftul pe tip (78).
       Înainte, ambele produceau `boxe-pasive`, deci erau indistingibile şi
       căutarea contrazicea meniul: clic pe Klipsch = 81 de produse, căutare
       „boxe klipsch" = 43. Handle-ul `boxe-toate` există ca să se poată alege
       colecţia de GRUP acolo unde ea există, fără să atingem cererea precisă.
       NU e un handle de colecţie reală: nu apare în TIPURI_CATEGORIE, iar dacă
       marca n-are colecţie de grup se cade pe comportamentul de dinainte. */
    "boxe": "boxe-toate", "boxa": "boxe-toate", "difuzoare": "boxe-toate",
    "boxe pasive": "boxe-pasive",
    "boxe de podea": "boxe-pasive-de-podea",
    "boxe podea": "boxe-pasive-de-podea",
    "boxe active": "boxe-amplificate", "boxe amplificate": "boxe-amplificate",
    "boxe wireless": "boxe-amplificate", "boxe bluetooth": "boxe-amplificate",
    "stativ boxe": "standuri-si-suporturi-boxe", "stative boxe": "standuri-si-suporturi-boxe",
    "standuri boxe": "standuri-si-suporturi-boxe", "suporturi boxe": "standuri-si-suporturi-boxe",
    // electronice
    "amplificator": "amplificatoare-integrate", "amplificatoare": "amplificatoare-integrate",
    "amplificator integrat": "amplificatoare-integrate",
    "amplificator de putere": "amplificatoare-de-putere",
    "amplificatoare de putere": "amplificatoare-de-putere",
    /* Geamănul fără „de" (2026-10-19): interogarea mixtă trece prin
       stripStopwords() înainte de findVendorScope(), deci „amplificator de putere
       nad" ajunge aici ca „amplificator putere" și nu ducea la
       amplificatoare-de-putere-nad. Plasa: test/typesense-search-categorie-legaturi. */
    "amplificator putere": "amplificatoare-de-putere",
    "amplificatoare putere": "amplificatoare-de-putere",
    "receiver": "receivere-a-v", "receivere": "receivere-a-v", "receiver av": "receivere-a-v",
    "dac": "dac", "dac uri": "dac", "convertor": "dac",
    "streamer": "streamer-network-player", "streamere": "streamer-network-player",
    "network player": "streamer-network-player",
    "subwoofer": "subwoofere", "subwoofere": "subwoofere", "sub": "subwoofere",
    "soundbar": "soundbar", "soundbaruri": "soundbar",
    // video
    "televizor": "televizoare", "televizoare": "televizoare", "tv": "televizoare",
    "videoproiector": "videoproiector", "videoproiectoare": "videoproiector",
    "proiector": "videoproiector",
    // restul
    "cabluri": "cabluri-audio", "cablu": "cabluri-audio",
    /* Sub-categoriile de cabluri, adaugate 2026-08-26 dupa semnalarea lui Tudor.
       Toate trei EXISTA ca colectii (verificat prin /collections/<h>.json:
       cabluri-de-subwoofer 12, cabluri-de-boxe 85, cabluri-hdmi 20), deci linkul
       „Vezi toate" duce la raftul potrivit, nu la toate cele 371 de cabluri.
       Verificate si respinse fiindca dau 404: cabluri-rca, cabluri-optice,
       cabluri-usb, cabluri-de-alimentare, cabluri-jack, cabluri-xlr. */
    "cabluri subwoofer": "cabluri-de-subwoofer", "cablu subwoofer": "cabluri-de-subwoofer",
    "cabluri de subwoofer": "cabluri-de-subwoofer", "cablu de subwoofer": "cabluri-de-subwoofer",
    "cabluri boxe": "cabluri-de-boxe", "cablu boxe": "cabluri-de-boxe",
    "cabluri de boxe": "cabluri-de-boxe", "cablu de boxe": "cabluri-de-boxe",
    "cabluri hdmi": "cabluri-hdmi", "cablu hdmi": "cabluri-hdmi",
    "microfon": "microfoane", "microfoane": "microfoane",
    "vinil": "discuri-vinil", "vinyl": "discuri-vinil", "discuri": "discuri-vinil",
    "discuri vinil": "discuri-vinil", "placi": "discuri-vinil",
    "baterii": "baterii-si-acumulatori", "acumulatori": "baterii-si-acumulatori",
    "filtru audio": "filtre-audio", "filtre": "filtre-audio",
    "accesorii boxe": "accesorii-boxe-active",
    /* Adăugate la a doua trecere (2026-08-20), după ce am numărat ce colecții de
       categorie rămăseseră nelegate. Doar categorii REALE — sub-filtrele („Televizoare
       4K", „Videoproiectoare Long Throw", „Boxe cu Internet streaming") sunt lăsate
       deliberat afară: cine scrie „televizoare" vrea raftul întreg, iar de acolo
       filtrează. O intenție care sare peste raft direct într-un sub-filtru ar ascunde
       restul gamei. */
    "cd player": "cd-playere", "cd playere": "cd-playere", "cd": "cd-playere",
    "preamplificator": "preamplificatoare", "preamplificatoare": "preamplificatoare",
    "preamp": "preamplificatoare",
    "boxe portabile": "boxe-portabile", "boxa portabila": "boxe-portabile",
    "boxe pc": "boxe-pentru-pc", "boxe pentru pc": "boxe-pentru-pc",
    "boxe birou": "boxe-pentru-pc",
  };

  /** Handle-ul de colecție dacă interogarea e o CATEGORIE exactă, altfel null. */
  /* ── Branduri pe care NU le vindem: alternative în loc de zero ────────────────
   *
   * Decizia lui Tudor (2026-08-26): „nu prea avem produse Fiio deși sunt foarte
   * populare; poți face să i se recomande produse Fosi și iFi relevante".
   *
   * MĂSURAT, nu presupus (corpus real din product_search_queries +
   * product_nohits_queries, 2026-08-26):
   *   - catalogul are UN singur produs Fiio (BTR3K), EPUIZAT;
   *   - fiecare căutare „fiio" e pe un MODEL, niciuna pe brandul singur:
   *     ka11 (13), btr13 (6), eh11 (4), k11 (2), k7 (1) — DAC-uri portabile și
   *     amplificatoare de căști;
   *   - aproape toate cad azi în zero rezultate.
   *   - Fosi Audio (45 produse) și iFi Audio (100) acoperă exact acele categorii,
   *     în aceeași zonă de preț.
   *
   * DE CE pe CATEGORIE și nu „toată gama celor două branduri": cine caută un DAC
   * portabil de 400 lei nu vrea un amplificator de putere sau un filtru de rețea.
   * `types` restrânge substituția la tipurile de produs echivalente; verificat pe
   * indexul live că această listă întoarce GR40, SK01, K5 Pro, ZD3, ZH3,
   * StepSense C3, iDSD Gryphon 2 — toate din categoria cerută.
   *
   * Rândul explicativ („nu avem X, alternative:") a fost DECIS DE TUDOR să NU
   * apară (2026-08-26). Renderer-ul primește `substitutionFor` oricum, deci
   * adăugarea lui ulterioară e un `if`, nu o rescriere.
   *
   * `barred: true` marchează brandurile blocate prin RESTRICȚIE DE DISTRIBUȚIE pe
   * .ro, nu prin lipsă de stoc (Cambridge, Ruark, EverSolo, Argon — vezi
   * brand-hidden-on-market.liquid și memoria proiectului). Tudor a aprobat
   * explicit alternativele și pentru ele (2026-08-26). Distincția rămâne scrisă
   * fiindcă e o zonă de conformitate: NU afișăm produse ale brandului barat —
   * doar produse ale brandurilor pe care le vindem. Mecanismul e identic; eticheta
   * există ca o revizuire viitoare să nu confunde „nu avem stoc" cu „nu avem voie".
   *
   * Extinderea la restul brandurilor cerute frecvent (audiolab, audeze,
   * panasonic, soundcore…) e AMÂNATĂ deliberat până se vede efectul pe Fiio —
   * decizia lui Tudor. Mecanismul e general, doar harta e scurtă. */
  var BRAND_SUBSTITUTIONS = {
    "Fiio": {
      vendors: ["Fosi Audio", "iFi Audio"],
      types: ["DAC casti", "Amplificator casti", "DAC"],
    },
    "Cambridge Audio": {
      barred: true,
      vendors: ["Marantz", "Denon", "NAD", "Bluesound", "WiiM"],
      types: ["Amplificator", "Streamer", "DAC", "CD Player"],
    },
    "Ruark Audio": {
      barred: true,
      vendors: ["Bang & Olufsen", "Lenco", "Kanto Audio", "Klipsch"],
      types: ["Boxe amplificate", "Boxe portabile"],
    },
    "EverSolo": {
      barred: true,
      vendors: ["WiiM", "Bluesound", "iFi Audio", "NAD"],
      types: ["Streamer", "DAC"],
    },
    "Argon Audio": {
      barred: true,
      vendors: ["Wharfedale", "Elac", "Q Acoustics", "Magnat"],
      types: ["Boxe pasive", "Boxe amplificate"],
    },
  };

  /* Interogarea numește un brand pe care nu-l vindem? Întoarce configurația de
     substituție, altfel null. Se uită la ÎNTREAGA interogare normalizată (brand
     singur) și la spanurile din interior („fiio ka11" → Fiio), ca să prindă
     exact tiparul măsurat: căutare pe model, nu pe brand. */
  function detectBrandSubstitution(query) {
    var normalized = normalizeBrandQuery(query);
    var direct = VENDOR_LOOKUP[normalized];
    if (direct && BRAND_SUBSTITUTIONS[direct]) return { vendor: direct, config: BRAND_SUBSTITUTIONS[direct] };
    var scope = findVendorScope(query);
    if (scope && BRAND_SUBSTITUTIONS[scope.vendor]) {
      return { vendor: scope.vendor, config: BRAND_SUBSTITUTIONS[scope.vendor] };
    }
    return null;
  }

  /* URL-ul care aduce alternativele: produsele brandurilor de înlocuire, DOAR în
     categoriile echivalente, DOAR pe stoc (o alternativă epuizată nu ajută pe
     nimeni), în ordinea comercială — `rank_score` primar, fiindcă nu există text
     de potrivit (q:"*"), exact ca la ruta de brand-intent. */
  function substitutionUrl(config, limit) {
    var vendorFilter = config.vendors.map(function (v) { return "vendor:=`" + v + "`"; }).join(" || ");
    var typeFilter = config.types.map(function (t) { return "`" + t + "`"; }).join(",");
    var params = new URLSearchParams({
      q: "*",
      query_by: "vendor,title",
      filter_by: cuFiltruDePiata("(" + vendorFilter + ") && in_stock:true && product_type:[" + typeFilter + "]"),
      group_by: "product_id",
      group_limit: String(GROUP_VARIANT_LIMIT),
      sort_by: "rank_score:desc,sort_rank:asc",
      per_page: String(limit),
    });
    return brandSearchFetchUrl(params);
  }

  /* ── Intenții de categorie BULGARE (2026-08-26, la deschiderea porții .bg) ────
   *
   * Cerința lui Tudor: „.bg să aibă aceeași utilizabilitate ca .ro".
   *
   * MĂSURATE, nu traduse. Sursa e traficul real .bg din GA4 (1.130 termeni pe 90
   * de zile, `assets/audit/cautari-bg-ga4-2026-08-21.csv`), din care 117 conțin
   * chirilice. Volumele reale ale termenilor de categorie:
   *   субуфер 32, кабел 16, грамофон 16, слушалки 12, усилватели 8, стример 8,
   *   дози 4+4, ресивър 4, тонколони 3, високоговорители 4, винил 2, cd плеър 2
   *
   * Constatarea din 21.08 care contează aici: pe .bg BRANDURILE se caută în
   * LATINĂ (kef 1003, sonos 795), iar chirilicele sunt aproape exclusiv
   * CATEGORII. De aceea harta asta acoperă doar categorii — brandurile trec deja
   * prin VENDOR_LOOKUP, care e independent de alfabet.
   *
   * Formele flexionate contează în bulgară: „слушалки" (articolul hotărât
   * „слушалките"), „усилвател"/„усилватели", „стример"/„стримър" (ambele scrieri
   * apar în trafic, cu 8 și 4 evenimente). Le includem pe cele MĂSURATE, nu toate
   * formele teoretic posibile.
   *
   * Fiecare handle a fost verificat prin HTTP că răspunde 200 pe audiomonkey.bg
   * (2026-08-26) — o intenție care duce la 404 e mai rea decât lipsa ei. */
  /* TIPURILE DE PRODUS din spatele fiecărui handle de categorie (2026-09-04).

     DE CE EXISTĂ. `findVendorScope` scoate brandul şi îl aplică drept FILTRU
     (`vendor:=X`), dar restul interogării pleca drept TEXT LIBER în `q`. Când
     brandul chiar are acea categorie, ordonarea salva aparenţele; când NU o are,
     rezultatul era arbitrar. Măsurat pe indexul live:
       „kef casti"   -> KEF XIO (soundbar), LS50 Wireless II   — KEF are 0 căşti
       „casti sony"  -> Audio-Technica ATH-TWX9                — Sony are 0 căşti
     Un raft gol e un răspuns onest; un soundbar în loc de căşti nu e.

     Valorile NU sunt ghicite: sunt citite din colecţiile reale, luând tipurile
     care acoperă ≥5% din fiecare colecţie (Admin API, 2026-09-04). Un handle
     absent de aici NU e o eroare — înseamnă doar că nu se poate filtra pe tip şi
     se cade pe comportamentul de dinainte. */
  var TIPURI_CATEGORIE = {
    "boxe-pasive": ["Boxe pasive"],
    "boxe-pasive-de-podea": ["Boxe pasive"],
    "boxe-amplificate": ["Boxe amplificate"],
    "boxe-portabile": ["Boxe amplificate"],
    "boxe-pentru-pc": ["Boxe amplificate"],
    "subwoofere": ["Subwoofer"],
    "casti": ["Casti"],
    "amplificatoare-pentru-casti": ["Amplificator casti"],
    "amplificatoare-integrate": ["Amplificator"],
    "amplificatoare-de-putere": ["Amplificator de putere"],
    "preamplificatoare": ["Preamplificator"],
    "pre-amp-phono-stage": ["Preamplificator Phono"],
    "streamer-network-player": ["Streamer"],
    "dac": ["DAC"],
    "cd-playere": ["CD Players & Recorders"],
    "receivere-a-v": ["Receiver"],
    "soundbar": ["Soundbar"],
    "televizoare": ["Televizor"],
    "videoproiector": ["Videoproiector"],
    "pick-up": ["Pick-up"],
    "doze-pickup": ["Doza pick-up"],
    "ace-pick-up": ["Ace pick-up"],
    "accesorii-pick-up": ["Accesorii pick-up"],
    "suporturi-tv": ["Suport TV"],
    "discuri-vinil": ["Disc vinil"],
    "cabluri-audio": ["Cabluri"],
    "cabluri-de-boxe": ["Cabluri"],
    "cabluri-hdmi": ["Cabluri"],
    "cabluri-de-subwoofer": ["Cabluri"],
    "microfoane": ["Microfoane"],
    "standuri-si-suporturi-boxe": ["Stativ boxe"],
    "accesorii-boxe-active": ["Accesorii boxe"],
    "filtre-audio": ["Filtru audio", "Sursa de curent cu filtrare"],
    "baterii-si-acumulatori": ["Baterii", "Baterii reincarcabile"],
  };

  /* Filtrul Typesense pentru un handle de categorie, sau null dacă nu ştim tipurile.
     `null` NU e eroare: înseamnă „nu filtra, comportă-te ca înainte". */
  function filtruTipCategorie(categoryHandle) {
    /* `boxe-toate` e handle de INTENŢIE, nu de colecţie: n-are tipuri proprii,
       dar căutarea text pe „boxe" trebuie să filtreze la fel ca înainte de
       10.09, altfel cererea generică ar întoarce şi cabluri, şi stative. */
    if (categoryHandle === "boxe-toate") categoryHandle = "boxe-pasive";
    var tipuri = TIPURI_CATEGORIE[categoryHandle];
    if (!tipuri || !tipuri.length) return null;
    return "product_type:=[" + tipuri.map(function (t) { return "`" + t + "`"; }).join(",") + "]";
  }

  var CATEGORY_INTENT_BG = {
    // subwoofere — cel mai căutat termen chirilic (32)
    "субуфер": "subwoofere", "субуфери": "subwoofere", "субуферa": "subwoofere",
    "събуфер": "subwoofere",
    // cabluri (16) — inclusiv trunchierea reală „кабе" (8)
    "кабел": "cabluri-audio", "кабели": "cabluri-audio", "кабе": "cabluri-audio",
    // pick-up / gramofon (16)
    "грамофон": "pick-up", "грамофони": "pick-up", "грамофона": "pick-up",
    // căști (12)
    "слушалки": "casti", "слушалките": "casti", "наушници": "casti",
    // amplificatoare (8)
    "усилвател": "amplificatoare-integrate", "усилватели": "amplificatoare-integrate",
    "усилвателя": "amplificatoare-integrate",
    // streamere (8+4, ambele scrieri apar în trafic)
    "стример": "streamer-network-player", "стримър": "streamer-network-player",
    "стриймър": "streamer-network-player",
    // doze pick-up (4+4)
    "доза": "doze-pickup", "дози": "doze-pickup", "дозата": "doze-pickup",
    // ace pick-up — „игла" apare de 12 ori, mereu legat de doză/pick-up
    "игла": "ace-pick-up", "игли": "ace-pick-up", "иглата": "ace-pick-up",
    // receivere (4)
    "ресивър": "receivere-a-v", "ресивъри": "receivere-a-v", "ресийвър": "receivere-a-v",
    // boxe: „тонколони" (3) și „високоговорители" (4) — ambele folosite real
    "тонколони": "boxe-pasive", "тонколона": "boxe-pasive",
    "високоговорители": "boxe-pasive", "високоговорител": "boxe-pasive",
    "колони": "boxe-pasive",
    // boxe active (8: „активни високоговорител")
    "активни високоговорители": "boxe-amplificate",
    "активни високоговорител": "boxe-amplificate",
    "активни колони": "boxe-amplificate",
    // vinil (2)
    "винил": "discuri-vinil", "плочи": "discuri-vinil", "грамофонни плочи": "discuri-vinil",
    // CD (2)
    "cd плеър": "cd-playere", "сд плеър": "cd-playere",
    // filtre audio (2: „аудио филтър")
    "аудио филтър": "filtre-audio", "филтър": "filtre-audio",
    // microfoane
    "микрофон": "microfoane", "микрофони": "microfoane",
    // preamplificatoare
    "предусилвател": "preamplificatoare", "предусилватели": "preamplificatoare",
  };

  /* Harta de intenții A PIEȚEI CURENTE — niciodată amândouă.
   *
   * Separarea e cerută explicit de Tudor (2026-08-26): „.ro să nu comunice cu .bg
   * și .bg cu .ro". O hartă unificată ar fi fost mai puțin cod, dar ar fi însemnat
   * că un termen românesc declanșează o rută pe .bg (și invers) — exact felul de
   * scurgere care apoi se vede în trafic și nu se explică ușor.
   *
   * Alfabetele nu se suprapun, deci în practică o unificare n-ar fi produs
   * coliziuni AZI; separarea e aici ca să rămână adevărat și mâine, când cineva
   * adaugă pe .bg un termen latin („subwoofer" se caută și așa pe .bg). */
  function hartaCategorii() {
    return currentMarket() === "bg" ? CATEGORY_INTENT_BG : CATEGORY_INTENT;
  }

  function detectCategoryIntent(query) {
    var normalized = normalizeBrandQuery(query);
    if (!normalized) return null;
    var harta = hartaCategorii();
    return Object.prototype.hasOwnProperty.call(harta, normalized)
      ? harta[normalized]
      : null;
  }

  /**
   * WHY THERE IS NO "is this a model/install query" TOKEN CHECK HERE, deliberately.
   *
   * The section B/C requirement is that "KEF Ci200RR", "in-wall KEF", "boxe tavan
   * KEF" must NOT trigger the brand-browse consumer-first quota. That guarantee
   * comes from detectBrandIntent() requiring an EXACT normalized match against
   * KNOWN_VENDORS — "kef ci200rr" normalizes to "kef ci200rr", which is not "kef",
   * so it can never match a vendor and always falls through to runTextSearch()
   * unchanged, by construction, with no keyword list to keep in sync.
   *
   * An earlier draft of this file added a second check here — a regex of
   * installation-sounding tokens (ci\d, in-wall, tavan, perete, incastrabil) —
   * as defence-in-depth in case a future vendor name ever collided with one of
   * those words. It was removed: the spec this branch implements explicitly says
   * "Do not implement a KEF-only workaround, a hardcoded product_type blacklist,
   * or title-based runtime filtering," and a token list of install-sounding words
   * checked against free-text queries is exactly that kind of runtime filtering,
   * just aimed at a different field. If a future vendor name genuinely collides
   * with a common model/install term, the fix is to correct KNOWN_VENDORS or
   * detectBrandIntent's matching rule, not to bolt on a keyword denylist here.
   */

  /* Variants fetched per product group. Only used to compute the displayed price — the
     dropdown always shows ONE row per product. 5 covers the usual colour runs (the KEF
     LS50 Wireless II has 5) without inflating the response; a product with more than
     this just means the price is chosen from the 5 most relevant variants, which is a
     far smaller error than showing the same product five times. */
  var GROUP_VARIANT_LIMIT = 5;

  var debounceTimer = null;
  var currentRequestController = null;
  // Monotonic generation counter — the authoritative "is this response still
  // relevant" check. AbortController.abort() alone is not sufficient: it's a
  // best-effort signal, not a guarantee the aborted request's .then never fires
  // (seen in practice — an older, slower "jb" request landing after a newer "jbi"
  // request had already rendered correct results, silently overwriting them with
  // stale/fewer results). Every request captures the generation it was started at;
  // its render step only applies if that generation is still the latest.
  var latestGeneration = 0;

  /* Suppressing the theme's OWN predictive search.
   *
   * The theme binds its search to `keyup` (custom-async.js:2751) and fetches
   * Shopify's native predictive search behind a 300ms setTimeout, then does
   * `liveSearchEl.innerHTML = resultsMarkup` (custom-async.js:4997). That lands
   * AFTER our render and overwrites it — which is why "jbi" showed 0 results even
   * though Typesense returns 7: native search finds nothing for the typo, and it
   * painted last. Intercepting `input` never helped, because `input` and `keyup`
   * are separate events with separate dispatch cycles.
   *
   * Rather than race three separate write paths, use the theme's own off switch:
   * it skips liveSearch() entirely when the #search form carries `no-autocomplete`
   * (custom-async.js:2759). That guard wraps ONLY the fetch — the dropdown's
   * open/close classes are set outside it, so the UI keeps behaving normally.
   *
   * AUTO-FALLBACK: the class is only applied while Typesense is actually working.
   * On a real failure (network/401/5xx — not an abort) it is removed, so the
   * theme's native search takes over on the next keystroke rather than the user
   * getting no search at all. Re-applied on the next success. */
  var THEME_SEARCH_OFF_CLASS = "no-autocomplete";
  /* Tracks whether Typesense is believed healthy. Starts true (optimistic — the
   * config guard above already confirmed a host and key are present), flips false
   * on a real request failure, back to true on the next success. attach()'s poll
   * reads this so it re-asserts suppression ONLY while healthy — otherwise the
   * 2s tick would undo the fallback within two seconds of it kicking in. */
  var typesenseHealthy = true;

  /* ── Sonda de revenire (2026-08-30) ────────────────────────────────────────
     PROBLEMA pe care o rezolvă. Cât timp `typesenseHealthy` e false, listenerul de
     mai jos predă tastele temei și NU mai cheamă onInput() — deci runSearch() nu
     mai pornește niciodată. Toate cele șase locuri care pun `healthy = true` la
     succes trăiesc în ramuri pornite din runSearch(), iar al șaptelea (pollul din
     attach) e păzit de `if (typesenseHealthy)`, deci reimpune clasa doar când e
     DEJA sănătos — nu testează nimic. Fără sonda asta, prima pană ar lăsa pagina
     în căutarea temei până la reîncărcare, chiar dacă Typesense revine în secunda
     următoare.

     DE CE NU „încercăm în paralel, pe calea normală", care ar fi fost mai simplu:
     ruta de brand-intent ia produsele din `products.json` al SHOPIFY, nu din
     Typesense, iar erorile ei de hidratare sunt înghițite deliberat. Ar fi declarat
     `healthy = true` fără ca vreo cerere să fi atins Typesense — fals pozitiv exact
     pe calea pe care ne bazăm ca să ieșim din pană. Verificat cu Codex, 30.08.

     Sonda e deliberat mută: aceeași cerere ca o căutare reală (deci același
     preflight CORS care a picat azi), dar rezultatul se ARUNCĂ. Nu randează, nu
     atinge #livesearch, nu se ciocnește cu dropdownul temei. Singurul ei efect e
     să pună `healthy` înapoi pe true, ceea ce reactivează calea normală de la
     următoarea tastă. Pornește doar la activitatea utilizatorului, nu pe un
     interval — o pagină lăsată deschisă peste noapte nu trimite nimic. */
  var recoveryProbeInFlight = false;
  var recoveryProbeLastAt = 0;
  var RECOVERY_PROBE_MIN_GAP_MS = 5000;

  function scheduleTypesenseRecoveryProbe(query) {
    if (typesenseHealthy || recoveryProbeInFlight) return;
    var now = Date.now();
    if (now - recoveryProbeLastAt < RECOVERY_PROBE_MIN_GAP_MS) return;
    if (!query || query.length < MIN_QUERY_LENGTH) return;
    recoveryProbeLastAt = now;
    recoveryProbeInFlight = true;
    fetchJson(searchUrl(query, false, "in", null))
      .then(function () {
        /* A răspuns: reintrăm în calea normală de la următoarea tastă. Nu randăm
           nimic din acest răspuns — tema tocmai a scris în dropdown, iar o a doua
           scriere ar produce un flash și un rezultat nedeterminist. */
        themeSearchSuppressed(true);
      })
      .catch(function () { /* tot jos: rămânem pe căutarea temei */ })
      .then(function () { recoveryProbeInFlight = false; });
  }

  /* Curăță TOATĂ starea noastră, ca tema să pornească pe curat. Scoaterea clasei
     `no-autocomplete` singură nu ajunge — mai rămân trei lucruri care ar contrazice
     tema: markupul nostru vechi în #livesearch (peste care tema scrie, „ultimul
     writer câștigă"), un debounce programat care ar porni o cerere DUPĂ ce am cedat
     ștafeta, și o cerere deja în zbor a cărei randare ar suprascrie rezultatele
     temei. Incrementarea generației e apărarea reală: abort() e best-effort, dar o
     randare cu generație veche e respinsă cert. */
  function failOpenToTheme() {
    clearTimeout(debounceTimer);
    debounceTimer = null;
    latestGeneration++;
    if (currentRequestController) {
      currentRequestController.abort();
      currentRequestController = null;
    }
    var container = document.getElementById("livesearch");
    if (container) container.innerHTML = "";
  }

  function themeSearchSuppressed(suppress) {
    typesenseHealthy = suppress;
    var form = document.getElementById("search");
    if (!form) return;
    if (suppress) form.classList.add(THEME_SEARCH_OFF_CLASS);
    else form.classList.remove(THEME_SEARCH_OFF_CLASS);
  }

  /* `no-autocomplete` does double duty in this theme: as well as skipping the
   * theme's own search in JS, async-search.css hides the dropdown outright —
   *
   *   #root #search.no-autocomplete > div,
   *   #search.no-autocomplete fieldset > div { display: none }
   *
   * which beats the `#search.has-text > div { display: block }` rule that normally
   * opens it. Without this override our results render into #livesearch correctly
   * and stay invisible. Re-show the dropdown only when the field actually has text
   * (`.has-text`, set by the theme), so an empty search box still stays closed.
   * Scoped to `.no-autocomplete` so it cannot affect the native search path when
   * suppression is off (Typesense unhealthy → fallback). */
  function injectDropdownCss() {
    if (document.getElementById("typesense-search-css")) return;
    var style = document.createElement("style");
    style.id = "typesense-search-css";
    style.textContent =
      "#root #search.no-autocomplete.has-text > div," +
      "#search.no-autocomplete.has-text fieldset > div{display:block}" +
      /* The subtitle sits inside the same <section> as the title, and this theme's
         heading rules would otherwise render it at heading weight — a second title.
         Reset to body values and mute it, so it reads as supporting text. Colour is
         derived from the theme's own ink rather than hardcoded grey, so it follows
         whatever the header theme is.

         clear:both is the actual fix for the subtitle rendering UNDER/beside the
         price instead of on its own row (found 2026-08-03, screenshot-confirmed).
         .cols in this theme is NOT flexbox — screen.css:989-991 floats its children
         (`.cols > * { float: right }`, first child `float: left`), so the title
         floats left and the price floats right inside .cols. Without an explicit
         clear, .ts-subtitle — a plain <p> sibling right after .cols closes — flows
         up into the leftover space beside the still-floated price column instead of
         starting a clean new row underneath both. */
      "#livesearch .ts-subtitle{clear:both;margin:2px 0 0;font-size:13px;line-height:1.35;" +
      "font-weight:400;text-transform:none;letter-spacing:normal;" +
      "color:color-mix(in srgb, currentColor 65%, transparent)}" +
      /* Eyebrow-ul de brand. Dimensiunile NU sunt alese din ochi, ci din ierarhia
         măsurată pe dropdown-ul real: model 16px/500, preţ 16px/600, subtitlu
         13px/400. La 13px eyebrow-ul ar concura cu subtitlul; 11px îl ţine clar
         subordonat (acelaşi raport ca pe cardul de homepage, 11px sub un model de
         19px), iar majusculele + letter-spacing îl fac să se citească drept
         etichetă, nu text.
         2px până la model, nu 4px ca pe cardul de colecţie: rândul din dropdown e
         dens, iar `h2` are deja 6px sub el.
         Culoarea e derivată din `currentColor`, ca la subtitlu, nu hardcodată —
         dropdown-ul moşteneşte cerneala headerului, iar un gri fix s-ar rupe pe
         fundalul galben. Resetez explicit weight/transform/letter-spacing fiindcă
         regulile de titlu ale temei prind şi acest `<p>` (aceeaşi capcană pentru
         care subtitlul are resetările lui). */
      "#livesearch .ts-brand{margin:0 0 2px;font-size:11px;line-height:1.2;" +
      "font-weight:500;text-transform:uppercase;letter-spacing:.08em;" +
      "color:color-mix(in srgb, currentColor 60%, transparent)}" +
      /* Insigna Black Friday SUB preț (Tudor, 21.10.2026: „emblemele, nu textul”): exact insigna VFD de pe
         carduri — clasele `.s1lb > .bf-card-insigna`, stilizate de snippets/bf-insigna-stil.liquid (randat
         global din layout cât rulează campania). Aici doar o scoatem din poziționarea de pe imaginea cardului:
         `.s1lb` aduce din temă o margine negativă (0 -7px 10px 0). */
      "#livesearch .ts-bf.s1lb{display:flex;margin:6px 0 0;padding:0;position:static}";
    document.head.appendChild(style);
  }

  /* allowTypo controls whether this specific request gets typo tolerance. See the
     two-step logic in runSearch() for why this is a parameter and not a constant —
     the short version: typo_tokens_threshold has no per-field granularity, so a
     single request can't have "typo-tolerant on vendor, exact everywhere else". */
  /* stockFilter: optional "in" | "out". Splits an otherwise identical query into the
     two stock phases used by runTextSearch() — see the STOCK BACKFILL comment there.
     Omitted (undefined) keeps the pre-2026-08-04 behaviour of one unfiltered query. */
  /**
   * Alege ordinea de sortare în funcție de ce a cerut clientul.
   *
   * `sort_rank` conține prioritatea de categorie (1 = lucruri prin care asculți,
   * 4 = accesorii/consumabile). Mutat înaintea lui `rank_score`, rezolvă cazurile
   * măsurate pe 199 de căutări reale din GA4, unde accesoriul ieșea primul:
   *   „sennheiser" -> Suport de perete AMBEO   (nu MOMENTUM 4)
   *   „ambeo"      -> Suport de perete AMBEO   (nu Soundbar-ul AMBEO)
   *   „Soundbar"   -> Suport de perete AMBEO   (nu vreun soundbar)
   *
   * DAR — și asta a ieșit la TESTARE, nu la proiectare — aceeași mutare STRICĂ
   * exact cazul opus, când clientul CERE un accesoriu:
   *   „cablu RCA"   -> Musical Fidelity M8x TT, un pick-up de 40.000 lei (!)
   *   „stativ boxe" -> Atacama HMS, un raft în loc de stativ
   * fiindcă penalizarea de categorie lovește chiar lucrul cerut. Fără A/B-ul de
   * dinainte de push, aș fi reparat trei căutări și aș fi stricat alte două.
   *
   * De aceea comutarea e condiționată: dacă interogarea NUMEȘTE un accesoriu, se
   * păstrează ordinea veche. Lista de mai jos e de cuvinte pe care le scrie OMUL
   * când vrea accesoriul — nu o listă de tipuri din catalog, fiindcă întrebarea
   * aici e „ce a cerut", nu „ce avem".
   */
  var CUVINTE_ACCESORIU = /(cabl|conector|banana|banană|adaptor|hus[aă]|carcas|suport|stativ|stand|puck|perie|clean|curea|ac de schimb|pernu|earpad|gril[aă]|telecomand|baterie|alimentare|surs[aă]|incarcator|încărcător)/i;

  /* Al treilea caz, adaugat 2026-08-24: interogarea NUMESTE O GAMA.
   *
   * Masurat de Tudor pe „beolab": Beolab 19 iesea ULTIMUL din 15, desi are cel mai
   * bun scor comercial dintre toate (rank 622 fata de 289 la restul). Cauza: e
   * clasificat ca subwoofer, iar prioritatea de categorie pune subwooferele dupa
   * boxe — regula corecta la o cautare generica („boxe"), gresita cand cineva cere
   * explicit o gama si vrea sa vada TOT ce contine.
   *
   * Aceeasi logica ca la accesorii, in oglinda: cand interogarea numeste lucrul
   * cerut, penalizarea de categorie lucreaza impotriva intentiei. Aici nu tinem o
   * lista de game (ar trebui intretinuta la fiecare produs nou); semnalul e
   * structural — un cuvant de gama urmat optional de un numar de model, potrivit
   * pe titluri reale. Practic: interogarile scurte, de 1-2 tokenuri, care nu sunt
   * cuvinte de categorie. „beolab", „beolab 8", „reference", „uni-q" intra;
   * „boxe de podea" sau „casti wireless" nu.
   *
   * Efectul: relevanta textului decide in continuare primul cos, apoi scorul
   * comercial, iar categoria ramane doar departajare — deci Beolab 19 urca acolo
   * unde il pune cererea reala, nu unde il coboara tipul lui. */
  var CUVINTE_CATEGORIE = /^(boxe|boxa|boxă|casti|căști|difuzor|difuzoare|amplificator|amplificatoare|subwoofer|subwoofere|soundbar|receiver|receivere|dac|streamer|streamere|pick[- ]?up|pickup|doze|doza|doză|televizor|televizoare|proiector|proiectoare|cd|player|turntable)\b/i;

  function esteCautareDeGama(q) {
    var t = String(q || "").trim();
    if (!t || CUVINTE_CATEGORIE.test(t)) return false;
    var tokens = t.split(/\s+/);
    if (tokens.length > 2) return false;
    // Al doilea token, daca exista, trebuie sa fie un numar/cod de model
    // („beolab 8", „r11"), nu un al doilea cuvant descriptiv.
    if (tokens.length === 2 && !/^[0-9]/.test(tokens[1])) return false;
    return tokens[0].length >= 3;
  }

  function sortByPentru(q) {
    if (CUVINTE_ACCESORIU.test(String(q || ""))) {
      return "_text_match(buckets: 4):desc,rank_score:desc,sort_rank:asc";
    }
    if (esteCautareDeGama(q)) {
      return "_text_match(buckets: 4):desc,rank_score:desc,sort_rank:asc";
    }
    return "_text_match(buckets: 4):desc,sort_rank:asc,rank_score:desc";
  }

  /* vendorFilter (opțional): numele CANONIC al unui vendor — restrânge căutarea la
     produsele lui (`vendor:=X`), calea de scoping din findVendorScope(). Nu se
     escapează manual: numele vin doar din KNOWN_VENDORS/VENDOR_ALIASES, iar forma
     cu backtick e cea deja folosită de brandSearchParams() pe aceleași nume.
     allowTypo capătă și valoarea "strict": num_typos=1 cu prag de 4 caractere —
     folosită ca ULTIMĂ încercare când nici exact, nici sonda de vendor nu au dat
     nimic („subwofer" → subwoofere; „aplif" → amplificatoare). 1, nu 2, fiindcă
     exact la 2 edituri trăiește falsul prieten măsurat: „cambridge" → „cartridge". */
  function searchUrl(q, allowTypo, stockFilter, vendorFilter, typeFilter) {
    var params = new URLSearchParams({
      q: q,
      /* Must stay in step with search() in worker/src/typesense.ts — separate query
         paths against the same collection, and only this one is what a customer hits.

         vendor is weighted HIGHEST because of a real mis-ranking found on 2026-08-03:
         searching "JBL" put an XGIMI projector first, because its title lists its built-in
         speakers ("… Google TV cu Netflix, JBL 2×3W, Stand 160°"). That is a correct match
         but the wrong answer — someone typing a brand wants that brand's products, not a
         product that merely mentions it in a spec list. Weighting vendor above title puts
         real JBL products first for both "JBL" and the typo "jbi", verified live.

         subtitle outranks description for the same reason at a smaller scale: it is short,
         human-written and descriptive ("Căști closed-back, cu drivere de 45 mm"), so a hit
         there is a stronger signal than one buried in a long spec paragraph. */
      /* `product_type` adăugat 2026-08-20, după inspecția a 199 de căutări reale din
         GA4 (5–19 august). Lipsa lui era cea mai costisitoare: magazinul are tipuri
         curate — 390 „Casti", 194 „Subwoofer", 76 „Doza pick-up", 53 „DAC" — dar
         niciunul nu era căutabil, fiindcă tipul nu figura printre câmpuri.

         Ce ieșea înainte, măsurat: „DAC" → Fosi Audio K5 Pro (are „DAC casti" în tip,
         deci prindea prin tags), nu vreunul dintre cele 53 de DAC-uri reale. „DOZE" →
         Musical Fidelity M6xi, un amplificator. „Subwoofer" → Beolab 19, corect din
         întâmplare. Oamenii caută pe categorie mai des decât pe model, iar catalogul
         avea deja răspunsul — doar că nu îl citea nimeni.

         Greutatea 4 îl pune sub `title` (5) și `vendor` (6), la egalitate cu
         `subtitle`: un produs al cărui TITLU conține „subwoofer" e un răspuns mai bun
         decât unul care e doar clasificat așa, dar clasificarea rămâne un semnal
         puternic — e un câmp ales de om, nu proză. */
      /* `title_bg` joins the searchable fields only ON .bg (2026-08-24): it holds the
         title that storefront actually serves, so a Bulgarian shopper typing
         „слушалки" or „Хи-фи стерео система" can match it. Weighted 5, the same as
         `title`, because on .bg it IS the title — not a secondary alias.

         Added per-market rather than globally so a .ro search can never match a
         Bulgarian string: the field would otherwise let a .ro query hit a document
         through text no .ro visitor can see, which is how phantom results are born.
         Harmless while the field is empty everywhere, but the reason outlives that. */
      /* model_parts (2026-09-25): the pieces of the model code as exact tokens —
         "RP-1400SW" is ONE token in title, so "1400" and "rp 1400" never matched it
         (built by worker/src/model-parts.ts). Weighted just BELOW title: measured,
         at equal weight Q Acoustics 3050c outranked NAD C 3050 on "3050", where the
         title holding the number as a word of its own is the more exact match. LAST in the list so every positional parameter
         below (weights, infix) only gains a trailing entry. */
      query_by:
        currentMarket() === "bg"
          ? "vendor,title_bg,title,subtitle,product_type,description,tags,sku,model_parts"
          : "vendor,title,subtitle,product_type,description,tags,sku,model_parts",
      query_by_weights:
        currentMarket() === "bg" ? "6,5,5,4,4,1,2,3,4" : "6,5,4,4,1,2,3,4",
      /* One row per PRODUCT, not per variant (Tudor, 2026-08-03: "daca am kef ls50
         wireless 2 in 5 culori si eu scriu in search kef ls50 wireless 2 sa nu imi
         arate de 5x kef ls50").

         The index stores one document per variant — deliberately, since each variant
         carries its own price and stock — so a 5-colour product filled all 5 result
         slots. Grouping collapses them at query time, which keeps the per-variant data
         available rather than throwing it away at index time.

         group_limit 5, not 1: the renderer needs every variant of a group to work out
         the lowest price that is actually buyable (see groupPrice). 1 would return only
         the most relevant variant, whose price is arbitrary from the shopper's side. */
      group_by: "product_id",
      group_limit: String(GROUP_VARIANT_LIMIT),
      /* Text relevance first, THEN commercial rank, then stock.
         `buckets: 4` is what makes rank_score matter at all. Without it Typesense's raw
         text score is so finely graded that no two products ever tie, so the second sort
         key is dead: searching "kef" returned KEF S3 / SQ1 / B2 (short titles score
         marginally higher than long ones — 578730123373578364 vs ...362) while the
         LS50 sat at position 27 and the brand-new LS Luxe at 10. Bucketing collapses
         comparable matches into 4 tiers so rank_score decides within a tier; the same
         query now returns LS Luxe, LS60, Reference 1, Reference 5.
         Still text-first: an exact query like "kef ls50 wireless 2" lands in a higher
         bucket than the generic brand matches and wins outright, verified. */
      /* sort_rank (not category_priority/in_stock_rank separately) — Typesense caps
         sort_by at 3 clauses ("Only upto 3 sort fields are allowed", hit live
         2026-08-11) and _text_match + rank_score already use 2 of the 3 slots.
         sort_rank = category_priority*2 - in_stock_rank (worker/src/typesense-sync.ts,
         computeSortRank()): a same-day, zero-sales-history launch batch ties
         rank_score completely (measured on the 50-product Fosi Audio launch,
         2026-08-11 — "fosi" surfaced cables before headphones/DACs/amps because
         rank_score had nothing left to break the tie on), so category_priority sorts
         headphones/DACs/streamers/turntables/speakers first, amps/subs/receivers
         second, other AV hardware third, cables/accessories last — but only when
         rank_score is tied, and in_stock_rank still breaks ties within a category. */
      /* `sort_rank` mutat ÎNAINTEA lui `rank_score` pentru căutările GENERICE
         (2026-08-20). `sort_rank` conține prioritatea de categorie: 1 = lucruri prin
         care asculți (căști, boxe, DAC, pick-up), 4 = accesorii și consumabile.

         Problema măsurată pe 199 de căutări reale din GA4: la termeni generici,
         accesoriul ieșea înaintea produsului.
           „sennheiser" -> Suport de perete AMBEO   (nu MOMENTUM 4)
           „ambeo"      -> Suport de perete AMBEO   (nu Soundbar-ul AMBEO)
           „Soundbar"   -> Suport de perete AMBEO   (nu vreun soundbar)
         Toate trei aveau `_text_match` egal — suportul chiar conține cuvântul în
         titlu — iar `rank_score` decidea în favoarea accesoriului, fiindcă un suport
         ieftin se vinde des. Categoria era criteriul potrivit, dar venea ultima.

         De ce e sigur pentru căutările specifice: `_text_match(buckets: 4)` rămâne
         PRIMUL. Cine scrie „AT-LP120X" are o potrivire mult mai bună pe produs decât
         pe orice accesoriu, deci intră în alt bucket și categoria nu apucă să conteze.
         Ordinea se schimbă doar când potrivirea de text e la fel de bună — exact
         cazul în care „ce fel de lucru e" e întrebarea corectă.

         Verificat că nu strică cazul invers: cine caută „husa", „stativ boxe" sau
         „cablu RCA" cere chiar accesoriul, iar potrivirea pe titlu îl aduce în
         bucket-ul de sus înaintea oricărui produs principal. */
      sort_by: sortByPentru(q),
      num_typos: allowTypo === "strict" ? "1" : "2",
      min_len_1typo: allowTypo === "strict" ? "4" : "1",
      min_len_2typo: "6",
      prefix: "true",
      /* Mid-token matching for model codes. `prefix` only anchors at the START of a
         token, and "AT-LP120XBT-USB" indexes as ONE token — hyphens are not separators
         (the collection sets neither token_separators nor symbols_to_index, verified
         against the live schema 2026-08-04). So "AT-LP120" matched but "LP120",
         "LP120XBT" and "XBT" all returned 0, as did "8000F" (RP-8000F II) and "X2900"
         (AVR-X2900H). 189 of 1,250 catalogue products (15%) carry a hyphenated model
         code; Audio-Technica alone had 41.

         Per-field, aligned to query_by's six fields — ON for title and sku (short,
         identifier-like, exactly the case Typesense endorses for infix), OFF for
         description (long prose, the case the docs warn against).

         `fallback`, NOT `always` — corrected 2026-08-04 the same day, after Tudor
         reported that typing MORE of a title made results worse ("Kardon Onyx Studio 9"
         → XGIMI projectors; "elac-od-m61" → nothing useful).

         `always` matches a query token as a substring ANYWHERE in the title, in addition
         to normal matching. So "OD" in "ELAC OD-M61" matches p-OD-ea, C-OD-a, N-OD-E…
         Measured: `q=OD` alone returns 179 results under `always` vs 4 under `fallback`,
         and "ELAC OD-M61" returns 121 (top: Elac Debut ConneX DCB61) vs 2 (top: the
         correct Elac Muro Pro OD-M61). It was never a ranking problem — 119 junk
         documents were flooding the candidate pool, and no sort tuning can fix that
         because they are legitimately in the result set.

         My earlier note claimed `always` beat `fallback` on "M50x" and "51M". That was
         measured on ten queries and was too narrow a test. Re-measured under `fallback`:
         LP120, M50x, 8000F, X2900, XBT, LP120XBT and the out-of-stock LP60 all still
         resolve correctly, and kef/klipsch/JBL/sennheiser are untouched. Only "51M"
         regresses, and it was already ranked 3rd behind unrelated cables.

         Do not switch back to `always` without re-running `q=OD`.

         POSITIONAL, one entry per query_by field — so it MUST be rebuilt whenever
         query_by changes shape. On .bg query_by gains `title_bg` in position 2, which
         gets `fallback` for the same reason `title` does (the Typesense field is
         declared infix-enabled, and it holds the same short identifier-like strings).
         Getting this wrong does not error: it silently shifts every later field's
         setting by one. */
      /* highlight_fields: de aici aflam ÎN CE CÂMP a potrivit fiecare rezultat —
         semnalul pe care se bazeaza retrogradarea „doar-descriere" de mai jos
         (vezi doarInDescriere). Costa cateva sute de octeti pe raspuns si nu
         schimba nici scorul, nici ordinea returnata de Typesense. */
      /* `sku` adăugat 2026-08-29: fără el, un produs găsit DOAR după codul lui
         ieșea cu `campuri` gol, adică exact semnătura lui „menționează X" — și
         era retrogradat sub potrivirile de titlu, deși o potrivire pe SKU e cea
         mai tare formă de potrivire care există. Acum `description` redevine,
         cum spune și comentariul de la doarInDescriere(), SINGURUL câmp căutat
         care nu e cerut aici. */
      /* include_fields: se trimit înapoi DOAR câmpurile pe care le citește clientul.
         Restul — `description` în primul rând, dar și sku/rank_score/sort_rank și
         zecile de `specs_*` — sunt folosite de Typesense la potrivire și sortare,
         pe server, și n-au ce căuta în răspuns.
         MĂSURAT 2026-08-29: „casti" 118 KB → 78 KB, „beolab" 118 KB → 57 KB
         (−33% și −51%). Contează de două ori: mai puțină bandă plătită (10 GB
         gratuit pe lună, apoi $0,09/GB) și mai puțin de descărcat pe mobil, unde
         octeții costă timp, nu doar bani.
         LISTA E EXHAUSTIVĂ prin verificare, nu prin memorie: sunt toate câmpurile
         citite ca `doc.<ceva>` în fișierul ăsta, plus `in_stock` (citit ca
         `d.in_stock` în toProductRows). Un câmp scos de aici din greșeală nu dă
         eroare — dispare tăcut din interfață. */
      include_fields:
        "product_id,title,title_bg,subtitle,subtitle_bg,price,price_bgn," +
        "image_url,handle,vendor,product_type,in_stock,markets,tags",
      highlight_fields: "title,subtitle,product_type,tags,vendor,sku,model_parts",
      highlight_full_fields: "title,subtitle,product_type",
      infix:
        currentMarket() === "bg"
          ? "off,fallback,fallback,off,off,off,off,fallback,off"
          : "off,fallback,off,off,off,off,fallback,off",
      per_page: String(FETCH_GROUP_LIMIT),
    });
    /* typo_tokens_threshold: 0 does not mean "typo tolerance only when the exact
       match is empty" — verified against the Typesense docs 2026-08-03, it means
       typo tolerance OFF, full stop. Setting it to kill "cambridge" -> "cartridge"
       silently killed every genuine typo too ("klipsh", "sennheizer", "jbi" all
       regressed to 0 results, caught 2026-08-03 when Tudor searched "klipsh" and the
       new-arrival Search panel showed 0). Sent only for the exact-match pass
       (allowTypo=false); omitted entirely on the typo-tolerant retry. */
    if (!allowTypo) params.set("typo_tokens_threshold", "0");
    var filters = [];
    if (stockFilter === "in") filters.push("in_stock:true");
    else if (stockFilter === "out") filters.push("in_stock:false");
    if (vendorFilter) filters.push("vendor:=`" + vendorFilter + "`");
    /* Categoria ca FILTRU, nu ca text liber — vezi TIPURI_CATEGORIE. */
    if (typeFilter) filters.push(typeFilter);
    /* Filtrul de piață se aplică MEREU, chiar când nu există alt filtru — vezi
       filtruDePiata(). De aceea `set` e necondiționat aici, spre deosebire de
       varianta veche care sărea peste `filter_by` când lista era goală. */
    params.set("filter_by", cuFiltruDePiata(filters.join(" && ")));
    var protocol = CONFIG.protocol || "https";
    var port = CONFIG.port || "443";
    var collection = CONFIG.collection || "products";
    return (
      protocol + "://" + CONFIG.host + ":" + port +
      "/collections/" + collection + "/documents/search?" + params.toString()
    );
  }

  /* Standalone query used only as a discriminator (see runSearch()): does this query
     text plausibly typo-match a REAL vendor in the catalog, or not? Scoped to the
     vendor field alone, with typo tolerance on, small per_page — cheap, and the
     answer decides whether the main retry is allowed to use typo tolerance at all.

     This is what tells "klipsh" (no vendor named that, but one typo-edit from
     "Klipsch") apart from "cambridge" (also no exact match, but zero-edit-distance
     from any vendor we carry — Cambridge Audio just isn't in the catalog, and typo
     tolerance must not paper over that with an unrelated turntable brand).

     min_len_1typo/min_len_2typo MUST match searchUrl()'s values, not just num_typos —
     found 2026-08-03 while regression-testing this exact function: without them
     Typesense's default min-length gate (4+ chars for a single typo) silently
     refused to typo-match "jbi" (3 chars) against "JBL" at all, so the probe came
     back 0 and the main query never got its typo-tolerant retry — reintroducing the
     "jbi" regression this whole two-step design exists to prevent. */
  function vendorTypoUrl(q) {
    var params = new URLSearchParams({
      q: q,
      query_by: "vendor",
      num_typos: "2",
      min_len_1typo: "1",
      min_len_2typo: "6",
      prefix: "true",
      per_page: "1",
      /* Sonda decide DACĂ interogarea e un typo de brand real, pe baza lui `found`.
         Fără filtrul de piață, „cambridge" întorcea 51 pe .ro (brand vândut doar
         pe .bg) și .ro pornea un retry typo-tolerant pentru un brand pe care nu-l
         poate vinde. Măsurat înainte de fix, 2026-08-26. */
      filter_by: filtruDePiata(),
    });
    var protocol = CONFIG.protocol || "https";
    var port = CONFIG.port || "443";
    var collection = CONFIG.collection || "products";
    return (
      protocol + "://" + CONFIG.host + ":" + port +
      "/collections/" + collection + "/documents/search?" + params.toString()
    );
  }

  /* One tag, INDEXED as a facet (see PRODUCTS_SCHEMA in worker/src/typesense.ts —
   * `tags` is `string[], facet: true`), written to Shopify by the section-A audit
   * on 2026-08-03. `filter_by: tags:!=X` and `tags:=X` are both valid Typesense
   * filter forms on a faceted string[] field — verified against the schema
   * before use here, not assumed. */
  var INSTALLATION_ROLE_TAG = "search_role_installation_project";

  /**
   * Builds the params shared by both phases of a brand-intent search — same
   * query_by/weights/sort_by/typo config as the ordinary searchUrl(), so a
   * brand-browse result is scored by the exact same relevance rules, just
   * pre-filtered to one vendor and split into two role-based fetches instead of
   * one flat one.
   */
  function brandSearchParams(canonicalVendor, extraFilter, limit) {
    var filters = ['vendor:=`' + canonicalVendor + '`'];
    if (extraFilter) filters.push(extraFilter);
    return new URLSearchParams({
      q: "*",
      /* Deliberately NOT market-aware, unlike searchUrl()'s query_by: `q` is "*", so
         no text is matched against these fields and adding `title_bg` here would
         change nothing. Ordering comes from rank_score/sort_rank below. */
      query_by: "vendor,title,subtitle,product_type,description,tags,sku",
      filter_by: cuFiltruDePiata(filters.join(" && ")),
      group_by: "product_id",
      group_limit: String(GROUP_VARIANT_LIMIT),
      // Commercial rank decides order here — there is no free-text query to rank
      // against (q:"*"), so rank_score is the PRIMARY sort, not a tiebreaker.
      // sort_rank remains the tiebreaker (category first, then in-stock within a
      // category — see the ordinary searchUrl()'s sort_by comment above); the hard
      // in-stock-first guarantee comes from the two-phase fetch in
      // runBrandIntentSearch(), not from this sort.
      sort_by: "rank_score:desc,sort_rank:asc",
      per_page: String(limit),
    });
  }

  function brandSearchFetchUrl(params) {
    var protocol = CONFIG.protocol || "https";
    var port = CONFIG.port || "443";
    var collection = CONFIG.collection || "products";
    return (
      protocol + "://" + CONFIG.host + ":" + port +
      "/collections/" + collection + "/documents/search?" + params.toString()
    );
  }

  /**
   * Runs the exact-brand-intent search: consumer products first, installation
   * products only to fill remaining slots — per docs/search-ranking-review-
   * codex-response-2026-08-03.md, "Politica de rezultat pentru dropdown de 8".
   *
   * Two sequential fetches, not one query with a client-side sort, because
   * Typesense's `sort_by` cannot express "all rows without tag X, THEN all rows
   * with tag X" — only field-value ordering. Two role-scoped queries are the
   * documented way to get that guarantee (see docs/search-ranking-review-codex-
   * response-2026-08-03.md, "Nu trebuie considerată problema #2 rezolvată... o
   * simplă penalizare statică în computeRank() nu garantează revenirea lor" —
   * a static score bucketed by _text_match cannot guarantee consumer-first
   * ordering, which is exactly why this branch does NOT use buckets or
   * _text_match at all).
   *
   * Falls back to the ordinary text-first search if the vendor-filtered query
   * returns zero results (a vendor with zero live products on .ro, or a filter
   * typo) — never show "no results" when general search might still find
   * something, per this file's existing fail-open philosophy.
   */
  function runBrandIntentSearch(query, canonicalVendor, container, thisGeneration, signal) {
    var consumerParams = brandSearchParams(
      canonicalVendor,
      "tags:!=`" + INSTALLATION_ROLE_TAG + "`",
      RESULT_LIMIT
    );
    fetchJson(brandSearchFetchUrl(consumerParams), signal)
      .then(function (consumerData) {
        if (thisGeneration !== latestGeneration) return;
        var consumerRows = toProductRows(consumerData);
        /* Numărul din „Vezi toate cele N produse" trebuie să fie TOTALUL brandului,
           nu câte a întors interogarea asta.

           Greșit inițial (prins pe DEV, 2026-08-20): folosisem `consumerData.found`,
           care numără doar produsele de CONSUM — filtrul acestei prime interogări
           exclude rolul de instalare. La KEF ieșea „72 produse", în timp ce colecția
           are 124. Un link care promite mai puțin decât livrează e o promisiune
           greșită, chiar dacă în favoarea noastră.

           `found` e numărul de GRUPURI (grupăm după product_id), deci numără produse,
           nu variante — corect pentru eticheta asta. Îl luăm din a doua interogare,
           care nu are filtru de rol. Diferența față de productsCount al colecției
           poate rămâne mică (colecția exclude openbox prin regulă, indexul are alte
           reguli de publicare), dar ordinul de mărime e corect. */
        /* `handle` NU există în domeniul acestei funcții — semnătura e
           (query, canonicalVendor, container, thisGeneration, signal). Versiunea
           anterioară îl referea oricum, ceea ce arunca ReferenceError, prins de
           `.catch`-ul de mai jos, care cădea tăcut pe căutarea generică: brandul
           își pierdea ordinea și rândul „Vezi toate produsele X".
           Trecea neobservat pe .ro fiindcă acolo ruta normală e runCollectionSearch
           (citește ordinea din colecție) și abia eșecul ei ajungea aici. Pe .bg,
           unde calea de colecție e dezactivată (prețuri RON), asta a DEVENIT ruta
           normală de brand — deci bugul a trecut din latent în vizibil.
           Găsit de Codex la review, 2026-08-28.
           Handle-ul nu e necesar: renderer-ul îl derivă din vendor prin
           brandCollectionHandle() când lipsește. */
        var brandLink = { vendor: canonicalVendor, total: 0 };
        var totalPromise;
        if (canonicalVendor) {
          /* Cursa care trebuie evitată: interogarea de total e asincronă, iar randarea
             poate ajunge înaintea ei — atunci eticheta ar rămâne fără număr. De aceea
             NU se randează nimic până nu se rezolvă (sau eșuează) și ea; e o cerere de
             un singur document, deci costul e neglijabil față de un rând ciuntit. */
          totalPromise = Promise.all([
            fetchJson(brandSearchFetchUrl(brandSearchParams(canonicalVendor, "", 1)), signal)
              .then(function (allData) { brandLink.total = allData.found || 0; })
              .catch(function () { /* fără total: eticheta cade elegant pe „Vezi toate produsele X" */ }),
            /* rândul „Vezi toate" nu trebuie să ducă într-un 404 — vezi COLECTII_BRAND */
            verificaColectieBrand(brandCollectionHandle(canonicalVendor))
          ]);
        } else {
          /* La categorie NU cerem un total: `products.json` nu îl expune, iar o a doua
             cerere doar pentru cifră nu merită. Eticheta cade pe forma fără număr —
             „Vezi toate produsele din <categorie>" — care e la fel de utilă. */
          totalPromise = Promise.resolve();
        }
        if (consumerRows.length >= RESULT_LIMIT) {
          totalPromise.then(function () {
            if (thisGeneration !== latestGeneration) return;
            themeSearchSuppressed(true);
            renderResults(container, consumerRows, query, brandLink);
          });
          return;
        }
        // Fewer than RESULT_LIMIT consumer products — backfill with installation
        // products, per the review's explicit rule (only when the quota isn't
        // already met by consumer products alone).
        var remaining = RESULT_LIMIT - consumerRows.length;
        var installParams = brandSearchParams(
          canonicalVendor,
          "tags:=`" + INSTALLATION_ROLE_TAG + "`",
          remaining
        );
        return fetchJson(brandSearchFetchUrl(installParams), signal)
          .then(function (installData) {
            if (thisGeneration !== latestGeneration) return;
            var combined = consumerRows.concat(toProductRows(installData));
            if (combined.length === 0) {
              // Vendor matched exactly but has zero live products in either role —
              // fall back to ordinary search rather than show an empty brand result.
              runTextSearch(query, container, thisGeneration, signal);
              return;
            }
            totalPromise.then(function () {
              if (thisGeneration !== latestGeneration) return;
              themeSearchSuppressed(true);
              renderResults(container, combined, query, brandLink);
            });
          })
          .catch(function (err) {
            if (err.name === "AbortError") return; // superseded by a newer keystroke
            if (thisGeneration !== latestGeneration) return;
            console.error("typesense-search: brand backfill failed, using consumer-only", err);
            themeSearchSuppressed(true);
            renderResults(container, consumerRows, query, brandLink);
          });
      })
      .catch(function (err) {
        if (err.name === "AbortError") return; // superseded by a newer keystroke
        if (thisGeneration !== latestGeneration) return;
        console.error("typesense-search: brand search failed, falling back to general search", err);
        runTextSearch(query, container, thisGeneration, signal);
      });
  }

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }

  /* ── Which storefront this page is (2026-08-21) ───────────────────────────────
     Written by typesense-search-init.liquid from `localization.market.handle`, the
     same signal the rest of the theme gates on. Defaults to "ro" so a config that
     predates the field behaves exactly as before. One reader, so the fallback lives
     in one place instead of being repeated at every call site. */
  /* Doar piețele CUNOSCUTE. Codex a semnalat (2026-08-28) că vechiul
     `|| "ro"` era un fallback nesigur: o pagină .bg cu un config fără `market` ar
     fi cerut și randat `markets:=ro`, adică exact scurgerea pe care mecanismul o
     previne. Acum o valoare necunoscută întoarce null, iar apelantul oprește
     căutarea noastră și lasă căutarea nativă a temei — degradare, nu scurgere. */
  var PIETE_CUNOSCUTE = { ro: 1, bg: 1 };

  function currentMarket() {
    var m = window.typesenseConfig && window.typesenseConfig.market;
    return PIETE_CUNOSCUTE[m] ? m : null;
  }

  /* ── IZOLAREA PIEȚELOR — filtru SERVER-SIDE, obligatoriu pe fiecare cerere ────
   *
   * Cerința lui Tudor (2026-08-26), la deschiderea porții .bg: „produsele din .bg
   * să nu se scurgă pe .ro și din .ro pe .bg".
   *
   * DE CE NU E DE AJUNS `onCurrentMarket()`: acela e un POST-filtru — documentele
   * străine sunt aduse în browser și abia apoi ascunse la randare. Măsurat pe
   * indexul live înainte de fix: ruta de brand pentru „Cambridge Audio" (barat pe
   * .ro prin restricție de distribuție) întorcea 36 de produse `markets:['bg']`,
   * iar sonda de vendor 51 — toate ajungeau pe .ro. Nu se VEDEAU, dar:
   *   · ajungeau pe rețea, la un client căruia nu i se pot vinde;
   *   · falsificau deciziile care se iau pe `found`, nu pe rânduri — sonda de
   *     vendor „cambridge" returna 51, deci .ro trata brandul ca existent;
   *   · un singur apelant care uită post-filtrul devine o scurgere vizibilă.
   *
   * Filtrul de mai jos e SINGURA cale: se lipește în `filter_by` la fiecare cerere,
   * în toate cele patru constructoare de URL (searchUrl, brandSearchParams,
   * substitutionUrl, vendorTypoUrl). `onCurrentMarket()` rămâne ca a doua plasă de
   * siguranță — apărare în adâncime, nu redundanță inutilă.
   *
   * `markets:=<piata>` se aplică NECONDIȚIONAT. Documentele pre-tranziție fără
   * câmpul `markets` nu mai există (verificat 2026-08-26: 5.188 documente în
   * index, 5.076 cu markets:=ro și 5.183 cu markets:=bg), deci filtrul nu poate
   * goli rezultatele — iar dacă vreodată reapare un document nestampilat, e mai
   * bine să lipsească dintr-o piață decât să apară în cea greșită. */
  function filtruDePiata() {
    return "markets:=" + currentMarket();
  }

  /* Lipește filtrul de piață peste un `filter_by` existent (sau îl creează).
     Un singur loc care știe cum se compun filtrele, ca să nu poată diverge. */
  function cuFiltruDePiata(filtruExistent) {
    var m = filtruDePiata();
    return filtruExistent ? filtruExistent + " && " + m : m;
  }

  /* ── Per-market presentation (2026-08-24) ─────────────────────────────────────
     The index is one collection serving both storefronts, so a document carries
     BOTH presentations: `title`/`price` are what .ro serves (Romanian, RON), while
     `title_bg`/`price_bgn` are what audiomonkey.bg actually serves for the same
     product — captured verbatim from the .bg storefront by the sync worker, not
     translated by us.

     .bg's catalogue is a MIX: Bulgarian where a translation exists, Romanian
     fallback otherwise (measured 2026-08-21). So `title_bg` is not "the Bulgarian
     title", it is "the title .bg serves" — falling back to `title` reproduces
     exactly what the storefront itself would show, which is the correct behaviour
     rather than a compromise.

     Both fall back to the .ro field when the .bg one is absent, which also covers
     the whole transition window: a document written before the two-market sync has
     neither field, and .ro users are unaffected either way. */
  /* MONEDA PIEȚEI BULGARE ESTE EURO, nu leva (confirmat de Tudor și verificat pe
     storefront 2026-08-27: `Shopify.currency.active === "EUR"` pe audiomonkey.bg).
     Câmpul din index se numește `price_bgn` din motive istorice — numele e o
     rămășiță, valoarea pe care o poartă e cea servită de .bg, adică EUR. Verificat
     pe KEF LS50 Wireless II: `price_bgn` = 2499, iar storefront-ul arată €2.499.
     Sufixul „ лв." de dinainte eticheta valori corecte cu moneda GREȘITĂ — genul de
     eroare pe care nimeni n-o raportează ca bug, dar care schimbă complet ce crede
     clientul că plătește. Redenumirea câmpului în index e o lucrare separată. */
  var BG_CURRENCY_SUFFIX = " €";

  function displayTitle(doc) {
    if (!doc) return "";
    if (currentMarket() === "bg" && doc.title_bg) return doc.title_bg;
    return doc.title || "";
  }

  /* AM26 2026-08-28 — brandul urcă deasupra modelului şi în rezultatele de
     căutare, ca pe cardurile de colecţie şi pe PDP (decis de Tudor: „lasam pe
     toate brandurile"; apoi „ce ar fi sa facem si in search la fel").

     ACELEAŞI reguli ca pe celelalte suprafeţe, şi contează să rămână aşa:
     - comparaţia e case-insensitive. Cu potrivire exactă, un brand scris cu
       majuscule în câmpul vendor dar normal în titlu iese TĂCUT pe dinafară —
       măsurat pe catalog: SENNHEISER 5% din 104, WIREWORLD 0% din 141.
     - tăierea se face cu `slice` pe titlul ORIGINAL, deci modelul păstrează
       scrierea din titlu, nu pe cea din vendor.
     - fail-safe: dacă titlul nu începe fix cu „<vendor> ", nu se taie nimic şi
       rândul arată exact ca înainte. Asta lasă singură în pace vinilurile,
       bateriile şi titlurile descriptive („Boxe podea KEF R7") — fără listă de
       vendori excluşi, care ar trebui întreţinută manual.

     Lucrează pe titlul AFIŞAT, nu pe `doc.title`, deci pe .bg taie din
     `title_bg` — altfel pe piaţa bulgară ar compara vendorul cu titlul greşit. */
  function splitBrand(doc) {
    var title = displayTitle(doc);
    var vendor = (doc && doc.vendor) || "";
    if (!vendor || !title) return { brand: "", model: title };
    var prefix = vendor + " ";
    if (title.toLowerCase().indexOf(prefix.toLowerCase()) !== 0) {
      return { brand: "", model: title };
    }
    var rest = title.slice(prefix.length);
    if (!rest.trim()) return { brand: "", model: title };
    return { brand: vendor, model: rest };
  }

  /** The price to SHOW, in the current market's currency. Falls back to the RON
   *  price only when the BGN one is missing — a .bg row is better served by a
   *  slightly-wrong-currency number than by no price at all, and the fallback is
   *  visible in the suffix rather than silent. */
  function displayPrice(doc, ronPrice) {
    if (currentMarket() === "bg" && doc && typeof doc.price_bgn === "number") {
      return { value: doc.price_bgn, currency: "bg" };
    }
    return { value: ronPrice, currency: "ro" };
  }

  function formatPrice(value, currency) {
    /* No decimals (Tudor, 2026-08-03): "3.029,00 lei" -> "3.029 lei". Hi-fi prices here
       are whole lei in practice, and the ",00" is pure noise in a dropdown where the
       price sits next to a 60-character product title.
       Rounded rather than truncated so a genuine 3.029,60 does not read as 3.029 —
       still keeps the thousands separator from ro-RO.

       BGN keeps the same no-decimals rule and the same ro-RO grouping (both locales
       use "." for thousands), only the suffix changes — the lev is not a currency
       where Hi-Fi prices need stotinki either. */
    var n = Math.round(Number(value) || 0);
    var text = n.toLocaleString("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    /* Sufix, nu prefix: așa afișează storefront-ul .bg însuși (€2.499 apare ca
       „2.499 €" în listele lui), deci dropdown-ul trebuie să arate la fel. */
    return text + (currency === "bg" ? BG_CURRENCY_SUFFIX : " lei");
  }

  /* Flattens Typesense's grouped response into one entry per product.
   *
   * `price` is the lowest price among IN-STOCK variants, not the lowest overall. That
   * distinction is not pedantry: the KEF LS50 Wireless II has a 7,999 variant that is
   * out of stock alongside four at 9,995, so "de la 7.999 lei" would advertise a price
   * nobody can actually pay. Falls back to the overall minimum only when the whole
   * product is out of stock, where there is nothing buyable to quote anyway.
   *
   * `hasRange` marks products whose in-stock variants differ in price, so the renderer
   * can say "de la X" instead of implying a single price. */
  /* ── Market guard (2026-08-21) ────────────────────────────────────────────────
     The index is becoming two-market (worker sets `markets` per document: "ro",
     "bg" or both) so brands barred from .ro — Cambridge, Ruark, EverSolo, Argon —
     can finally be searchable on .bg. This guard keeps them OUT of the .ro
     dropdown, client-side.

     MISSING FIELD = VISIBLE, deliberately: documents written before the worker
     change have no `markets` yet, and today's whole index is .ro-only by
     construction, so an untagged doc is by definition a .ro doc. The strict
     server-side `filter_by: markets:=ro` would instead return ZERO results for
     every untagged doc — a dead search for the entire transition window. Switch
     to the server-side filter (and delete this guard) only after a full sync lap
     has stamped every document — verify with
     q=*&filter_by=markets:=ro matching ~the whole index first.

     Transitional wart, accepted: the "Vezi toate cele N produse" total comes from
     an unfiltered count query, so once .bg-only docs land it may overcount by a
     brand's .bg-only handful until the server-side filter takes over. */
  /* Subtitlul în limba pieței, sau nimic. Separat de randare ca ziua în care
     apare `subtitle_bg` să fie o singură linie de schimbat, într-un loc anume. */
  function subtitleForMarket(doc) {
    if (!doc) return "";
    if (currentMarket() === "bg") return doc.subtitle_bg || "";
    return doc.subtitle || "";
  }

  function onCurrentMarket(doc) {
    var m = doc && doc.markets;
    if (!m || !m.length) return true; // pre-transition doc: .ro-only index, no field yet
    return m.indexOf(currentMarket()) !== -1;
  }


  /* ── Un rând per MODEL, nu per configurație de montare (2026-08-24) ───────────
     Tudor, măsurat pe „beolab": 15 rezultate există, dar 5 din primele 10 poziții
     erau același Beolab 8 — o dată pentru fiecare montare (stativ de masă, de
     podea, suport de perete, de plafon, pereche). Beolab 28 lua încă 2. Practic
     jumătate din dropdown pentru două modele, în timp ce 28/50/90 rămâneau
     nevăzute. Ce vrea cineva care scrie „beolab" e GAMA.

     De ce nu rezolvă group_by: în Shopify fiecare montare e un PRODUS separat, cu
     product_id propriu, deci Typesense grupează corect din punctul lui de vedere.
     Reparația de fond e unirea lor într-un produs cu opțiuni (rămâne pe listă, e
     lucrare de catalog); asta e stratul de prezentare până atunci.

     CE NU COLAPSEAZĂ, verificat pe toate cele 4.052 de produse înainte de a scrie
     regula — o versiune naivă („taie tot după linie") unea greșit:
       · „Sonos Era 300" cu „Sonos Era 300 Wall Mount" — al doilea e ACCESORIUL de
         perete, alt produs, alt preț;
       · cele 4 „iFi GO pod Ear Loops" cu MMCX / A2DC / 2-pin / Pentaconn —
         conectori genuin diferiți, nu ambalaje ale aceluiași lucru;
       · un produs cu resigilatul lui — stare și preț diferite, se văd separat.
     De aceea coada se taie doar când descrie CHIAR o montare sau o cantitate, iar
     „resigilat" ține familii distincte. */
  var CONFIG_TAIL =
    "(?:pereche|bucată|bucata|set de \\d+|pachet de \\d+|" +
    "cu (?:stativ|suport|montare)[^\u2013\u2014]*|stativ[^\u2013\u2014]*|suport[^\u2013\u2014]*)";
  /* Aceleași configurații, dar în ENGLEZĂ, la sfârșit de titlu și FĂRĂ separator:
     „Beolab 28 wall mount", „Beolab 28 floor stand" — 33 de produse în catalog.
     Separat de CONFIG_TAIL fiindcă tiparul e altul (nu are liniuță în față).

     ATENȚIE, verificat pe catalogul real înainte de a scrie regula: aceleași
     cuvinte denumesc și ACCESORII vândute separat — „KEF S1 Floor Stand" (1.749
     lei, tip „Stativ boxe") e un stativ, nu boxa. Diferența nu se vede în titlu,
     se vede în product_type: varianta de montare păstrează tipul produsului
     principal („Beolab 28 wall mount" e „Boxe amplificate", 81.779 lei), iar
     accesoriul are tipul lui. De aceea familyKey taie coada DOAR pentru produsele
     principale — altfel „KEF S1 Floor Stand" ar fi colapsat în „KEF S1", un
     produs complet diferit. */
  var CONFIG_TAIL_EN_RX = /\s+(wall mount|floor stand|table stand|ceiling mount)\s*$/i;
  var CONFIG_RX = new RegExp(
    "\\s*[\u2013\u2014]\\s*" + CONFIG_TAIL + "(?:\\s*,\\s*" + CONFIG_TAIL + ")*\\s*$" +
      "|,\\s*cu\\s+(?:stativ|suport|montare)\\b[^,]*$",
    "i"
  );
  var RESEALED_RX = /\bresigilat\b/i;

  /** Cheia de familie a unui titlu: modelul, fără coada de configurație.
   *  Resigilatele primesc cheie proprie — nu se amestecă niciodată cu produsul nou. */
  function familyKey(title, doc) {
    var t = String(title || "").trim();
    var resealed = RESEALED_RX.test(t);
    var base = t.replace(RESEALED_RX, "");
    /* Coada englezească se taie doar când produsul e din categoria principală —
       vezi comentariul de la CONFIG_TAIL_EN_RX. Fără `doc` (apel din teste sau
       dintr-o cale care nu-l are), se comportă ca înainte: nu taie. */
    if (doc && estePrincipal(doc)) base = base.replace(CONFIG_TAIL_EN_RX, "");
    var prev = null;
    // Cozile se pot înlănțui: „pereche, cu stativ de masă".
    while (prev !== base) {
      prev = base;
      base = base.replace(CONFIG_RX, "").trim().replace(/,$/, "").trim();
    }
    return base.toLowerCase() + (resealed ? " resigilat" : "");
  }

  /** Colapsează rândurile aceluiași model într-unul singur, păstrând ordinea.
   *
   *  Rândul care rămâne e cel mai IEFTIN dintre cele cumpărabile (aceeași regulă ca
   *  la variante în toProductRows: nu promitem un preț pe care nimeni nu-l poate
   *  plăti), iar `hasRange` devine adevărat dacă montările diferă între ele — așa
   *  „Beolab 8" se citește „de la 15.999 lei", nu prețul unei montări anume. */

  /* ── Ordinea în interiorul unei game (2026-08-24, Tudor) ──────────────────────
     Cerut direct: la „beolab" vreau Beolab 8, 19, 28, 50, 90 — în ordinea
     numărului de model — și NU vreau Transmitter printre primele.

     DE CE nu rezolvă rank_score. Toate boxele Beolab au EXACT același scor
     comercial, 289, fiindcă niciuna nu are istoric de vânzări în `sales_history`
     și primesc toate același „prior" (MISSING_REVENUE_PRIOR_DEMAND din
     computeRank). La egalitate perfectă, ordinea dintre ele e arbitrară — o dă
     Typesense după scorul de text, care e practic identic. Iar Beolab Transmitter
     1 URCĂ peste toate cu 461, fiindcă el chiar s-a vândut: un accesoriu de 1.999
     lei bate cinci boxe de zeci de mii, ceea ce e exact opusul intenției
     comerciale la o căutare pe numele gamei.

     DOUĂ REGULI, ambele doar pentru căutările pe gamă (esteCautareDeGama):

     1. ACCESORIILE COBOARĂ SUB PRODUSELE PRINCIPALE. „Beolab" înseamnă boxele
        Beolab; transmițătorul și modulele rămân în listă, dar la coadă. Nu le
        scoatem — cine chiar caută transmițătorul tot îl găsește. Detectarea se
        face pe `product_type`, câmpul pe care catalogul îl are deja curat, NU pe
        titlu: „Transmitter" e clasificat „DAC", Beoconnect Core „Streamer".
        Semnalat separat: clasificarea aia e discutabilă (un transmițător nu e un
        DAC), dar repararea ei atinge product_type pe produse live și afectează
        colecții și filtre — lucrare separată, decisă cu Tudor.

     2. ÎN INTERIORUL ACELEIAȘI CLASE, ORDINEA E DUPĂ NUMĂRUL DE MODEL. 8 < 19 <
        28 < 50 < 90. Numărul se citește din titlu, după numele gamei.

     DOAR PENTRU BANG & OLUFSEN, deocamdată (decizia lui Tudor). La B&O numărul
     de model urmează consecvent poziția în gamă, ceea ce nu e adevărat peste tot
     — la alte branduri un număr mai mare nu înseamnă neapărat un produs mai sus
     în gamă, iar o regulă generală ar reordona greșit game pe care nimeni nu s-a
     plâns. Se extinde când verificăm brand cu brand, nu preventiv. */
  var GAMA_VENDOR = "bang & olufsen";
  /* Tipurile care sunt „produsul principal" al unei game de boxe. Restul —
     DAC, Streamer, cabluri, suporturi — coboară. Comparat lowercase. */
  var GAMA_TIPURI_PRINCIPALE = [
    "boxe amplificate",
    "boxe active",
    "boxe pasive",
    "subwoofer",
    "soundbar",
    "casti",
    "căști",
  ];

  /* Edițiile speciale coboară sub modelul standard (2026-08-24, Tudor).
     „Normal ar fi trebuit să o punem împreună cu 50-ul normal și să fie variantă
     de culoare, dar la asta sunt 2 produse separate; așa că fă să apară la search
     50-ul normal, iar Ferrari-ul să fie mult mai jos."

     Sunt 7 astfel de produse la B&O (Beolab 50 Ferrari, Beoplay H95 Ferrari,
     Beosound Explore Ferrari, Beosound Theatre 55 Ferrari + resigilatele lor).
     Nu se ascund — cine caută anume ediția o găsește — dar nu iau locul din față
     al modelului standard, care e ce vrea aproape oricine scrie numele gamei.

     Coboară SUB modelele standard, dar RĂMÂN deasupra accesoriilor: o ediție
     Ferrari de 439.999 lei e tot un produs principal, spre deosebire de un
     transmițător de 1.999. Deci trei trepte: standard, ediție specială, accesoriu. */
  var EDITIE_SPECIALA_RX = /\b(ferrari|mclaren|limited edition|special edition)\b/i;

  /** true dacă titlul marchează o ediție specială vândută ca produs separat. */
  function esteEditieSpeciala(doc) {
    return EDITIE_SPECIALA_RX.test(String((doc && doc.title) || ""));
  }

  /** Numărul de model din titlu („Beolab 28 wall mount" -> 28), sau null. */
  function numarModel(title) {
    var m = String(title || "").match(/\b(\d{1,4})\b/);
    return m ? parseInt(m[1], 10) : null;
  }

  /** true dacă produsul e din categoria principală a gamei, nu un accesoriu. */
  function estePrincipal(doc) {
    var t = String((doc && doc.product_type) || "").toLowerCase();
    for (var i = 0; i < GAMA_TIPURI_PRINCIPALE.length; i++) {
      if (t.indexOf(GAMA_TIPURI_PRINCIPALE[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Reordonează rândurile unei căutări pe gamă: produsele principale înaintea
   * accesoriilor, iar în interiorul fiecărei clase după numărul de model.
   *
   * Se aplică DOAR când toate rândurile sunt de la același vendor și acela e cel
   * configurat — altfel o căutare care amestecă mărci ar fi rearanjată după o
   * regulă care nu i se potrivește. Rândurile fără număr de model își păstrează
   * ordinea relativă (sortare stabilă), după cele numerotate.
   */
  /** Numărul de model CERUT explicit în interogare („beolab 90" -> 90), sau null
   *  când omul a scris doar gama („beolab"). */
  function numarModelCerut(query) {
    var m = String(query || "").trim().match(/(?:^|\s)(\d{1,4})(?:\s|$)/);
    return m ? Number(m[1]) : null;
  }

  function ordoneazaGama(rows, query) {
    if (!esteCautareDeGama(query) || rows.length < 2) return rows;
    for (var i = 0; i < rows.length; i++) {
      var v = String((rows[i].doc && rows[i].doc.vendor) || "").toLowerCase();
      if (v !== GAMA_VENDOR) return rows; // nu e gama pe care o guvernăm
    }
    /* Modelul CERUT, dacă interogarea numește unul.
       Semnalat de Tudor 2026-08-28: „beolab 90" arăta Beolab 50 primul. Cauza —
       regula asta sortează crescător după numărul de model, ceea ce e CORECT
       pentru „beolab" (arată-mi gama, în ordine), dar greșit pentru „beolab 90",
       unde numărul nu e un criteriu de ordonare, ci PRODUSUL CERUT. Tiparul e
       același cu esteCautareDeGama(): un al doilea token numeric o califica drept
       căutare de gamă tocmai fiindcă e un cod de model — deci exact acolo unde
       ordonarea numerică nu se mai aplică. */
    var cerut = numarModelCerut(query);
    return rows
      .map(function (row, idx) {
        /* treapta: 0 = model standard, 1 = ediție specială, 2 = accesoriu.
           Numărul de model departajează DOAR în interiorul aceleiași trepte, ca
           „Beolab 50 Ferrari" să nu se strecoare lângă „Beolab 50" pe seama lui. */
        var treapta = !estePrincipal(row.doc) ? 2 : esteEditieSpeciala(row.doc) ? 1 : 0;
        var nr = numarModel(row.doc && row.doc.title);
        return {
          row: row, idx: idx, treapta: treapta, nr: nr,
          /* Potrivirea EXACTĂ cu modelul cerut trece înaintea oricărei trepte:
             cine scrie „beolab 90" vrea Beolab 90 primul, chiar dacă ar fi ediție
             specială sau accesoriu. Restul gamei rămâne mai jos, în ordinea de
             până acum — util ca să vezi vecinii, dar nu peste ce ai cerut. */
          cerut: cerut != null && nr === cerut ? 0 : 1,
        };
      })
      .sort(function (a, b) {
        if (a.cerut !== b.cerut) return a.cerut - b.cerut;
        if (a.treapta !== b.treapta) return a.treapta - b.treapta;
        if (a.nr != null && b.nr != null && a.nr !== b.nr) return a.nr - b.nr;
        if (a.nr != null && b.nr == null) return -1;
        if (a.nr == null && b.nr != null) return 1;
        return a.idx - b.idx; // stabil: păstrează ordinea venită de la Typesense
      })
      .map(function (x) {
        return x.row;
      });
  }


  /* ── Care montare reprezintă familia, și cum se numește (2026-08-24, Tudor) ───
     „pune 28 floor stand în loc de wall mount și eventual la astea pune în ()
      (wall mount), (floor stand); la beolab 18 la fel în (pereche)"

     DOUĂ LUCRURI, amândouă despre rândul colapsat:

     1. CARE variantă e liderul. Regula implicită — cea mai ieftină cumpărabilă —
        e corectă pentru preț („de la X lei" trebuie să fie plătibil), dar alege
        greșit ce POZĂ și ce nume vede omul: la Beolab 28, `wall mount` costă
        98.859 și `floor stand` 107.599, deci cea mai ieftină e montarea pe perete,
        iar Tudor vrea stativul de podea. Preferința e o listă declarată, nu o
        deducere din preț: stativul de podea arată produsul întreg, în picioare,
        și e imaginea pe care o recunoaște cineva care caută boxa.
        Prețul afișat rămâne CEL MAI MIC — nu promitem un preț mai mare decât
        cel real doar fiindcă am ales altă poză.

     2. CUM se numește rândul. Titlul păstrat e al liderului, dar coada de
        configurație a fost tăiată de familyKey, deci „Beolab 28 floor stand"
        devenea „Beolab 28" — se pierdea informația că e stativul de podea.
        Acum coada revine ca etichetă în paranteză: „Beolab 28 (floor stand)",
        „Beolab 18 (pereche)". Se aplică DOAR când familia are mai multe montări
        sau când coada chiar a fost tăiată — un produs fără variante își păstrează
        titlul exact cum e în catalog. */
  var MONTARE_PREFERATA = [
    /floor stand/i,
    /stativ de podea/i,
    /stativ de masă|stativ de masa/i,
    /table stand/i,
    /pereche/i,
  ];

  /** Poziția în ordinea de preferință a montării; mai mic = preferat. */
  function rangMontare(title) {
    var t = String(title || "");
    for (var i = 0; i < MONTARE_PREFERATA.length; i++) {
      if (MONTARE_PREFERATA[i].test(t)) return i;
    }
    return MONTARE_PREFERATA.length; // nemenționat: după cele preferate
  }

  /** Coada de configurație tăiată de familyKey, ca etichetă scurtă („floor stand",
   *  „pereche"), sau "" dacă titlul nu avea niciuna. */
  function etichetaMontare(title) {
    var t = String(title || "").trim();
    var en = t.match(CONFIG_TAIL_EN_RX);
    if (en) return en[1].toLowerCase();
    var ro = t.match(CONFIG_RX);
    if (ro) {
      return ro[0]
        .replace(/^[\s–—,]+/, "")
        .replace(/^cu\s+/i, "")
        .trim()
        .toLowerCase();
    }
    return "";
  }

  /** Rândul, cu coada de configurație mutată în paranteză la finalul titlului:
   *  „Beolab 28 floor stand" -> „Beolab 28 (floor stand)",
   *  „Beolab 18 – pereche"   -> „Beolab 18 (pereche)".
   *  Întoarce rândul neschimbat dacă titlul nu are coadă de configurație.
   *  Copie, nu mutație: `doc` vine din index și e citit și de alte căi. */
  function cuEtichetaMontare(row) {
    var titlu = String((row.doc && row.doc.title) || "");
    var eticheta = etichetaMontare(titlu);
    if (!eticheta) return row;
    var baza = titlu
      .replace(CONFIG_TAIL_EN_RX, "")
      .replace(CONFIG_RX, "")
      .replace(/[\s,–—-]+$/, "")
      .trim();
    return Object.assign({}, row, {
      doc: Object.assign({}, row.doc, { title: baza + " (" + eticheta + ")" }),
    });
  }

  function collapseFamilies(rows) {
    var byKey = {};
    var order = [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var key = familyKey(row.doc && row.doc.title, row.doc);
      if (!key) {
        order.push(row);
        continue;
      }
      if (!byKey[key]) {
        byKey[key] = { lead: row, members: [row] };
        order.push(key);
      } else {
        byKey[key].members.push(row);
      }
    }
    return order.map(function (entry) {
      if (typeof entry !== "string") return entry; // rând fără cheie, netransformat
      var fam = byKey[entry];
      if (fam.members.length === 1) {
        /* Un singur membru: nu e nimic de colapsat, dar coada de configurație tot
           se normalizează în paranteză, ca „Beolab 18 – pereche" să arate la fel
           ca „Beolab 28 (floor stand)". */
        return cuEtichetaMontare(fam.lead);
      }
      var inStock = fam.members.filter(function (r) {
        return r.inStock !== false;
      });
      var pool = inStock.length ? inStock : fam.members;
      var prices = pool.map(function (r) {
        return Number(r.price) || 0;
      });
      var min = Math.min.apply(null, prices);
      var max = Math.max.apply(null, prices);
      /* Liderul se alege pe PREFERINȚA DE MONTARE, nu pe preț: prețul afișat
         rămâne oricum cel mai mic (`min` de mai jos), dar poza și numele trebuie
         să fie ale montării care reprezintă produsul. Vezi MONTARE_PREFERATA. */
      /* Preferința de montare se aplică între variantele apropiate de prețul minim,
         nu doar la cele EXACT pe minim.

         Corectat 2026-08-26, după ce Tudor a semnalat că „beolab" arată tot
         `wall mount`. Comentariul de aici susținea că „la Beolab 28 ambele montări
         pornesc de la același minim" — FALS, măsurat pe indexul live: floor stand
         pornește de la 89.640, wall mount de la 89.199. O diferență de 441 lei
         (0,5%) era de ajuns ca filtrul strict pe minim să lase preferința fără
         niciun candidat de ales, deci montarea preferată nu se aplica NICIODATĂ
         când cea nepreferată e fie și cu un leu mai ieftină.

         De ce o toleranță e corectă aici, nu o concesie: rândul afișează „DE LA
         89.199 lei" (`hasRange` e adevărat de îndată ce montările diferă), adică
         un preț de intrare în familie, nu prețul lucrului din poză. Garanția care
         contează — să nu promitem un preț mai mic decât cel real — se păstrează
         intactă, fiindcă `price` rămâne `min`.

         PRAGUL: 5% din minim. Acoperă diferențele de montare din catalog (0,5% la
         Beolab 28) fără să atingă cazurile în care variantele sunt lucruri diferite
         ca preț — la Beolab 8, single 15.929 vs pereche 30.259 (90%) rămâne pe
         varianta ieftină, exact ca înainte. */
      var PRAG_MONTARE = 1.05;
      var aproapeDeMinim = pool.filter(function (r) {
        return (Number(r.price) || 0) <= min * PRAG_MONTARE;
      });
      var lead = (aproapeDeMinim.length ? aproapeDeMinim : pool)
        .slice()
        .sort(function (a, b) {
          var d = rangMontare(a.doc && a.doc.title) - rangMontare(b.doc && b.doc.title);
          /* La rang egal decide prețul — altfel ordinea ar depinde de ordinea în
             care Typesense a întors documentele, adică ar fi nedeterministă. */
          return d !== 0 ? d : (Number(a.price) || 0) - (Number(b.price) || 0);
        })[0] || pool[0];
      var cu = cuEtichetaMontare(lead);
      /* Membrii rămân atașați rândului colapsat, ca desfaFamiliaCeruta() să-i poată
         readuce atunci când omul chiar a cerut modelul ăsta — vezi comentariul de
         acolo. Liderul primul (montarea preferată, aleasă mai sus), restul după
         preț: ordinea în care se citește o listă de configurații. */
      var restul = fam.members
        .filter(function (r) { return r !== lead; })
        .sort(function (a, b) { return (Number(a.price) || 0) - (Number(b.price) || 0); })
        .map(cuEtichetaMontare);
      return {
        doc: cu.doc,
        price: min,
        // „de la" dacă montările diferă între ele SAU dacă liderul avea deja gamă.
        hasRange: max > min || lead.hasRange === true,
        inStock: inStock.length > 0,
        tokensMatched: lead.tokensMatched,
        /* `campuri` TREBUIE dus mai departe. Fără el, orice familie cu mai mulți
           membri ieșea din colapsare fără semnalul pe care se bazează
           doarInDescriere(), deci retrogradarea „menționează X" nu se mai
           declanșa NICIODATĂ pentru ea — tăcut, fiindcă `undefined` nu e o eroare,
           doar „nu știm".
           Măsurat 2026-08-29 pe „atlp60": doza Audio-Technica ATN3600LC (care
           potrivește doar în descrierea ei, ca accesoriu compatibil) apărea în
           DOUĂ rânduri, se colapsa, pierdea `campuri` și ajungea PRIMA — înaintea
           pick-up-urilor AT-LP60 pe care omul le caută. */
        campuri: lead.campuri,
        familie: entry,
        membri: [cu].concat(restul),
      };
    });
  }

  /* ── Modelul cerut anume își arată configurațiile (2026-08-28, Tudor) ────────
     „de ce când scriu beolab 8 sau beolab 28 nu îmi arată toate variantele și îmi
     arată numai una?"

     Același tipar ca la „beolab 90" cu o zi înainte: o regulă corectă pentru
     căutarea pe GAMĂ se aplica și când omul numește un MODEL. Colapsarea pe
     familie există fiindcă la „beolab" cinci rânduri de Beolab 8 (câte o montare)
     acopereau restul gamei. Dar cine scrie „beolab 8" a ales deja modelul — ce-i
     rămâne de ales sunt exact configurațiile pe care colapsarea le ascunde
     (măsurat pe indexul live: Beolab 8 are 5 produse, Beolab 28 are 2).

     Deci: colapsat la căutarea pe gamă, desfăcut la căutarea pe model. Se aplică în
     renderRows, lângă celelalte reguli care depind de interogare — toProductRows
     nu o primește, și are 13 locuri de apel. */

  /** true dacă interogarea numește CHIAR modelul familiei ăsteia („beolab 8" →
   *  familia „bang & olufsen beolab 8"), nu doar gama („beolab"). */
  function familiaEsteCeruta(key, query) {
    var cerut = numarModelCerut(query);
    if (cerut == null || !key) return false;
    if (numarModel(key) !== cerut) return false;
    var k = String(key).toLowerCase();
    var tokens = String(query || "").toLowerCase().split(/\s+/);
    for (var i = 0; i < tokens.length; i++) {
      /* Se cer doar cuvintele întregi (≥3 litere). „b&o beolab 8" ajunge aici cu tot
         cu prescurtarea de brand, care nu apare în titlul canonic („Bang & Olufsen"),
         iar dacă am cere-o am rata familia degeaba. */
      if (!/^[a-zăâîșşțţ]{3,}$/i.test(tokens[i])) continue;
      if (k.indexOf(tokens[i]) === -1) return false;
    }
    return true;
  }

  /* ── Colapsarea nu supraviețuia lipirii a două liste (2026-08-29) ───────────
     Găsit de auditul de prezentare pe „ambeo": dropdown-ul arăta „AMBEO Soundbar
     Plus (wall mount)" ȘI „AMBEO Soundbar Plus", plus aceeași pereche pentru Max —
     adică 4 din 6 rânduri erau două modele. Exact inundarea pe care colapsarea
     există s-o prevină.

     CAUZA: collapseFamilies() se aplică în toProductRows(), adică pe FIECARE
     răspuns în parte. Calea „in-stock + backfill out-of-stock" lipește apoi două
     liste deja colapsate, iar o familie prezentă în amândouă iese de două ori.
     Nicio funcție nu se uita vreodată la lista lipită — până aici.

     De ce nu se poate reface pur și simplu collapseFamilies pe lista lipită:
     titlurile au deja coada de montare mutată în paranteză de cuEtichetaMontare(),
     formă pe care familyKey() nu o mai recunoaște. Se scoate paranteza înainte de
     cheie — la fel ca în scripts/typesense/render-rows.mjs. */
  function cheieFamilie(row) {
    if (row && row.familie) return row.familie;
    var t = String((row && row.doc && row.doc.title) || "").replace(/\s*\([^()]*\)\s*$/, "");
    return familyKey(t, row && row.doc);
  }

  /** Un rând per familie pe lista LIPITĂ, păstrând ordinea și reunind membrii.
   *  Primul rând câștigă: vine din pasul in-stock, deci e cel cumpărabil. */
  function unesteFamilii(rows) {
    var byKey = {}, out = [];
    for (var i = 0; i < (rows || []).length; i++) {
      var row = rows[i];
      var key = row ? cheieFamilie(row) : "";
      if (!key) { out.push(row); continue; }
      if (!byKey[key]) { byKey[key] = row; out.push(row); continue; }
      /* Familia s-a mai văzut: rândul se pierde, dar membrii lui NU — altfel
         desfaFamiliaCeruta() ar arăta mai puține configurații decât există. */
      var pastrat = byKey[key];
      var noi = row.membri || [row];
      var vechi = pastrat.membri || [pastrat];
      var vazute = {}, uniti = [];
      vechi.concat(noi).forEach(function (mr) {
        var id = mr && mr.doc && mr.doc.product_id;
        var k = id != null ? String(id) : (mr && mr.doc && mr.doc.title) || "";
        if (vazute[k]) return;
        vazute[k] = 1;
        uniti.push(mr);
      });
      if (uniti.length > 1) {
        pastrat.membri = uniti;
        pastrat.familie = key;
      }
    }
    return out;
  }

  /** Înlocuiește rândul colapsat al modelului cerut cu configurațiile lui.
   *  Celelalte familii rămân colapsate: „beolab 8" nu desface și Beolab 28. */
  function desfaFamiliaCeruta(rows, query) {
    if (numarModelCerut(query) == null) return rows || [];
    var out = [];
    for (var i = 0; i < (rows || []).length; i++) {
      var r = rows[i];
      if (r && r.membri && r.membri.length > 1 && familiaEsteCeruta(r.familie, query)) {
        out = out.concat(r.membri);
      } else {
        out.push(r);
      }
    }
    return out;
  }

  function toProductRows(data) {
    var groups = data.grouped_hits;
    if (!groups) {
      // Ungrouped response (shouldn't happen, but never render nothing because of it).
      return collapseFamilies(
        (data.hits || []).filter(function (h) { return onCurrentMarket(h.document); }).map(function (h) {
          return { doc: h.document, price: h.document.price, hasRange: false };
        })
      );
    }
    /* collapseFamilies la IEȘIRE, nu la fiecare apelant: toProductRows are șase
       locuri de apel (căutare text, brand consumer + installation, backfill de
       stoc, retry) și un al șaptelea adăugat mâine ar fi uitat tăcut. */
    return collapseFamilies(groups.map(function (group) {
      var docs = (group.hits || []).map(function (h) { return h.document; }).filter(onCurrentMarket);
      if (!docs.length) return null;
      var inStock = docs.filter(function (d) { return d.in_stock; });
      var pool = inStock.length ? inStock : docs;
      var prices = pool.map(function (d) { return Number(d.price) || 0; });
      var min = Math.min.apply(null, prices);
      var max = Math.max.apply(null, prices);
      // Show the variant that owns the displayed price, so image/handle/stock all agree
      // with the number next to them.
      var lead = pool.filter(function (d) { return (Number(d.price) || 0) === min; })[0] || docs[0];
      /* tokensMatched: how many of the query's tokens Typesense actually matched in this
         document, read from the group's best hit. Carried through so the backfill can
         compare rows against each other — see bestTokensMatched()/renderWithStockBackfill().
         Taken from hits[0] because Typesense orders a group's hits by relevance, so the
         first is the group's best. Undefined if the field is ever absent, which the
         consumers treat as "no opinion" rather than zero.

         Reportat aici pe 2026-08-20: mecanismul fusese adaugat pe 4 august (d3ac2de7) in
         am26-typesense-search si am26-pdp-pachete, dar acest draft a fost ramificat pe 12
         august dintr-o baza fara el, deci s-a pierdut TACUT — verificat atunci direct de pe
         site cu curl pe asset-ul din pagina: zero aparitii in productie. */
      var bestHit = (group.hits || [])[0] || {};
      var matchInfo = bestHit.text_match_info || {};
      return {
        doc: lead,
        price: min,
        hasRange: max > min,
        inStock: inStock.length > 0,
        tokensMatched: matchInfo.tokens_matched,
        /* tokensDropped: câți tokeni a ARUNCAT Typesense ca să poată răspunde
           (drop_tokens). Un rând cu tokensDropped > 0 e răspuns la o întrebare
           mai scurtă decât cea pusă — vezi esteRezultatSlab(). */
        tokensDropped: matchInfo.num_tokens_dropped,
        /* Câmpurile în care s-a potrivit efectiv interogarea. Gol = potrivirea a
           venit din `description` (singurul câmp căutat pe care NU-l cerem în
           highlight_fields) — vezi doarInDescriere(). */
        campuri: (bestHit.highlights || []).map(function (h) { return h.field; }),
      };
    }).filter(Boolean));
  }

  /**
   * „Rezultat slab" = Typesense a găsit ceva, dar NUMAI aruncând cuvinte din
   * interogare. Măsurat („klipsh boxe", 2026-08-26): 594 de potriviri, toate cu
   * num_tokens_dropped ≥ 1 — cabluri SCP și boxe KEF pentru un om care a scris
   * greșit „Klipsch". Înainte, un astfel de răspuns oprea pipeline-ul (found > 0
   * arăta a succes), deci retry-ul cu toleranță la typo nu mai rula niciodată.
   * Acum un rezultat integral-slab lasă escaladarea să continue; rândurile slabe
   * se păstrează și se afișează DOAR dacă nici retry-ul nu găsește ceva întreg.
   * Doar pentru interogări multi-token: pe un singur token, num_tokens_dropped
   * are artefacte pe calea infix și nu există „întrebare mai scurtă".
   *
   * Excepție: codul de model scris cu spațiu (2026-09-25). „sb 1000" pe
   * „SVS SB-1000 PRO": indexul ține „SB-1000" ca UN token („sb1000"), deci
   * Typesense potrivește „sb" ca prefix și raportează „1000" drept aruncat —
   * deși titlul conține exact ce s-a cerut. Marcat slab, rezultatul corect era
   * înlocuit de sonda de vendor („sb" → PSB Speakers). Un rând al cărui titlu,
   * fără spații și semne, conține interogarea la fel compactată NU e slab.
   * Doar pentru interogări cu cifră — adică coduri de model —, ca typo-urile
   * de tip „klipsh boxe" să-și păstreze escaladarea neschimbată.
   */
  function compactModel(str) {
    return stripDiacritics(String(str || "")).toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  function esteRezultatSlab(sentQuery, rows) {
    if (String(sentQuery || "").trim().split(/\s+/).length < 2) return false;
    if (!rows || !rows.length) return false;
    var cod = /\d/.test(sentQuery) ? compactModel(sentQuery) : "";
    if (cod.length < 4) cod = "";
    for (var i = 0; i < rows.length; i++) {
      if (!(rows[i].tokensDropped > 0)) return false;
      if (cod && compactModel(rows[i].doc && rows[i].doc.title).indexOf(cod) !== -1) return false;
    }
    return true;
  }

  /* The highest tokens_matched across a set of rows, or 0 if none report one. */
  function bestTokensMatched(rows) {
    var best = 0;
    for (var i = 0; i < rows.length; i++) {
      var n = rows[i] && rows[i].tokensMatched;
      if (typeof n === "number" && n > best) best = n;
    }
    return best;
  }

  /* ── Zero-result rescue: WhatsApp + known-category shortcuts ──────────────────
     Tudor, 2026-08-12. A zero-result search was a dead end: the dropdown said
     "Niciun rezultat" and the customer left. It is also the highest-intent moment
     on the site — someone typed a specific product name — so it earns a way to
     reach a human rather than a full stop.

     MEASURED, not assumed. All 478 distinct zero-result queries were read from
     product_nohits_queries on 2026-08-12. What they actually are:
       - model codes and brand typos that ALREADY work today (lp60, ls60, ubr62,
         db53, kc62 all return hits with the live params; klipsh returns 0
         on the first pass and is rescued by the vendor-typo retry below). The log
         is a RUNNING TOTAL since 2026-08-03, so it counts queries that were broken
         then and have since been fixed — it does not describe the index today.

         CORECTAT 2026-08-29: „hd599" era trecut aici ca rezolvat fiindcă întoarce
         UN rezultat — dar acela e cablul HD 5X8/5X9, nu casca HD 599, care există
         și e pe stoc. Forma lipită („hd599") nu se potrivește pe niciun câmp al
         produsului; se potrivește pe DESCRIEREA unui accesoriu compatibil. „Are
         rezultate" nu înseamnă „are rezultatul bun" — vezi nota despre lipsa unui
         câmp de model insensibil la spații, în raportul din TASKS.md.
       - brands we may not sell on .ro (cambridge 18, eversolo 6, ruark 3): zero is
         the CORRECT answer there, see brand-hidden-on-market.liquid.
       - the word "resigilate" itself (5×), which is handled separately below —
         prin CATEGORY_SHORTCUTS: zero-result-ul e INTENȚIONAT (resigilatele sunt
         ținute în afara indexului, decizia lui Tudor din 03.08), iar omul primește
         un LINK către colecție, nu produsele în dropdown.
         Încercat și respins pe 2026-08-29: o rută CATEGORY_INTENT către colecție.
         Funcționa, dar `runCollectionSearch` LISTEAZĂ produsele colecției în
         dropdown — adică exact ce nu se vrea. Linkul e răspunsul, nu lista.

     Tudor's decision, recorded because it is a real trade-off: the WhatsApp button
     shows for EVERY zero-result query, including the barred brands. Those customers
     still have buying intent and can be offered an alternative — the operator will
     see the searched term in the prefilled message and can answer accordingly.

     The prefilled message says «produsul "X"» rather than «brandul "X"», because
     most of these queries are model codes (ls50, ubr62), not brand names — Tudor's
     correction, 2026-08-12. */
  /* ── Textele din dropdown, per piață (2026-08-26) ─────────────────────────────
   *
   * Până la deschiderea porții .bg nu era nevoie de ele: scriptul rula DOAR pe .ro,
   * deci un șir românesc hardcodat era corect prin construcție. Odată ce .bg
   * primește aceeași căutare, fiecare dintre aceste șiruri devine text românesc
   * afișat unui client bulgar — adică exact tiparul „fallback tăcut la limba
   * primară" din regulile proiectului, doar că în JS în loc de Shopify.
   *
   * Traducerile sunt scrise ca text de interfață bulgăresc obișnuit; termenii de
   * produs (mărci, modele) nu se traduc niciodată. Dacă apare o a treia piață,
   * lipsa unei chei cade pe română — vizibil, dar niciodată gol. */
  var TEXTE = {
    ro: {
      rezultatePentru: function (q) { return "Rezultate pentru „" + q + "”"; },
      niciunRezultat: function (q) { return "Niciun rezultat pentru „" + q + "”"; },
      spuneNe: "Spune-ne ce cauți și îți recomandăm noi.",
      scrieNe: "Scrie-ne pe WhatsApp",
      stocEpuizat: "Stoc epuizat",
      dela: "de la ",
      veziToateBrand: function (n, v) {
        return n ? "Vezi toate cele " + n + " produse " + v : "Vezi toate produsele " + v;
      },
      veziToateCategorie: function (n, t) {
        return n ? "Vezi toate cele " + n + " produse din " + t : "Vezi toate produsele din " + t;
      },
      waMesaj: function (q) {
        return 'Bună! Am căutat produsul "' + q + '" pe audiomonkey.ro și nu l-am găsit. Mă puteți ajuta?';
      },
    },
    bg: {
      rezultatePentru: function (q) { return "Резултати за „" + q + "”"; },
      niciunRezultat: function (q) { return "Няма резултати за „" + q + "”"; },
      spuneNe: "Кажете ни какво търсите и ще ви препоръчаме.",
      scrieNe: "Пишете ни в WhatsApp",
      stocEpuizat: "Изчерпан",
      dela: "от ",
      veziToateBrand: function (n, v) {
        return n ? "Вижте всички " + n + " продукта " + v : "Вижте всички продукти " + v;
      },
      veziToateCategorie: function (n, t) {
        return n ? "Вижте всички " + n + " продукта от " + t : "Вижте всички продукти от " + t;
      },
      waMesaj: function (q) {
        return 'Здравейте! Търсих продукта "' + q + '" в audiomonkey.bg и не го намерих. Може ли да ми помогнете?';
      },
    },
  };

  function t() {
    return TEXTE[currentMarket()] || TEXTE.ro;
  }

  var WHATSAPP_NUMBER = "40728934934"; // wa.me format: digits only, no + or spaces

  /* Generic catalogue words that name a SECTION we do have rather than a product.
     "resigilate" is the measured case: 5 zero-result searches, while the shop does
     carry 174 open-box products — they are deliberately EXCLUDED from the search
     index (EXCLUDED_TAG "is_openbox" in worker/src/typesense-sync.ts, so an open-box
     twin cannot crowd out the new product it is named after). So the fix is not to
     index them; it is to send the customer to the collection that already exists. */
  var CATEGORY_SHORTCUTS = [
    {
      /* Prefix (\w*), nu listă închisă: corpus-ul real din product_nohits_queries
         conține și trunchierile „resigi" (2026-08-26), „resig" — omul se oprește
         din tastat oriunde. \w nu prinde diacritice, dar niciuna din formele
         acestei familii nu are. */
      test: /^(resig\w*|desig\w*|open ?box)$/i,
      label: "Vezi produsele resigilate disponibile",
      url: "/collections/resigilate-disponibile",
    },
  ];

  function categoryShortcutFor(query) {
    var q = String(query || "").trim();
    for (var i = 0; i < CATEGORY_SHORTCUTS.length; i++) {
      if (CATEGORY_SHORTCUTS[i].test.test(q)) return CATEGORY_SHORTCUTS[i];
    }
    return null;
  }

  function whatsappHref(query) {
    var text = t().waMesaj(String(query || "").trim());
    return "https://wa.me/" + WHATSAPP_NUMBER + "?text=" + encodeURIComponent(text);
  }

  /* Handle-ul de colecție pentru un vendor. Colecțiile de brand din magazin
     folosesc handle-ul derivat din numele vendorului: „Bang & Olufsen" ->
     bang-olufsen, „Meze Audio" -> meze-audio, „Pro-Ject" -> pro-ject.
     Trei excepții reale, verificate în Admin API pe 2026-08-20 — handle-ul din
     Shopify NU e cel derivat:
       Pro-Ject -> „project" (creată fără cratimă în 2023)
       WIREWORLD -> „wirewold" (greșeală de tastare, indexată, nu se corectează)
       Atacama -> „atacama-audio"
     Dacă un brand nu are colecție, `brandCollectionHandle` întoarce handle-ul
     derivat oricum — linkul ar da 404, deci apelantul verifică întâi lista. */
  var VENDOR_COLLECTION_OVERRIDES = {
    "Pro-Ject": "project",
    "WIREWORLD": "wirewold",
    "Atacama": "atacama-audio",
    /* Polk Audio N-ARE colecţie de brand în magazin — handle-ul derivat
       `polk-audio` dă 404 (verificat 2026-09-04). Singura colecţie a mărcii e
       `boxe-polk-audio`, iar toate cele 17 produse ale ei sunt boxe, deci e şi
       destinaţia corectă pentru „polk" simplu. Fără linia asta, aliasul „polk"
       adăugat în aceeaşi zi ar fi trimis oamenii într-un 404. */
    "Polk Audio": "boxe-polk-audio",
    /* Kanto Mounts (vendor din 01.09, 6 suporturi TV activate pe 08.10) n-are colecţie de brand;
       toate produsele lui sunt suporturi TV, deci raftul marcă × categorie e şi ţinta brandului.
       Fără linia asta, Enter pe „kanto mounts" ar duce la /collections/kanto-mounts → 404. */
    "Kanto Mounts": "suporturi-tv-kanto-mounts",
  };
  /* COLECȚII MARCĂ × CATEGORIE (T2-005, 2026-09-04).

     Problema pe care o rezolvă: `findVendorScope` prindea deja „boxe kef" și
     scotea perechea (vendor KEF, rest „boxe"), dar Enter ducea la colecția de
     BRAND — /collections/kef, toate cele 115 produse KEF, inclusiv căști și
     accesorii. Din 2026-09-04 există colecții reale marcă × categorie, deci
     „boxe kef" are un răspuns exact: /collections/boxe-kef, 95 de produse.

     Cheia e `vendor canonic + "|" + handle de categorie` — adică EXACT ce
     întorc deja `findVendorScope()` și `detectCategoryIntent()`. Nu se
     reinterpretează textul: dacă cele două funcții nu cad de acord, nu există
     intrare aici și se cade pe comportamentul de azi.

     De ce o listă explicită și nu un handle derivat mecanic („boxe-" + brand):
     un handle inexistent produce 404, iar 404 e mai rău decât o colecție de
     brand corectă dar mai largă. Aceeași regulă ca la
     VENDOR_COLLECTION_OVERRIDES. Fiecare intrare de aici e verificată prin
     Admin API înainte de a fi adăugată.

     Se extinde pe măsură ce se creează restul colecțiilor pilot (celelalte 18
     așteaptă o decizie de produs despre cum se definește „electronice"). */
  /* ── Reevaluare 10.09.2026 (pasul B) ────────────────────────────────────
     32 de rutari din loturile-pilot (04.09) trimiteau spre colectia LARGA a
     brandului, fiindca la acea data nu exista alta. Lotul din 05.09 a creat
     rafturi exacte, deci „subwoofere kef" nu mai trebuie sa duca in
     `boxe-kef` (98 de produse, majoritatea boxe) cand exista `subwoofere-kef`
     (13). Populatiile ambelor capete au fost citite din Admin API inainte de
     schimbare; fiecare inlocuire ingusteaza destinatia, niciuna nu o largeste.

     Velodyne: cele doua colectii erau DUPLICATE (aceleasi 19 produse, reguli
     echivalente). Pe 10.09 Tudor a decis sa ramana `subwoofere-velodyne-acoustics`;
     `subwoofere-velodyne` a fost STEARSA, dupa ce textul ei — mai bun, fiindca
     numeste modelele MiniVee X / SPL-X 12 / Deep Blue 15 — a fost mutat pe cea
     pastrata, RO si BG. Backup:
     assets/backup-metafielduri/velodyne-consolidare-2026-09-10/inainte.json */
  var BRAND_CATEGORY_COLLECTIONS = {
    "KEF|boxe-pasive": "boxe-pasive-kef",
    "KEF|boxe-amplificate": "boxe-active-kef",
    "KEF|subwoofere": "subwoofere-kef",
    "SENNHEISER|casti": "casti-sennheiser",
    "SENNHEISER|amplificatoare-pentru-casti": "casti-sennheiser",
    /* Extindere 2026-09-04: încă 8 colecţii. Vendorii sunt scrişi EXACT cum îi
       are magazinul (`iFi Audio`, `Meze Audio` — cu spaţiu şi majuscule), fiindcă
       cheia se compune din ce întorc detectBrandIntent()/findVendorScope(). */
    "Elac|boxe-pasive": "boxe-pasive-elac",
    "Elac|boxe-amplificate": "boxe-active-elac",
    "Elac|subwoofere": "subwoofere-elac",
    "Klipsch|boxe-pasive": "boxe-pasive-klipsch",
    "Klipsch|boxe-amplificate": "boxe-active-klipsch",
    "Klipsch|subwoofere": "subwoofere-klipsch",
    "Focal|boxe-pasive": "boxe-pasive-focal",
    "Focal|boxe-amplificate": "boxe-focal",
    "Focal|subwoofere": "subwoofere-focal",
    "Sonos|boxe-pasive": "boxe-sonos",
    "Sonos|boxe-amplificate": "boxe-active-sonos",
    "Sonos|subwoofere": "boxe-sonos",
    "Beyerdynamic|casti": "casti-beyerdynamic",
    "Beyerdynamic|amplificatoare-pentru-casti": "casti-beyerdynamic",
    "Meze Audio|casti": "casti-meze-audio",
    "Meze Audio|amplificatoare-pentru-casti": "casti-meze-audio",
    "Focal|casti": "casti-focal",
    "Focal|amplificatoare-pentru-casti": "casti-focal",
    /* iFi Audio face amplificatoare de căşti şi DAC-uri, nu căşti: colecţia are
       21 de amplificatoare şi O pereche de căşti. De aceea titlul e
       „Amplificatoare de căşti iFi Audio", iar „căşti ifi" tot aici trebuie să
       ducă — omul care caută aşa vrea exact raftul ăsta, chiar dacă îl numeşte
       greşit. Handle-ul rămâne `casti-ifi-audio`. */
    "iFi Audio|casti": "casti-ifi-audio",
    "iFi Audio|amplificatoare-pentru-casti": "amplificatoare-pentru-casti-ifi-audio",
    /* Lotul 2 (2026-09-04). `subwoofere-velodyne-acoustics` NU e „boxe-velodyne": toate
       cele 19 produse ale mărcii din grupul `boxe` sunt subwoofere, deci un
       titlu cu „boxe" ar promite altceva. Interogarea „boxe velodyne" duce tot
       acolo — omul caută aşa, iar raftul e cel corect. */
    "Bowers & Wilkins|boxe-pasive": "boxe-pasive-bowers-wilkins",
    "Bowers & Wilkins|boxe-amplificate": "boxe-bowers-wilkins",
    "Bowers & Wilkins|subwoofere": "subwoofere-bowers-wilkins",
    "Bowers & Wilkins|casti": "casti-bowers-wilkins",
    "Bowers & Wilkins|amplificatoare-pentru-casti": "casti-bowers-wilkins",
    "Dali|boxe-pasive": "boxe-pasive-dali",
    "Dali|boxe-amplificate": "boxe-dali",
    "Dali|subwoofere": "boxe-dali",
    "SVS|boxe-pasive": "boxe-pasive-svs",
    "SVS|boxe-amplificate": "boxe-svs",
    "SVS|subwoofere": "subwoofere-svs",
    "Yamaha|boxe-pasive": "boxe-pasive-yamaha",
    "Yamaha|boxe-amplificate": "boxe-yamaha",
    "Yamaha|subwoofere": "boxe-yamaha",
    "Q Acoustics|boxe-pasive": "boxe-pasive-q-acoustics",
    "Q Acoustics|boxe-amplificate": "boxe-q-acoustics",
    "Q Acoustics|subwoofere": "boxe-q-acoustics",
    "PSB Speakers|boxe-pasive": "boxe-pasive-psb-speakers",
    "PSB Speakers|boxe-amplificate": "boxe-psb",
    "PSB Speakers|subwoofere": "boxe-psb",
    "Polk Audio|boxe-pasive": "boxe-pasive-polk-audio",
    "Polk Audio|boxe-amplificate": "boxe-polk-audio",
    "Polk Audio|subwoofere": "boxe-polk-audio",
    "Velodyne Acoustics|boxe-pasive": "subwoofere-velodyne-acoustics",
    "Velodyne Acoustics|boxe-amplificate": "subwoofere-velodyne-acoustics",
    "Velodyne Acoustics|subwoofere": "subwoofere-velodyne-acoustics",
    "Audio-Technica|casti": "casti-audio-technica",
    "Audio-Technica|amplificatoare-pentru-casti": "casti-audio-technica",
    "EPOS|casti": "casti-epos",
    "EPOS|amplificatoare-pentru-casti": "casti-epos",
    "HiFiMAN|casti": "casti-hifiman",
    "HiFiMAN|amplificatoare-pentru-casti": "casti-hifiman",
    /* Lotul 3 — „electronice" (2026-09-04). Grupul e uniunea a 7 tipuri, deci
       oricare dintre ele trimite la acelaşi raft de aparate al mărcii. */
    "NAD|amplificatoare-integrate": "amplificatoare-nad",
    "NAD|amplificatoare-de-putere": "amplificatoare-de-putere-nad",
    "NAD|preamplificatoare": "electronice-nad",
    "NAD|receivere-a-v": "electronice-nad",
    "NAD|streamer-network-player": "electronice-nad",
    "NAD|dac": "electronice-nad",
    "NAD|cd-playere": "electronice-nad",
    "Marantz|amplificatoare-integrate": "amplificatoare-marantz",
    "Marantz|receivere-a-v": "receivere-marantz",
    "Marantz|preamplificatoare": "electronice-marantz",
    "Marantz|cd-playere": "cd-players-recorders-marantz",
    "Marantz|amplificatoare-de-putere": "electronice-marantz",
    "Denon|receivere-a-v": "receivere-denon",
    "Denon|amplificatoare-integrate": "amplificatoare-denon",
    "Denon|cd-playere": "electronice-denon",
    "Denon|streamer-network-player": "electronice-denon",
    "Pro-Ject|amplificatoare-integrate": "amplificatoare-pro-ject",
    "Pro-Ject|dac": "dac-pro-ject",
    "Pro-Ject|cd-playere": "cd-players-recorders-pro-ject",
    "Pro-Ject|streamer-network-player": "electronice-pro-ject",
    "Pro-Ject|amplificatoare-de-putere": "electronice-pro-ject",
    "Pro-Ject|preamplificatoare": "electronice-pro-ject",
    "iFi Audio|dac": "dac-ifi-audio",
    "iFi Audio|streamer-network-player": "electronice-ifi-audio",
    "iFi Audio|preamplificatoare": "electronice-ifi-audio",
    "WiiM|streamer-network-player": "electronice-wiim",

    /* GP — singura pereche marca x categorie care a trecut pragul de 5 produse
       la masurarea din 10.09 (117 produse: 96 obisnuite + 21 reincarcabile).
       Colectia `baterii-gp` a fost creata in aceeasi zi, cu filtrul „Tip baterie"
       (Reincarcabila / Obisnuita) cerut de Tudor. Restul candidatilor masurati
       (Primare 4, iFi Audio 4, Sonos 4, Volumio 3) au ramas sub prag: cifrele
       initiale de 5-6 veneau din feed-ul public, care include resigilatele pe
       care colectiile le exclud prin regula. */
    "GP|baterii-si-acumulatori": "baterii-gp",

    /* „boxe <brand>" = colecţia de GRUP a mărcii (pasive + active + subwoofere),
       ca să dea acelaşi raft ca meniul „Boxe > Branduri > <marcă>". Decis de
       Tudor pe 10.09, după ce am măsurat că cele două se contraziceau: clic pe
       Klipsch = 81 de produse, căutare „boxe klipsch" = 43. Aceeaşi intenţie,
       două rafturi — exact ce produce „am văzut boxa pe site şi n-o mai găsesc".

       Cererea PRECISĂ nu e atinsă: „boxe pasive klipsch" rămâne pe
       `boxe-pasive-klipsch`, „subwoofere klipsch" pe `subwoofere-klipsch`.
       Fiecare ţintă de mai jos a fost verificată prin Admin API că EXISTĂ şi are
       produse; brandurile fără colecţie de grup (ArtSound, Triangle, Pro-Ject,
       Acoustic Energy…) lipsesc deliberat — se cade pe raftul pe tip, care e
       mai bun decât un 404. `bang-olufsen-boxe` NU e o abatere de convenţie:
       e chiar handle-ul spre care duce meniul. */
    "KEF|boxe-toate": "boxe-kef",   // 98 produse
    "Elac|boxe-toate": "boxe-elac",   // 91 produse
    "Klipsch|boxe-toate": "boxe-klipsch",   // 81 produse
    "Bowers & Wilkins|boxe-toate": "boxe-bowers-wilkins",   // 41 produse
    "Focal|boxe-toate": "boxe-focal",   // 35 produse
    "Dali|boxe-toate": "boxe-dali",   // 34 produse
    "SVS|boxe-toate": "boxe-svs",   // 32 produse
    "Bang & Olufsen|boxe-toate": "bang-olufsen-boxe",   // 27 produse
    "Yamaha|boxe-toate": "boxe-yamaha",   // 23 produse
    "Q Acoustics|boxe-toate": "boxe-q-acoustics",   // 20 produse
    "PSB Speakers|boxe-toate": "boxe-psb",   // 19 produse
    "Polk Audio|boxe-toate": "boxe-polk-audio",   // 17 produse
    "Sonos|boxe-toate": "boxe-sonos",   // 15 produse
    "WiiM|amplificatoare-integrate": "electronice-wiim",

    /* ── Lotul 05.09.2026: cele 135 de colecţii marcă × categorie ──────────
       Colecţiile fuseseră create şi publicate pe 2026-09-05, dar NIMIC nu
       trimitea spre ele: harta se oprise la loturile-pilot din 04.09, deci
       „căşti b&o", „televizoare sony" sau „cabluri wireworld" cădeau pe
       colecţia largă de brand sau pe /search. Semnalat de Tudor (10.09) pe
       cazul „casti b&o" / „b&o casti".

       Perechile NU sunt scrise de mână: sunt generate din `product_type`-ul
       fiecărei colecţii, inversând TIPURI_CATEGORIE (tip → handle-uri de
       categorie), deci un tip care acoperă mai multe handle-uri le primeşte
       pe toate („Cabluri" → cabluri-audio / de-boxe / hdmi / de-subwoofer).
       Toate cele 105 colecţii-ţintă au fost verificate prin Admin API:
       există şi au produse. Cele fără handle de categorie în TIPURI_CATEGORIE
       (Accesorii Căşti, Mobilier Hi-Fi, Mufe şi conectori, Mediaplayer,
       Accesorii audio-video) rămân NEMAPATE — un handle inventat ar produce
       404, iar regula casei e că 404 e mai rău decât o colecţie mai largă.
       `boxe-pasive-jbl-synthesis` NU e ținta lui „JBL": vendorul canonic al
       căutării e „JBL", iar Synthesis e gama de cinema premium — „boxe jbl"
       nu trebuie deturnat acolo. De la 07.10 „JBL Synthesis" e vendor propriu
       (14 produse publicate pe 30.09), deci „boxe jbl synthesis" are rută
       spre raftul lui, fără să atingă „boxe jbl". */
    "Acoustic Energy|boxe-pasive": "boxe-pasive-acoustic-energy",
    "Acoustic Energy|boxe-pasive-de-podea": "boxe-pasive-acoustic-energy",
    "AIAIAI|casti": "casti-aiaiai",
    "Architettura Sonora|boxe-pasive": "boxe-pasive-architettura-sonora",
    "Architettura Sonora|boxe-pasive-de-podea": "boxe-pasive-architettura-sonora",
    "ArtSound|boxe-amplificate": "boxe-active-artsound",
    "ArtSound|boxe-pentru-pc": "boxe-active-artsound",
    "ArtSound|boxe-portabile": "boxe-active-artsound",
    "ArtSound|boxe-pasive": "boxe-pasive-artsound",
    "ArtSound|boxe-pasive-de-podea": "boxe-pasive-artsound",
    "Arylic|amplificatoare-integrate": "amplificatoare-arylic",
    "Arylic|streamer-network-player": "streamere-arylic",
    "Atacama|standuri-si-suporturi-boxe": "stativ-boxe-atacama",
    "Audio-Technica|accesorii-pick-up": "accesorii-pick-up-audio-technica",
    "Audio-Technica|ace-pick-up": "ace-pick-up-audio-technica",
    "Audio-Technica|doze-pickup": "doze-pickup-audio-technica",
    "Audio-Technica|microfoane": "microfoane-audio-technica",
    "Audio-Technica|pick-up": "pick-up-uri-audio-technica",
    "Bang & Olufsen|accesorii-boxe-active": "accesorii-boxe-bang-olufsen",
    "Bang & Olufsen|boxe-amplificate": "boxe-active-bang-olufsen",
    "Bang & Olufsen|boxe-pentru-pc": "boxe-active-bang-olufsen",
    "Bang & Olufsen|boxe-portabile": "boxe-active-bang-olufsen",
    "Bang & Olufsen|boxe-pasive": "boxe-pasive-bang-olufsen",
    "Bang & Olufsen|boxe-pasive-de-podea": "boxe-pasive-bang-olufsen",
    "Bang & Olufsen|casti": "casti-bang-olufsen",
    "Bang & Olufsen|soundbar": "soundbar-bang-olufsen",
    "Bang & Olufsen|televizoare": "televizoare-bang-olufsen",
    "Bluesound|boxe-amplificate": "boxe-active-bluesound",
    "Bluesound|boxe-pentru-pc": "boxe-active-bluesound",
    "Bluesound|boxe-portabile": "boxe-active-bluesound",
    "Bluesound|standuri-si-suporturi-boxe": "stativ-boxe-bluesound",
    "Bluesound|streamer-network-player": "streamere-bluesound",
    "Bose|boxe-amplificate": "boxe-active-bose",
    "Bose|boxe-pentru-pc": "boxe-active-bose",
    "Bose|boxe-portabile": "boxe-active-bose",
    "Bowers & Wilkins|boxe-pasive-de-podea": "boxe-pasive-bowers-wilkins",
    "Bowers & Wilkins|standuri-si-suporturi-boxe": "stativ-boxe-bowers-wilkins",
    "Dali|boxe-pasive-de-podea": "boxe-pasive-dali",
    "Dan Clark Audio|casti": "casti-dan-clark-audio",
    "Denon|casti": "casti-denon",
    "Denon|pick-up": "pick-up-uri-denon",
    "DEVIALET|accesorii-boxe-active": "accesorii-boxe-devialet",
    "DEVIALET|boxe-amplificate": "boxe-active-devialet",
    "DEVIALET|boxe-pentru-pc": "boxe-active-devialet",
    "DEVIALET|boxe-portabile": "boxe-active-devialet",
    "DEVIALET|standuri-si-suporturi-boxe": "stativ-boxe-devialet",
    "Edifier|boxe-amplificate": "boxe-active-edifier",
    "Edifier|boxe-pentru-pc": "boxe-active-edifier",
    "Edifier|boxe-portabile": "boxe-active-edifier",
    "Elac|accesorii-boxe-active": "accesorii-boxe-elac",
    "Elac|boxe-pentru-pc": "boxe-active-elac",
    "Elac|boxe-portabile": "boxe-active-elac",
    "Elac|boxe-pasive-de-podea": "boxe-pasive-elac",
    "Elac|standuri-si-suporturi-boxe": "stativ-boxe-elac",
    "EPOS|microfoane": "microfoane-epos",
    "Epson|videoproiector": "videoproiectoare-epson",
    "Focal|boxe-pasive-de-podea": "boxe-pasive-focal",
    "Focal|standuri-si-suporturi-boxe": "stativ-boxe-focal",
    "Fosi Audio|amplificatoare-integrate": "amplificatoare-fosi-audio",
    "Fosi Audio|amplificatoare-pentru-casti": "amplificatoare-pentru-casti-fosi-audio",
    "Goldring|doze-pickup": "doze-pickup-goldring",
    "Harman Kardon|boxe-amplificate": "boxe-active-harman-kardon",
    "Harman Kardon|boxe-pentru-pc": "boxe-active-harman-kardon",
    "Harman Kardon|boxe-portabile": "boxe-active-harman-kardon",
    "iFi Audio|cabluri-audio": "cabluri-ifi-audio",
    "iFi Audio|cabluri-de-boxe": "cabluri-ifi-audio",
    "iFi Audio|cabluri-de-subwoofer": "cabluri-ifi-audio",
    "iFi Audio|cabluri-hdmi": "cabluri-ifi-audio",
    "iFi Audio|filtre-audio": "filtre-audio-ifi-audio",
    "JBL|boxe-amplificate": "boxe-active-jbl",
    "JBL|boxe-pentru-pc": "boxe-active-jbl",
    "JBL|boxe-portabile": "boxe-active-jbl",
    "JBL|casti": "casti-jbl",
    "JBL|soundbar": "soundbar-jbl",
    "JBL|accesorii-boxe-active": "accesorii-boxe-jbl",
    "JBL|microfoane": "microfoane-jbl",
    "Indiana Line|boxe-pasive": "boxe-pasive-indiana-line",
    "Indiana Line|boxe-pasive-de-podea": "boxe-pasive-indiana-line",
    "Indiana Line|subwoofere": "subwoofere-indiana-line",
    "Indiana Line|boxe-toate": "indiana-line",
    "Audio Pro|boxe-amplificate": "boxe-active-audio-pro",
    "Audio Pro|boxe-pentru-pc": "boxe-active-audio-pro",
    "Audio Pro|boxe-portabile": "boxe-active-audio-pro",
    "Audio Pro|boxe-toate": "audio-pro",
    "Rotel|amplificatoare-integrate": "amplificatoare-rotel",
    "Rotel|amplificatoare-de-putere": "amplificatoare-de-putere-rotel",
    "Rotel|cd-playere": "cd-players-recorders-rotel",
    "JBL Synthesis|boxe-pasive": "boxe-pasive-jbl-synthesis",
    "JBL Synthesis|boxe-pasive-de-podea": "boxe-pasive-jbl-synthesis",
    "JVC|videoproiector": "videoproiectoare-jvc",
    "Kanto Audio|boxe-amplificate": "boxe-active-kanto-audio",
    "Kanto Audio|boxe-pentru-pc": "boxe-active-kanto-audio",
    "Kanto Audio|boxe-portabile": "boxe-active-kanto-audio",
    "Kanto Audio|standuri-si-suporturi-boxe": "stativ-boxe-kanto-audio",
    "Kanto Audio|accesorii-pick-up": "accesorii-pick-up-kanto-audio",
    "Kanto Mounts|suporturi-tv": "suporturi-tv-kanto-mounts",
    "KEF|accesorii-boxe-active": "accesorii-boxe-kef",
    "KEF|boxe-pasive-de-podea": "boxe-pasive-kef",
    "KEF|standuri-si-suporturi-boxe": "stativ-boxe-kef",
    "Klipsch|boxe-pentru-pc": "boxe-active-klipsch",
    "Klipsch|boxe-portabile": "boxe-active-klipsch",
    "Klipsch|boxe-pasive-de-podea": "boxe-pasive-klipsch",
    "Klipsch|soundbar": "soundbar-klipsch",
    "Lenco|accesorii-pick-up": "accesorii-pick-up-lenco",
    "Lenco|pick-up": "pick-up-uri-lenco",
    "LG|televizoare": "televizoare-lg",
    "LOEWE|boxe-amplificate": "boxe-active-loewe",
    "LOEWE|boxe-pentru-pc": "boxe-active-loewe",
    "LOEWE|boxe-portabile": "boxe-active-loewe",
    "LOEWE|televizoare": "televizoare-loewe",
    "Meze Audio|cabluri-audio": "cabluri-meze-audio",
    "Meze Audio|cabluri-de-boxe": "cabluri-meze-audio",
    "Meze Audio|cabluri-de-subwoofer": "cabluri-meze-audio",
    "Meze Audio|cabluri-hdmi": "cabluri-meze-audio",
    "Musical Fidelity|amplificatoare-integrate": "amplificatoare-musical-fidelity",
    "NAGAOKA|ace-pick-up": "ace-pick-up-nagaoka",
    "NAGAOKA|doze-pickup": "doze-pickup-nagaoka",
    "NAIM|amplificatoare-integrate": "amplificatoare-naim",
    "Onkyo|receivere-a-v": "receivere-onkyo",
    "Optoma|videoproiector": "videoproiectoare-optoma",
    "Polk Audio|boxe-pasive-de-podea": "boxe-pasive-polk-audio",
    "Primare|amplificatoare-integrate": "amplificatoare-primare",
    "Primare|cd-playere": "cd-players-recorders-primare",
    "Pro-Ject|accesorii-pick-up": "accesorii-pick-up-pro-ject",
    "Pro-Ject|boxe-pasive": "boxe-pasive-pro-ject",
    "Pro-Ject|boxe-pasive-de-podea": "boxe-pasive-pro-ject",
    "Pro-Ject|cabluri-audio": "cabluri-pro-ject",
    "Pro-Ject|cabluri-de-boxe": "cabluri-pro-ject",
    "Pro-Ject|cabluri-de-subwoofer": "cabluri-pro-ject",
    "Pro-Ject|cabluri-hdmi": "cabluri-pro-ject",
    "Pro-Ject|doze-pickup": "doze-pickup-pro-ject",
    "Pro-Ject|pick-up": "pick-up-uri-pro-ject",
    "Pro-Ject|pre-amp-phono-stage": "preamplificatoare-phono-pro-ject",
    "PSB Speakers|boxe-pasive-de-podea": "boxe-pasive-psb-speakers",
    "Q Acoustics|boxe-pasive-de-podea": "boxe-pasive-q-acoustics",
    "QED|accesorii-boxe-active": "accesorii-boxe-qed",
    "QED|cabluri-audio": "cabluri-qed",
    "QED|cabluri-de-boxe": "cabluri-qed",
    "QED|cabluri-de-subwoofer": "cabluri-qed",
    "QED|cabluri-hdmi": "cabluri-qed",
    "Samsung|televizoare": "televizoare-samsung",
    "SCP|cabluri-audio": "cabluri-scp",
    "SCP|cabluri-de-boxe": "cabluri-scp",
    "SCP|cabluri-de-subwoofer": "cabluri-scp",
    "SCP|cabluri-hdmi": "cabluri-scp",
    "SENNHEISER|cabluri-audio": "cabluri-sennheiser",
    "SENNHEISER|cabluri-de-boxe": "cabluri-sennheiser",
    "SENNHEISER|cabluri-de-subwoofer": "cabluri-sennheiser",
    "SENNHEISER|cabluri-hdmi": "cabluri-sennheiser",
    "SENNHEISER|microfoane": "microfoane-sennheiser",
    "SilentPower|cabluri-audio": "cabluri-silentpower",
    "SilentPower|cabluri-de-boxe": "cabluri-silentpower",
    "SilentPower|cabluri-de-subwoofer": "cabluri-silentpower",
    "SilentPower|cabluri-hdmi": "cabluri-silentpower",
    "Sonos|boxe-pentru-pc": "boxe-active-sonos",
    "Sonos|boxe-portabile": "boxe-active-sonos",
    "Sonos|standuri-si-suporturi-boxe": "stativ-boxe-sonos",
    "Sony|televizoare": "televizoare-sony",
    "Stack Audio|accesorii-boxe-active": "accesorii-boxe-stack-audio",
    "Sumiko|doze-pickup": "doze-pickup-sumiko",
    "SVS|boxe-pasive-de-podea": "boxe-pasive-svs",
    "Technics|casti": "casti-technics",
    "Technics|pick-up": "pick-up-uri-technics",
    "Triangle|boxe-pasive": "boxe-pasive-triangle",
    "Triangle|boxe-pasive-de-podea": "boxe-pasive-triangle",
    "WIREWORLD|cabluri-audio": "cabluri-wireworld",
    "WIREWORLD|cabluri-de-boxe": "cabluri-wireworld",
    "WIREWORLD|cabluri-de-subwoofer": "cabluri-wireworld",
    "WIREWORLD|cabluri-hdmi": "cabluri-wireworld",
    "Xgimi|videoproiector": "videoproiectoare-xgimi",
    "Yamaha|amplificatoare-integrate": "amplificatoare-yamaha",
    "Yamaha|boxe-pasive-de-podea": "boxe-pasive-yamaha",
    "Yamaha|receivere-a-v": "receivere-yamaha",
    "Yamaha|soundbar": "soundbar-yamaha",

    /* NEMAPATE deliberat — brandurile cu `barred: true` in BRAND_SUBSTITUTIONS
       (Cambridge Audio, Ruark Audio, EverSolo, Argon Audio). Au colectii
       marca x categorie create pe 05.09, dar amandoi apelantii rutei le opresc
       inainte de harta (`!BRAND_SUBSTITUTIONS[vendor]` in runSearch, `return`
       in handlerul de Enter), fiindca sunt interzise la vanzare pe .ro si
       trebuie sa cada in renderWeak() cu alternativele configurate.
       Intrarile pentru ele ar fi fost cod mort care ARATA ca ruteaza — semnalat
       de Codex la verificarea din 10.09. Daca vreodata se deschid pe .bg, aici
       e locul unde se adauga, impreuna cu ridicarea garzii. */

  };

  /* Întoarce handle-ul colecției marcă × categorie, sau null dacă perechea n-are
     una. `null` NU e o eroare: înseamnă „mergi mai departe pe drumul de azi". */
  function brandCategoryCollectionHandle(vendor, categoryHandle) {
    if (!vendor || !categoryHandle) return null;
    var direct = BRAND_CATEGORY_COLLECTIONS[vendor + "|" + categoryHandle];
    if (direct) return direct;
    /* REZERVĂ pentru cererea generică de boxe. `boxe-toate` e un handle de
       INTENŢIE, nu de colecţie: îl are doar marca cu raft de grup real. Fără
       linia asta, „boxe triangle" ar pierde ruta pe care o avea înainte de
       10.09 (ducea la `boxe-pasive-triangle`) — adică despărţirea generic/precis
       ar fi rupt exact cazurile pe care nu le atinge. Ordinea contează: grupul
       întâi, pasivele ca rezervă. */
    if (categoryHandle === "boxe-toate") {
      return BRAND_CATEGORY_COLLECTIONS[vendor + "|boxe-pasive"] || null;
    }
    return null;
  }

  function brandCollectionHandle(vendor) {
    if (VENDOR_COLLECTION_OVERRIDES[vendor]) return VENDOR_COLLECTION_OVERRIDES[vendor];
    return String(vendor)
      .toLowerCase()
      .replace(/&/g, " ")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /* EXISTĂ colecția de brand? (2026-10-06, Tudor)

     `brandCollectionHandle` DERIVĂ handle-ul din nume, fără să știe dacă pagina există.
     Auditul nocturn a găsit 18 branduri recunoscute de căutare fără colecție (SCP 40
     de produse, Avax 21, Pioneer DJ 8…): „scp" + Enter ducea în 404, verificat pe live,
     iar rândul „Vezi toate cele N produse SCP" din dropdown trimitea în același 404.

     MĂSURAT pe live, 06.10 — trei capcane care au dictat forma de mai jos:
       · `/collections/<inexistent>/products.json` răspunde 200 cu `{"products":[]}`,
         nu 404 → doar o listă NEGOALĂ dovedește existența;
       · `/collections/wirewold.json` dă 404, deși pagina `/collections/wirewold` are
         redirect 301 spre `wireworld` → `.json` nu urmează redirect-urile de URL;
       · `HEAD /collections/<h>` urmează redirect-ul și e corect, dar costă 0,5–1,8 s
         pe o colecție existentă (serverul randează pagina).
     Deci: (1) `products.json` cu produse → există, fără cerere în plus (runCollection-
     Search); (2) handle-urile din VENDOR_COLLECTION_OVERRIDES sunt verificate de mână
     → există (un override învechit ar duce iar în 404 — auditul nocturn le ține de
     aceea în clasa GRAVĂ, nu în cea informativă); (3) altfel `.json` (rapid), iar DOAR la 404 un `HEAD` pe pagină, care
     prinde redirect-urile. Fără colecție, brandul merge în căutarea normală (/search).
     O eroare de rețea lasă necunoscut (undefined) → tot /search: o listă de rezultate
     e mai bună decât un 404 posibil.

     Numele `verificaColectieBrand` e MARCAJUL după care auditul nocturn recunoaște că
     garda există în asset-ul live (comentariile dispar la minificare, numele rămân).
     Cu ea, un brand fără colecție e o decizie de produs, nu un 404. Nu redenumi
     funcția fără să schimbi și `audit-rutare-colectii.py`. */
  var COLECTII_BRAND = {};
  var enterInCurs = false;
  for (var vOverride in VENDOR_COLLECTION_OVERRIDES) {
    if (Object.prototype.hasOwnProperty.call(VENDOR_COLLECTION_OVERRIDES, vOverride)) {
      COLECTII_BRAND[VENDOR_COLLECTION_OVERRIDES[vOverride]] = true;
    }
  }

  function verificaColectieBrand(handle) {
    if (Object.prototype.hasOwnProperty.call(COLECTII_BRAND, handle)) {
      return Promise.resolve(COLECTII_BRAND[handle]);
    }
    var cale = "/collections/" + encodeURIComponent(handle);
    return fetch(cale + ".json")
      .then(function (res) {
        if (res.ok) return true;
        if (res.status !== 404) return undefined;
        return fetch(cale, { method: "HEAD" }).then(function (pagina) {
          if (pagina.ok) return true;
          return pagina.status === 404 ? false : undefined;
        });
      })
      .then(function (exista) {
        if (exista !== undefined) COLECTII_BRAND[handle] = exista;
        return exista;
      })
      .catch(function () { return undefined; });
  }

  function linkCautare(text) {
    return "/search?q=" + encodeURIComponent(text);
  }

  /* `brandLink` (opțional): { vendor, total } — când e prezent, sub rezultate apare
     un rând „Vezi toate cele N produse <BRAND>" care duce la colecția brandului.
     Cerut de Tudor (2026-08-20): dropdown-ul arată 8 produse dintr-o gamă care poate
     avea 100+, iar cine tastează un nume de brand vrea de multe ori să RĂSFOIASCĂ,
     nu un singur produs. Fără el, singura cale spre colecție era Enter — care duce
     la /search, o pagină cu altă ordonare. */
  /* Completează rândurile cu alternative din BRAND_SUBSTITUTIONS, dacă e cazul,
     și abia apoi randează.
     Tudor, 2026-08-26: „noi avem fiio ka11, ăla trebuie să arate primul, dar după
     tu poți să arăți produse relevante" — deci ȘI, nu ORI. Prima versiune înlocuia
     rezultatele reale cu alternative doar când căutarea eșua complet; asta lăsa
     „fiio btr3k" cu un singur rând epuizat și niciun drum mai departe.
     (Notă de fapt, verificată pe 4 căi independente în aceeași zi — Admin API pe
     vendor, Admin API pe nume/SKU, suggest.json public și scanarea întregului
     catalog: KA11 nu există azi în catalog. Mecanismul de aici nu depinde de asta;
     în ziua în care KA11 e adăugat, apare primul, exact ca BTR3K acum.)

     Se montează în renderResults fiindcă e singurul punct prin care trec toate
     cele șase căi de căutare — vezi comentariul de mai jos. Orice cale nouă
     moștenește comportamentul, în loc să fie uitată tăcut. */
  function renderResults(container, rows, query, brandLink) {
    var sub = detectBrandSubstitution(query);
    var reale = ordoneazaGama(rows || [], query);
    if (!sub || !container) {
      renderRows(container, rows, query, brandLink);
      return;
    }
    /* Câte alternative încap sub rezultatele reale. Dacă produsele noastre umplu
       deja dropdown-ul, nu tăiem din ele ca să facem loc — alternativele sunt un
       bonus, nu o taxă pe rezultatele proprii. */
    var liber = RESULT_LIMIT - Math.min(reale.length, RESULT_LIMIT);
    if (liber <= 0) {
      renderRows(container, rows, query, brandLink);
      return;
    }
    fetchJson(substitutionUrl(sub.config, liber + reale.length), null)
      .then(function (subData) {
        var vazute = {};
        reale.forEach(function (r) {
          if (r.doc && r.doc.product_id != null) vazute[r.doc.product_id] = true;
        });
        var alt = toProductRows(subData).filter(function (r) {
          return !(r.doc && r.doc.product_id != null && vazute[r.doc.product_id]);
        }).slice(0, liber);
        renderRows(container, reale.concat(alt), query, brandLink);
      })
      .catch(function (err) {
        if (err.name === "AbortError") return;
        /* Alternativele sunt un plus; eșecul lor nu costă niciodată clientul
           rezultatele reale. */
        console.error("typesense-search: alternativele de brand au eșuat", err);
        renderRows(container, rows, query, brandLink);
      });
  }

  /* ── „Este X" bate „menționează X" ──────────────────────────────────────────
   *
   * Semnalat de Tudor pe 2026-08-26: căutarea „telecomanda" arăta KEF LS Luxe,
   * SVS Prime Wireless Pro și cinci Fosi Audio ÎNAINTEA oricărei telecomenzi.
   *
   * MĂSURAT pe indexul live, nu presupus: primele 9 rezultate potriveau EXCLUSIV
   * în `description` (toate spun, în proză, că au „telecomandă inclusă"), în timp
   * ce Beoremote One BT potrivea în `product_type` ȘI `subtitle`, iar Bluesound
   * RC1 la fel. Cauza nu e greutatea câmpurilor: `_text_match(buckets: 4)` pune
   * toate potrivirile de un token în ACELAȘI coș, deci decide `rank_score` — iar
   * o boxă de 20.999 lei se vinde mai bine decât o telecomandă de 1.950.
   *
   * De ce nu se rezolvă prin greutăți: `description` are deja greutatea minimă
   * (1 din 6). Problema e că bucketing-ul șterge diferența ÎNAINTE ca greutatea
   * să conteze. Singura reparație este să reordonăm după ce Typesense a răspuns.
   *
   * Regula: un rând care potrivește DOAR în descriere coboară sub toate rândurile
   * care potrivesc în titlu/subtitlu/tip/tag/vendor. NU se elimină — un produs cu
   * telecomandă inclusă rămâne un rezultat legitim, doar nu primul.
   *
   * Stabil (nu sortare comparativă) ca să nu strice ordinea comercială din
   * interiorul fiecărei grupe. Inert când toate rândurile sunt de același fel —
   * adică pe marea majoritate a interogărilor, verificat pe subwoofer, cd player,
   * microfon, streamer, boxe: toate aveau deja potrivire în titlu/tip. */
  function doarInDescriere(row) {
    /* `campuri` lipsă (răspuns fără highlights, ex. calea de colecție) înseamnă
       „nu știm" — și nu retrogradăm pe baza a ceea ce nu știm. */
    return Array.isArray(row.campuri) && row.campuri.length === 0;
  }

  function esteInainteaMentiunilor(rows) {
    var reale = [];
    var mentiuni = [];
    for (var i = 0; i < rows.length; i++) {
      (doarInDescriere(rows[i]) ? mentiuni : reale).push(rows[i]);
    }
    /* Dacă TOATE rândurile sunt menționări, ordinea rămâne neatinsă: nu avem cu
       ce să le comparăm, iar o listă reordonată degeaba ar fi doar zgomot. */
    if (!reale.length || !mentiuni.length) return rows;
    return reale.concat(mentiuni);
  }

  /* ── Modelul cerut trece în față, la ORICE brand (2026-08-29) ───────────────
     A treia oară același tipar în trei zile: „cine scrie un număr de model vrea
     acel produs primul". Prima oară a fost „beolab 90" (Beolab 50 ieșea primul), a
     doua „beolab 8". Amândouă au fost reparate în ordoneazaGama() — care se aplică
     DOAR la Bang & Olufsen și doar la interogări de cel mult două cuvinte.

     Al treilea caz arată de ce e greșit ca regula să fie a unui singur brand:
     „momentum 5" scoate primul „SENNHEISER Ear Tips", iar casca MOMENTUM True
     Wireless 5 e a patra. Accesoriul potrivește în `subtitle` și `sku` (își
     listează compatibilitățile), deci nu e nici măcar o menționare de descriere —
     retrogradarea existentă nu-l atinge.

     Regula, vendor-agnostică: dacă interogarea numește un număr de model, rândurile
     al căror TITLU conține chiar acel număr trec în față. Partiție stabilă, nu
     sortare — ordinea din interiorul fiecărei grupe rămâne cea comercială.

     Doar titlul contează, nu subtitlul: subtitlul e locul unde un accesoriu își
     scrie compatibilitățile, adică exact sursa problemei. */
  function modelulCerutInFata(rows, query) {
    var cerut = numarModelCerut(query);
    if (cerut == null || !rows || rows.length < 2) return rows || [];
    var rx = new RegExp("(?:^|[^0-9])" + cerut + "(?![0-9])");
    var chiarEl = [], restul = [];
    for (var i = 0; i < rows.length; i++) {
      var titlu = String((rows[i].doc && rows[i].doc.title) || "");
      (rx.test(titlu) ? chiarEl : restul).push(rows[i]);
    }
    if (!chiarEl.length || !restul.length) return rows;
    return chiarEl.concat(restul);
  }

  /** Regulile de ORDONARE, în ordinea în care se aplică. Comune dropdown-ului și
   *  paginii de rezultate — un singur loc, ca cele două suprafețe să nu poată
   *  răspunde diferit la aceeași întrebare.
   *
   *  Colapsarea pe familie NU e aici, deliberat: e o regulă de SUGESTIE (opt
   *  rânduri, nu-ți irosi două pe aceeași boxă), nu de LISTARE. Pe o pagină de
   *  rezultate omul vrea să vadă toate montările, nu una. */
  function ordoneazaRanduri(rows, query) {
    return esteInainteaMentiunilor(
      modelulCerutInFata(ordoneazaGama(rows || [], query), query)
    );
  }

  /* Insigna Black Friday a unui rând (21.10.2026) — oglinda lui snippets/bf-eticheta-card.liquid, ordinea
     ofertă → teaser → „în curând”. Dropdown-ul nu vede prețul BF (metacâmpul), deci citește tagurile puse de
     scripts/black-friday/teasere-bf.py din starea live; Workerul le duce în index în ~10 min de la schimbare
     (cautare-pret-webhook.ts). Pe .bg o ofertă cu preț FIX în euro (`bf-2026-fara-bg`) nu e BF, ca în
     snippets/bf-stare-var.liquid. Textele și starea campaniei vin din typesense-search-init.liquid (`bf`). */
  function etichetaBf(doc) {
    var cfg = window.typesenseConfig && window.typesenseConfig.bf;
    var tags = doc && doc.tags;
    if (!cfg || !Array.isArray(tags)) return "";
    var are = function (t) { return tags.indexOf(t) !== -1; };
    var faraBf = currentMarket() !== "ro" && are("bf-2026-fara-bg");
    var linii = null, cls = "";
    if (!faraBf && are("bf-2026-oferta")) linii = cfg.oferta;
    else if (!faraBf && are("bf-2026-teaser")) { linii = cfg.teaser; cls = " is-teaser"; }
    else if (are("bf-2026-curand")) { linii = cfg.curand; cls = " is-curand"; }
    if (!Array.isArray(linii) || !linii.length) return "";
    /* Ca pe card: două rânduri (`is-2r`, pasul mic al fontului) pentru teaser și „în curând”. */
    if (linii.length > 1) cls += " is-2r";
    return '<span class="s1lb ts-bf"><span class="bf-card-insigna' + cls + '"><b>' +
      linii.map(escapeHtml).join("<br>") + "</b></span></span>";
  }

  function renderRows(container, rows, query, brandLink) {
    /* Interogarea care a produs randarea asta (plan 2026-09-29, venitul din căutare): emițătorul
       (am-reco-tracking.js) o citește la clic și la „fără rezultate”, în loc de textul din bară. */
    if (container && container.setAttribute) container.setAttribute("data-am-q", String(query || ""));
    /* Ordonarea gamei se aplica AICI, nu in toProductRows: aceea nu primeste
       interogarea, iar regula depinde de ea (se aplica doar cautarilor pe gama).
       renderResults e punctul prin care trec TOATE cele sase cai de cautare —
       text, brand consumer + installation, backfill de stoc, retry, colectie —
       deci o cale noua o mosteneste automat, in loc sa fie uitata tacit. */
    rows = ordoneazaRanduri(desfaFamiliaCeruta(unesteFamilii(rows || []), query), query);
    /* Tăierea la limita de afișare se face AICI, după colapsare și ordonare — nu
       prin `per_page`. Altfel am tăia dintr-o listă în care mai multe rânduri
       urmează să se unească, și am pierde modele întregi (vezi FETCH_GROUP_LIMIT). */
    if (rows.length > RESULT_LIMIT) rows = rows.slice(0, RESULT_LIMIT);
    if (!rows || rows.length === 0) {
      /* NOTE: the previous version wrapped the whole expression in escapeHtml() a
         second time, so a query containing & or < rendered as "&amp;amp;" in the
         no-results line. Escaping now happens exactly once, on the query alone. */
      var noResultsText = window.translations && window.translations.search_no_results
        ? window.translations.search_no_results
        : t().niciunRezultat(query);
      var shortcut = categoryShortcutFor(query);
      container.innerHTML =
        '<div class="cols"><p>' + escapeHtml(noResultsText) + "</p></div>" +
        '<div class="ts-no-results-help">' +
          (shortcut
            ? '<a class="ts-nr-collection" href="' + escapeHtml(shortcut.url) + '">' +
                escapeHtml(shortcut.label) + "</a>"
            : "") +
          '<p class="ts-nr-prompt">' + escapeHtml(t().spuneNe) + '</p>' +
          '<a class="ts-nr-wa" href="' + escapeHtml(whatsappHref(query)) + '"' +
            ' target="_blank" rel="noopener noreferrer">' +
            '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
              '<path fill="currentColor" d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5 4.5.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/>' +
              '<path fill="currentColor" d="M12 2A10 10 0 0 0 3.5 17.2L2 22l4.9-1.5A10 10 0 1 0 12 2zm0 18.2c-1.6 0-3.1-.4-4.4-1.2l-.3-.2-3 .9.9-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z"/>' +
            "</svg>" +
            "<span>" + escapeHtml(t().scrieNe) + "</span>" +
          "</a>" +
        "</div>";
      return;
    }

    var itemsHtml = rows
      .map(function (row) {
        var doc = row.doc;
        /* Title and price in the CURRENT market's language/currency. On .ro these
           are doc.title / row.price exactly as before; on .bg they become what the
           .bg storefront itself serves. */
        var titleText = displayTitle(doc);
        var available = row.inStock !== false;
        var unavailableClass = available ? "" : " unavailable";
        var imgHtml = doc.image_url
          ? '<figure><picture><img src="' + escapeHtml(doc.image_url) +
            '" alt="' + escapeHtml(titleText) + '" width="70" height="71"></picture></figure>'
          : "";
        var stockNote = available ? "" : '<p class="small">' + escapeHtml(t().stocEpuizat) + '</p>';
        var productUrl = "/products/" + encodeURIComponent(doc.handle || "");
        /* custom.product_subtitle, when the product has one — most do not yet, so this
           renders nothing rather than an empty line. Sits under the title on its own
           row, matching where it appears on the PDP and in list cards. */
        /* Subtitlul se afișează DOAR pe piața care îl are în limba ei.
           Indexul are `title_bg`, dar NU are `subtitle_bg` (verificat pe schema live,
           2026-08-26): sincronizarea nu capturează subtitlul bulgar. Afișat ca atare
           pe .bg, ar fi text ROMÂNESC sub un titlu bulgăresc — exact „fallback-ul
           tăcut la limba primară" pe care regulile proiectului îl interzic.
           Mai bine lipsă decât în limba greșită. Se reactivează în clipa în care
           worker-ul indexează `subtitle_bg`. */
        var subtitluVizibil = currentMarket() === "ro" ? doc.subtitle : subtitleForMarket(doc);
        var subtitleHtml = subtitluVizibil
          ? '<p class="ts-subtitle small">' + escapeHtml(subtitluVizibil) + "</p>"
          : "";
        // "de la" only when the buyable variants genuinely differ in price — on a
        // single-price product it would just be noise.
        /* `doc` is the LEAD variant — the one that owns row.price (see toProductRows) —
           so its price_bgn is the BGN price of the very same variant, not of some
           other one. Reading it from anywhere else would pair a "de la" with a price
           belonging to a different variant. */
        var shown = displayPrice(doc, row.price);
        var priceText = (row.hasRange ? t().dela : "") + formatPrice(shown.value, shown.currency);
        /* Doar pe rândurile cumpărabile, ca insigna de pe card (variantă disponibilă). */
        var bfHtml = available ? etichetaBf(doc) : "";
        /* Brandul stă pe rândul lui, ÎNAINTEA lui `.cols`, nu în interiorul lui
           `h2`. Motivul e măsurat: `.cols` e flex (`display:flex`), cu titlul şi
           preţul ca fraţi pe acelaşi rând. Băgat în `h2`, brandul ar fi împins
           modelul pe al doilea rând al aceleiaşi cutii, iar preţul — aliniat sus —
           ar fi rămas în dreptul BRANDULUI, nu al modelului. Aşa, preţul rămâne
           pe linia modelului, exact ca înainte.
           (Comentariul mai vechi din injectDropdownCss spune că `.cols` foloseşte
           float-uri; pe tema DEV nu mai e adevărat — measured 2026-08-28.)

           `aria-label` cu titlul întreg doar când am tăiat: altfel linkul ar
           anunţa „Beosound 2 3rd Gen" fără brand. Textul vizibil e conţinut în
           etichetă, deci „Label in Name" (WCAG 2.5.3) rămâne respectat. */
        var brand = splitBrand(doc);
        var brandHtmlLine = brand.brand
          ? '<p class="ts-brand">' + escapeHtml(brand.brand) + "</p>"
          : "";
        var titleAria = brand.brand
          ? ' aria-label="' + escapeHtml(titleText) + '"'
          : "";
        /* `data-product-id` pentru instrumentare (am-list-tracking.js). Id-ul se ia
           DE AICI, din chiar înregistrarea care randează rândul — nu prin corelare
           după poziție cu răspunsul Typesense: ordinea din răspuns NU e ordinea
           din DOM (`reordoneazaPaginaDeRezultate` rearanjează, iar `ordoneazaRanduri`
           /`unesteFamilii` schimbă ordinea și pentru dropdown). Măsurat 15.09.2026:
           pe „kef", id-ul de la indexul 0 din răspuns aparținea altui produs decât
           primul card afișat. Atribut pur pasiv: nimic din temă nu-l citește. */
        var idProdus = doc.product_id != null ? String(doc.product_id) : "";
        var attrId = idProdus ? ' data-product-id="' + escapeHtml(idProdus) + '"' : "";
        return (
          '<li class="' + (imgHtml ? "" : "no-img") + unavailableClass + '"' + attrId + '>' +
          imgHtml +
          "<section>" + brandHtmlLine +
          "<div class=\"cols\"><h2><a href=\"" + escapeHtml(productUrl) + "\"" + titleAria + ">" +
          escapeHtml(brand.model) +
          '</a></h2><p class="price s1pr">' + escapeHtml(priceText) + bfHtml + "</p></div>" +
          subtitleHtml +
          stockNote +
          "</section></li>"
        );
      })
      .join("");

    var brandHtml = "";
    if (brandLink && (brandLink.vendor || brandLink.handle)) {
      var handle = brandLink.handle || brandCollectionHandle(brandLink.vendor);
      var eticheta;
      if (brandLink.vendor) {
        eticheta = brandLink.total
          ? t().veziToateBrand(brandLink.total, brandLink.vendor)
          : t().veziToateBrand(0, brandLink.vendor);
      } else {
        /* Categorie: titlul colecției e mai natural decât handle-ul („Doze pickup",
           nu „doze-pickup"), iar `brandLink.titlu` vine din CATEGORY_TITLES sau
           din colecție. Numărul se afișează când îl știm — „Vezi toate cele 96 de
           produse din Cabluri" spune omului că mai are unde căuta; fără el, rândul
           pare doar un link. */
        eticheta = brandLink.total
          ? t().veziToateCategorie(brandLink.total, brandLink.titlu || handle)
          : t().veziToateCategorie(0, brandLink.titlu || handle);
      }
      /* Brand fără colecție confirmată (COLECTII_BRAND) → căutarea normală, nu 404.
         Categoriile au `handle` explicit din colecții reale, deci nu trec pe aici. */
      var hrefTot = (brandLink.vendor && !brandLink.handle && COLECTII_BRAND[handle] !== true)
        ? linkCautare(brandLink.vendor)
        : "/collections/" + encodeURIComponent(handle);
      brandHtml =
        '<a class="ts-brand-all" href="' + escapeHtml(hrefTot) + '">' +
          escapeHtml(eticheta) +
          '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
            '<path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M9 6l6 6-6 6"/>' +
          "</svg>" +
        "</a>";
    }

    container.innerHTML =
      '<div class="cols"><p>' + escapeHtml(t().rezultatePentru(query)) + '</p></div>' +
      '<ul class="l4ca compact">' + itemsHtml + "</ul>" + brandHtml;
  }

  function fetchJson(url, signal) {
    return fetch(url, {
      headers: { "X-TYPESENSE-API-KEY": CONFIG.searchKey },
      signal: signal,
    }).then(function (res) {
      if (!res.ok) throw new Error("typesense search failed: " + res.status);
      return res.json();
    });
  }

  /* ── Un singur dus-întors pentru două căutări (2026-08-29) ──────────────────
     MĂSURAT: căutarea în Typesense durează 5–13 ms, dar dus-întorsul costă ~65 ms
     pe o conexiune caldă și 100–200 ms pe mobil. Calea dominantă a dropdown-ului
     face DOUĂ cereri secvențiale — exact pe stoc, apoi completarea cu epuizate —
     deci plătește dus-întorsul de două ori pentru ceva ce știm de la început că
     vrem: backfill-ul se declanșează ori de câte ori rămân sub 8 rânduri după
     colapsare, adică pe majoritatea interogărilor.

     `multi_search` le trimite pe amândouă într-o singură cerere. Aceeași muncă pe
     server, jumătate din așteptare pentru om. Endpoint-ul e deja folosit în
     `am-typesense-filters.js` pe colecții, deci nu e o dependență nouă.

     Parametrii se iau din searchUrl(), NU se rescriu aici: un al doilea loc în
     care se decid ar diverge — exact greșeala pe care o făcea auditul, care
     trimitea alți parametri decât producția. */
  function paramsDinUrl(url) {
    var qs = String(url).split("?")[1] || "";
    var o = {};
    new URLSearchParams(qs).forEach(function (v, k) { o[k] = v; });
    o.collection = CONFIG.collection || "products";
    return o;
  }

  function multiSearch(urls, signal) {
    var protocol = CONFIG.protocol || "https";
    var port = CONFIG.port || "443";
    return fetch(protocol + "://" + CONFIG.host + ":" + port + "/multi_search", {
      method: "POST",
      headers: {
        "X-TYPESENSE-API-KEY": CONFIG.searchKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ searches: urls.map(paramsDinUrl) }),
      signal: signal,
    })
      .then(function (res) {
        if (!res.ok) throw new Error("typesense multi_search failed: " + res.status);
        return res.json();
      })
      .then(function (data) {
        var r = (data && data.results) || [];
        /* Typesense întoarce erorile PER căutare, cu status 200 pe plic. Un
           rezultat cu `error` tratat ca răspuns gol ar arăta ca „n-am găsit
           nimic" — tăcut, și fals. */
        r.forEach(function (x) {
          if (x && x.error) throw new Error("typesense multi_search: " + x.error);
        });
        return r;
      });
  }

  /* STOCK BACKFILL (Tudor, 2026-08-04: "the idea was to push them at the bottom of the
     list but when there are no results to fill the list we should also show out of
     stock products").

     Out-of-stock products were never filtered out — 840 of them are indexed with
     in_stock_rank 0, and Typesense returns them. They were invisible for an emergent
     reason: `sort_by` puts in-stock first and the dropdown shows only RESULT_LIMIT
     rows, so whenever a query had >= 8 in-stock matches the out-of-stock ones never
     reached a visible slot. They only ever appeared by accident, on queries too sparse
     to fill the list.

     Fixed the same way the brand branch backfills installation products: fetch the
     in-stock phase first, and only if it leaves slots empty fetch the out-of-stock
     phase for exactly the remainder. Guarantees in-stock ALWAYS outrank out-of-stock
     (a single query with sort_by could not, once typo/text scoring enters the sort),
     and costs a second request only on queries that are already sparse.

     The renderer needs no change: it already marks any row with inStock === false with
     the theme's own "Stoc epuizat" wording plus an `unavailable` class. */
  /* vendorFilter/brandLink (opționale): pe calea scopată pe vendor, backfill-ul
     out-of-stock trebuie să rămână în ACELAȘI brand (altfel golul se umple cu
     produse străine de cerere), iar rândul „Vezi toate produsele X" se păstrează.
     displayQuery (opțional): ce se AFIȘEAZĂ în „Rezultate pentru …" — interogarea
     originală a omului; `query` rămâne cea TRIMISĂ (fără stopwords, fără tokenul
     de brand pe calea scopată), fiindcă refetch-ul fazei out o refolosește. */
  /* `outAdus` (opțional): răspunsul out-of-stock deja obținut printr-un
     multi_search. Când e dat, faza a doua nu mai face nicio cerere. Apelanții
     care nu-l au se comportă exact ca înainte. */
  function renderWithStockBackfill(query, allowTypo, container, thisGeneration, signal, data, vendorFilter, brandLink, displayQuery, outAdus) {
    var shown = displayQuery || query;
    var inStockRows = toProductRows(data);
    /* Numărăm rândurile DUPĂ colapsarea pe familie — toProductRows le-a unit deja,
       deci `inStockRows.length` e numărul real de rânduri afișabile. Comparația
       trebuie făcută pe asta, nu pe câte grupuri a întors Typesense: din
       2026-08-24 cerem FETCH_GROUP_LIMIT (24) grupuri, iar o comparație pe numărul
       brut ar sări mereu peste backfill și ar lăsa dropdownul pe jumătate gol la
       interogările rare. */
    if (inStockRows.length >= RESULT_LIMIT) {
      renderResults(container, inStockRows, shown, brandLink);
      return;
    }
    var remaining = RESULT_LIMIT - inStockRows.length;
    /* The out-of-stock phase asks for a FULL page rather than just `remaining`: some of
       what it returns will be deduped away below (mixed-stock products already shown),
       and a short request could leave the dropdown with fewer rows than it could fill.
       RESULT_LIMIT is 8, so this is one small request either way. */
    return (outAdus ? Promise.resolve(outAdus)
                    : fetchJson(searchUrl(query, allowTypo, "out", vendorFilter), signal))
      .then(function (outData) {
        if (thisGeneration !== latestGeneration) return;
        /* Drop products already shown by the in-stock phase before taking the
           remainder. The index holds one document per VARIANT, so a product with some
           variants in stock and others sold out matches BOTH phases — "KEF LS50 Meta"
           and "Klipsch RF-7 III" each appeared twice in one dropdown, the second time
           marked "Stoc epuizat" (caught by Codex review of 810b78f, reproduced on the
           live index 2026-08-04). Dedupe by product_id, and slice AFTER filtering or
           the duplicates consume the free slots and legitimate products are pushed
           out. */
        var seen = {};
        inStockRows.forEach(function (row) {
          if (row.doc && row.doc.product_id != null) seen[row.doc.product_id] = true;
        });
        /* Pragul RELATIV de token-uri (d3ac2de7, 2026-08-04; portat aici pe 2026-08-20).
           Cazul care l-a cerut: „kef ventura" are 2 produse pe stoc, deci faza 2 ruleaza cu
           in_stock:false — unde NIMIC nu satisface ambele token-uri, si fara filtru intra in
           lista KEF-uri care au potrivit un singur token, niciunul Ventura.

           RELATIV, nu absolut (tokensMatched === numarul de token-uri din interogare), si
           asta e tot rostul: „pro ject vinyl" are 3 token-uri, dar raspunsurile corecte se
           opresc la tm=2, fiindca „Pro-Ject" e UN token in index. O regula absoluta ar goli
           acel dropdown.

           Cand faza pe stoc e GOALA, pragul e 0 si nu se filtreaza nimic — o interogare fara
           niciun rezultat pe stoc (bugul initial, ex. „lp60") isi arata in continuare
           produsele fara stoc. Pragul scoate doar randuri strict mai slabe decat ceva deja
           vizibil, deci nu poate goli un dropdown care altfel ar fi avut continut.
           Masurat la introducere: 62 de interogari imbunatatite, zero regresii. */
        var floor = bestTokensMatched(inStockRows);
        var outRows = toProductRows(outData)
          .filter(function (row) {
            if (!floor) return true; // nimic pe stoc cu care sa compari — pastreaza tot
            if (typeof row.tokensMatched !== "number") return true; // fara date — nu ghici
            return row.tokensMatched >= floor;
          })
          .filter(function (row) {
            return !(row.doc && row.doc.product_id != null && seen[row.doc.product_id]);
          })
          .slice(0, remaining);
        renderResults(container, inStockRows.concat(outRows), shown, brandLink);
      })
      .catch(function (err) {
        if (err.name === "AbortError") return; // superseded by a newer keystroke
        if (thisGeneration !== latestGeneration) return;
        // Backfill is an enhancement — never let it cost the user the in-stock rows.
        console.error("typesense-search: out-of-stock backfill failed", err);
        renderResults(container, inStockRows, shown, brandLink);
      });
  }

  /**
   * The ordinary, exact-match-first / vendor-typo-guarded text search — everything
   * runSearch() did before the brand-intent branch was added on 2026-08-03. Also
   * the fallback target when brand-intent search matches a vendor with zero live
   * products, or when the brand-intent fetches themselves fail.
   */
  function runTextSearch(query, container, thisGeneration, signal) {
    /* Interogarea TRIMISĂ ≠ interogarea AFIȘATĂ: cuvintele de legătură ies din
       cea trimisă („boxe pentru birou" → „boxe birou"), dar dropdown-ul afișează
       mereu exact ce a scris omul. */
    var sentQuery = stripStopwords(query);
    var scope = findVendorScope(sentQuery);
    if (scope) {
      runVendorScopedSearch(query, sentQuery, scope, container, thisGeneration, signal);
      return;
    }
    runPlainTextSearch(query, sentQuery, container, thisGeneration, signal);
  }

  /**
   * Căutare cu brandul ca FILTRU: „boxe klipsch" devine q="boxe" +
   * vendor:=Klipsch. Vezi findVendorScope() pentru cele două moduri măsurate în
   * care calea de text pură pierdea aceste interogări (prefixul pe ultimul token
   * și potrivirea în descrierile altor branduri).
   *
   * Escaladare în interiorul brandului: exact pe stoc → exact fără stoc → typo
   * pe rest („kef ventra"). Dacă brandul chiar nu are nimic pentru cerere
   * („klipsch telecomanda"), cade pe căutarea generală NEfiltrată — fail-open,
   * ca tot fișierul, și tot ce era valid ieri rămâne valid azi.
   */
  function runVendorScopedSearch(displayQuery, sentQuery, scope, container, thisGeneration, signal) {
    /* Brandul are substituție configurată? Calea scopată rulează NORMAL — ce avem
       de la brandul cerut trebuie să apară primul (Tudor, 2026-08-26). Diferența e
       că renderResults completează apoi lista cu alternative, deci „gama" scurtă a
       unui brand pe care abia îl aprovizionăm nu mai e un drum înfundat.
       `brandLink` rămâne null aici, deliberat: link-ul „Vezi toate produsele X"
       ar duce la o colecție care pentru brandurile astea nu există (404 verificat
       pe cambridge-audio/ruark-audio/eversolo/argon-audio, 2026-08-26). */
    var brandLink = BRAND_SUBSTITUTIONS[scope.vendor] ? null : { vendor: scope.vendor, total: 0 };
    /* Dacă restul interogării e o CATEGORIE cunoscută, o trimitem ca filtru pe
       `product_type`, nu ca text liber. Altfel „kef casti" returna soundbar-uri
       KEF, iar „casti sony" un Audio-Technica — potriviri în descriere, nu în
       categorie. Cu filtrul, un brand care nu are acea categorie întoarce ZERO,
       iar escaladarea existentă duce mai departe la căutarea generală. */
    var typeFilter = filtruTipCategorie(detectCategoryIntent(scope.rest));
    fetchJson(searchUrl(scope.rest, false, "in", scope.vendor, typeFilter), signal)
      .then(function (data) {
        themeSearchSuppressed(true);
        if (thisGeneration !== latestGeneration) return;
        var rows = toProductRows(data);
        /* rows.length, nu doar data.found: filtrul de piață din toProductRows
           poate goli un răspuns cu found > 0 (brand vizibil doar pe .bg), iar un
           dropdown gol nu e un rezultat — se escaladează mai departe. */
        if (data.found > 0 && rows.length) {
          return renderWithStockBackfill(scope.rest, false, container, thisGeneration, signal, data, scope.vendor, brandLink, displayQuery);
        }
        /* Aceeaşi disciplină pe treapta „fără stoc": altfel filtrul de tip s-ar
           pierde exact acolo unde brandul n-are categoria, adică fix cazul pe
           care îl reparăm. Treapta de TYPO de mai jos rămâne NEfiltrată
           deliberat — acolo scopul e să găsim orice al brandului, iar dacă nici
           ea nu întoarce nimic se cade oricum pe gama brandului. */
        return fetchJson(searchUrl(scope.rest, false, "out", scope.vendor, typeFilter), signal).then(function (outData) {
          if (thisGeneration !== latestGeneration) return;
          var outRows = toProductRows(outData);
          if (outData.found > 0 && outRows.length) {
            renderResults(container, outRows.slice(0, RESULT_LIMIT), displayQuery, brandLink);
            return;
          }
          /* Typo DOAR pe rest — brandul e cert (a potrivit exact un nume/alias),
             deci toleranța nu poate aluneca spre alt brand. */
          return fetchJson(searchUrl(scope.rest, true, "in", scope.vendor), signal).then(function (retryData) {
            if (thisGeneration !== latestGeneration) return;
            var retryRows = toProductRows(retryData);
            if (retryData.found > 0 && retryRows.length) {
              return renderWithStockBackfill(scope.rest, true, container, thisGeneration, signal, retryData, scope.vendor, brandLink, displayQuery);
            }
            /* Brandul e cert, dar cererea nu i se potrivește („fiio ka11" — model
               necomercializat; „klipsch telecomanda" — nu fac telecomenzi).
               GAMA brandului, în ordinea comercială, e un răspuns mai bun decât
               typo-junk sau zero: clientul a numit marca, arată-i ce are marca.
               Doar rolul consumer (fără produsele de instalare), ca la ruta de
               brand-intent. Dacă nici gama nu are nimic vizibil pe .ro (brand
               barat — Cambridge), cade pe căutarea generală, care NU mai trece
               prin scoping (runPlainTextSearch), deci nu poate bucla. */
            return fetchJson(brandSearchFetchUrl(brandSearchParams(scope.vendor, "tags:!=`" + INSTALLATION_ROLE_TAG + "`", RESULT_LIMIT)), signal).then(function (rangeData) {
              if (thisGeneration !== latestGeneration) return;
              var rangeRows = toProductRows(rangeData);
              if (rangeRows.length) {
                renderResults(container, rangeRows.slice(0, RESULT_LIMIT), displayQuery, brandLink);
                return;
              }
              runPlainTextSearch(displayQuery, sentQuery, container, thisGeneration, signal);
            });
          });
        });
      })
      .catch(function (err) {
        if (err.name === "AbortError") return; // superseded by a newer keystroke
        if (thisGeneration !== latestGeneration) return;
        /* O eroare pe calea scopată nu costă utilizatorul căutarea: încearcă
           calea generală, care are propriul failover către căutarea nativă. */
        console.error("typesense-search: scoped search failed, falling back", err);
        runPlainTextSearch(displayQuery, sentQuery, container, thisGeneration, signal);
      });
  }

  /* ── Codul de model scris lipit (2026-08-29) ────────────────────────────────
     Găsit de auditul de prezentare: „hd599" arată CABLUL HD 5X8/5X9, deși casca
     HD 599 există și e pe stoc. Cauza, măsurată: forma lipită nu se potrivește pe
     niciun câmp al PRODUSULUI — se potrivește pe DESCRIEREA unui accesoriu, care
     își listează compatibilitățile. Iar asta e sistematic în defavoarea noastră:
     accesoriile enumeră modelele pe care le deservesc, produsele nu.

     „beolab90" merge azi din NOROC — nimerește SKU-ul. Nu e o regulă pe care se
     poate conta: depinde de cum a scris cineva un cod în catalog.

     Reparația de fond ar fi un câmp `model_keys` în index (titlu + SKU fără
     separatori), dar cere schimbare de schemă, worker și o resincronizare
     completă. Asta de aici obține același lucru pentru cazul măsurat, fără să
     atingă infrastructura: dacă TOT ce am găsit doar menționează interogarea,
     încearcă forma despărțită înainte de a te da bătut.

     Se declanșează rar prin construcție — „ls50", „kc62", „dt770", „q7meta" au
     toate potrivire în titlu, deci nu ajung niciodată aici. */
  function modelSpart(q) {
    var t = String(q || "").trim();
    if (!t || /\s/.test(t)) return null; // doar interogările de un singur token
    var m = t.match(/^([a-zăâîșşțţ]{2,})(\d{2,}[a-z]*)$/i);
    return m ? m[1] + " " + m[2] : null;
  }

  /** true dacă niciun rând nu potrivește altundeva decât în descriere — inclusiv
   *  cazul „niciun rând", care e aceeași lipsă de dovadă. */
  function numaiMentiuni(rows) {
    if (!rows || !rows.length) return true;
    for (var i = 0; i < rows.length; i++) {
      if (!doarInDescriere(rows[i])) return false;
    }
    return true;
  }

  function runPlainTextSearch(displayQuery, sentQuery, container, thisGeneration, signal, dejaSpart) {
    /* Rândurile „slabe" (vezi esteRezultatSlab) găsite pe parcurs. Se afișează
       numai dacă nicio fază nu produce un răspuns care potrivește toată
       interogarea — răspunsul de tip „best effort" rămâne, dar nu mai maschează
       un typo reparabil. */
    /* {data, allowTypo, vendorFilter} — prima fază care a produs rânduri, chiar
       slabe. Exact-in are prioritate (fără zgomot de typo); fazele de retry o
       completează doar dacă exactul n-a dat nimic — cazul „fiio ka11": modelul
       nu există în catalog, dar restul gamei Fiio e un răspuns mai bun decât
       „Niciun rezultat". */
    var weakData = null;
    function keepWeak(data, allowTypo, vendorFilter) {
      if (!weakData) weakData = { data: data, allowTypo: allowTypo, vendorFilter: vendorFilter };
    }
    function renderWeak() {
      if (weakData) {
        renderWithStockBackfill(sentQuery, weakData.allowTypo, container, thisGeneration, signal, weakData.data, weakData.vendorFilter, null, displayQuery);
        return;
      }
      renderResults(container, [], displayQuery);
    }
    /* Ambele faze exacte — pe stoc și epuizate — într-un singur dus-întors. Vezi
       multiSearch(). Faza a doua se consuma oricum pe majoritatea interogărilor;
       acum nu mai costă o a doua așteptare, iar cu include_fields cele două
       răspunsuri împreună cântăresc cât unul singur înainte. */
    multiSearch([searchUrl(sentQuery, false, "in"), searchUrl(sentQuery, false, "out")], signal)
      .then(function (rezultate) {
        var data = rezultate[0];
        var outAdus = rezultate[1];
        // Typesense answered — keep the theme's own search suppressed.
        themeSearchSuppressed(true);
        // Authoritative staleness check — see latestGeneration comment above.
        // A response for a superseded query must never overwrite a newer one's
        // results, even if it resolves after them.
        if (thisGeneration !== latestGeneration) return;

        var rows = toProductRows(data);
        /* Codul de model scris lipit — vezi modelSpart(). Se încearcă ÎNAINTE de
           orice, dar numai când nimic din ce am găsit nu potrivește altundeva
           decât în descriere; altfel un rezultat bun n-ar fi niciodată atins.
           `dejaSpart` oprește orice buclă: forma despărțită parcurge scara
           întreagă o singură dată. */
        var spart = dejaSpart ? null : modelSpart(sentQuery);
        if (spart && spart !== sentQuery && numaiMentiuni(rows)) {
          return runPlainTextSearch(displayQuery, spart, container, thisGeneration, signal, true);
        }
        if (data.found > 0 && rows.length && !esteRezultatSlab(sentQuery, rows)) {
          return renderWithStockBackfill(sentQuery, false, container, thisGeneration, signal, data, null, null, displayQuery, outAdus);
        }
        if (data.found > 0 && rows.length) keepWeak(data, false, null);

        /* Exact match found nothing (or only weak, token-dropped rows). Before
         * turning typo tolerance on for the whole query, check whether the text
         * plausibly typo-matches a REAL vendor we carry (see vendorTypoUrl()
         * comment). This is the "cambridge" guard: without it, re-running with
         * full typo tolerance unconditionally reintroduces "cambridge" ->
         * "cartridge" (Audio-Technica/B&O/Pro-Ject turntables). */
        /* Zero IN-STOCK exact matches is not the same as zero matches: since
           2026-08-04 the first pass is stock-filtered, so a product we carry but have
           sold out of (e.g. the KEF Muon) would otherwise fall through to the typo
           branch and be reported as "no results". Check the out-of-stock half of the
           exact pass before ever treating the query as a possible misspelling. */
        /* Deja adus de multi_search-ul de mai sus — nicio cerere aici. */
        return Promise.resolve(outAdus)
          .then(function (outData) {
            if (thisGeneration !== latestGeneration) return;
            var outRows = toProductRows(outData);
            if (outData.found > 0 && outRows.length && !esteRezultatSlab(sentQuery, outRows)) {
              renderResults(container, outRows.slice(0, RESULT_LIMIT), displayQuery);
              return;
            }
            return fetchJson(vendorTypoUrl(sentQuery), signal).then(function (vendorProbe) {
              if (thisGeneration !== latestGeneration) return;
              /* Sonda de vendor decide CÂTĂ toleranță primește retry-ul:
                 - trece → num_typos=2, istoric („klipsh" → Klipsch; „jbi" → JBL)
                 - pică  → "strict" (num_typos=1, nou 2026-08-26): repară typo-uri
                   de cuvinte obișnuite — „subwofer" → subwoofere, „aplif" →
                   amplificatoare — care înainte mureau în „Niciun rezultat",
                   fiindcă poarta de vendor era singura cale spre orice toleranță.
                   1, nu 2: falsul prieten măsurat („cambridge" → „cartridge")
                   trăiește exact la distanța 2. */
              var typoLevel = vendorProbe.found > 0 ? true : "strict";
              /* Sonda nu spune doar DACĂ un vendor e aproape — spune și CARE.
                 „klipsh boxe" fără filtru rămânea cu receiverele Onkyo deasupra
                 boxelor Klipsch chiar și după corecția typo (aceeași potrivire
                 în-descriere ca la interogarea scrisă corect). Retry-ul rulează
                 întâi restrâns la vendorul sondat; dacă restrângerea nu dă
                 nimic (sonda a ghicit greșit brandul), se reia nefiltrat. */
              var probeVendor = null;
              if (vendorProbe.found > 0 && vendorProbe.hits && vendorProbe.hits[0] &&
                  vendorProbe.hits[0].document) {
                probeVendor = vendorProbe.hits[0].document.vendor || null;
              }
              function retryPhase(vendorFilter, onMiss) {
                return fetchJson(searchUrl(sentQuery, typoLevel, "in", vendorFilter), signal).then(function (retryData) {
                  if (thisGeneration !== latestGeneration) return;
                  var retryRows = toProductRows(retryData);
                  if (retryData.found > 0 && retryRows.length && !esteRezultatSlab(sentQuery, retryRows)) {
                    return renderWithStockBackfill(sentQuery, typoLevel, container, thisGeneration, signal, retryData, vendorFilter, null, displayQuery);
                  }
                  /* Doar retry-ul RESTRÂNS la vendorul sondat lasă „slabe" în urmă:
                     un weak nefiltrat cu typo e zgomot pur (măsurat: „fiio ka11"
                     nefiltrat potrivea discuri de vinil prin typo pe track-list) —
                     acolo, zero + WhatsApp e răspunsul mai cinstit. */
                  if (vendorFilter && retryData.found > 0 && retryRows.length) keepWeak(retryData, typoLevel, vendorFilter);
                  // Typo retry found nothing in stock — try its out-of-stock half too.
                  return fetchJson(searchUrl(sentQuery, typoLevel, "out", vendorFilter), signal).then(function (retryOut) {
                    if (thisGeneration !== latestGeneration) return;
                    var retryOutRows = toProductRows(retryOut);
                    if (retryOut.found > 0 && retryOutRows.length && !esteRezultatSlab(sentQuery, retryOutRows)) {
                      renderResults(container, retryOutRows.slice(0, RESULT_LIMIT), displayQuery);
                      return;
                    }
                    return onMiss();
                  });
                });
              }
              if (probeVendor) {
                return retryPhase(probeVendor, function () { return retryPhase(null, renderWeak); });
              }
              return retryPhase(null, renderWeak);
            });
          });
      })
      .catch(function (err) {
        if (err.name === "AbortError") return; // superseded by a newer keystroke
        console.error("typesense-search:", err);
        /* Typesense is unreachable/misconfigured. Hand search back to the theme
         * rather than leaving the user with nothing: un-suppress so the native
         * predictive search runs on the next keystroke. Re-suppressed as soon as
         * a Typesense request succeeds again. */
        themeSearchSuppressed(false);
      });
  }

  /**
   * Dispatcher, added 2026-08-03 (docs/search-ranking-review-codex-response-
   * 2026-08-03.md, sections B/C). If the FULL normalized query is an exact known
   * vendor, run the consumer-first brand-intent branch; otherwise fall through to
   * the ordinary text search unchanged — this is the "if brand-intent is
   * uncertain, keep existing general search behaviour" requirement, satisfied by
   * construction: detectBrandIntent() only returns non-null on an exact match, so
   * every model/mixed query (verified: "kef ci200rr", "audio technica m50x",
   * "pro-ject at3600l") falls through here exactly as it did before this branch
   * existed.
   */
  /**
   * Căutare de brand care citește ORDINEA DIN COLECȚIE, nu din Typesense.
   *
   * DE CE (Tudor, 2026-08-20): dropdown-ul și pagina de colecție dădeau două
   * ordini diferite pentru aceeași întrebare „ce KEF-uri contează". Măsurat
   * atunci, primele 10 la KEF: 8 din 10 poziții difereau. Colecția are scorul
   * nocturn (vânzări cu decădere 90 zile + stoc + gamă + noutate) ȘI stratul de
   * merchandising — haloul Blade One Meta pe poziția 5, încastrabilele Ci
   * retrogradate. Typesense are `rank_score`, care e o implementare separată a
   * aceleiași idei, dar nu știe nimic despre merchandising.
   *
   * Varianta considerată și respinsă: să scriu scorul nocturn în `rank_score` la
   * fiecare sincronizare. Ar fi însemnat un al doilea consumator al scorului,
   * ținut în sincron prin cron — adică exact felul de duplicare care produce, în
   * timp, două surse de adevăr care diverg tăcut. Citirea directă din colecție
   * are o singură sursă, prin construcție.
   *
   * `/collections/<handle>/products.json` întoarce produsele ÎN ORDINEA colecției
   * (verificat pe 2026-08-20: primele 8 la KEF identice cu pagina, halo inclus).
   * E endpoint public al storefront-ului, deci nu cere cheie și nu costă o
   * interogare Typesense.
   *
   * ATENȚIE la capcana de unități din scraper/own-site.ts, valabilă și aici:
   * `products.json` întoarce prețul ca ȘIR ZECIMAL în lei („20999.00"), în timp
   * ce `/products/<handle>.js` îl întoarce ca întreg în bani. Aici e primul caz.
   *
   * Fail-open, ca tot restul fișierului: dacă brandul nu are colecție (404) sau
   * cererea eșuează, se cade pe `runBrandIntentSearch` — comportamentul de până
   * acum, nu o eroare pentru utilizator.
   */
  /**
   * Citește ordinea dintr-o COLECȚIE și o randează ca rezultate de căutare.
   *
   * Servește două intenții cu același mecanism:
   *   · brand    — `vendor` e numele mărcii, rândul de jos spune „Vezi toate cele N produse KEF"
   *   · categorie — `vendor` e null, rândul spune „Vezi toate cele N produse din Doze pickup"
   *
   * Motivul e același în ambele cazuri: colecția e locul unde trăiește ordinea
   * corectă (scor nocturn + merchandising). Căutarea nu recalculează, citește.
   */
  function runCollectionSearch(query, handle, canonicalVendor, container, thisGeneration, signal, eRaft) {
    /* Calea Typesense când colecția nu e folosită (.bg) sau cade (catch-ul de jos).
       O SINGURĂ funcție pentru ambele locuri. Până pe 19.10.2026: (1) catch-ul chema
       runBrandIntentSearch și pe CATEGORIE, unde `canonicalVendor` e null ⇒ filtrul
       vendor:=`null`, 0 rezultate exact când colecția nu răspundea; (2) RAFTUL marcă ×
       categorie („boxe kef", `eRaft`) cădea tot pe runBrandIntentSearch, care ignoră
       categoria ⇒ toată gama KEF — pe .bg MEREU, pe .ro când colecția cădea (Codex).
       Acum doar brandul pur merge pe căutarea de brand; categoria și raftul merg pe
       runTextSearch, adică exact unde ajunge runSearch fără colecție (raftul: vendor
       ca filtru + `product_type` din categorie, în runVendorScopedSearch). */
    function cadePeTypesense() {
      if (canonicalVendor && !eRaft) {
        runBrandIntentSearch(query, canonicalVendor, container, thisGeneration, signal);
      } else {
        runTextSearch(query, container, thisGeneration, signal);
      }
    }
    /* PE .bg calea asta NU se folosește (2026-08-26). `products.json` de pe
       storefront-ul bulgar servește titluri bulgare, dar PREȚURI ÎN RON — verificat:
       KEF KC92 vine cu 2373.95, care e prețul românesc, iar randarea îl eticheta
       „lei" sub un titlu bulgăresc. Nota din 24.08 („endpointul e deja conștient de
       piață") era adevărată doar pentru TITLURI; pentru prețuri, nu.
       Pe .bg mergem pe Typesense, care are `price_bgn` per document și trece prin
       displayPrice() — deci moneda nu poate ieși greșită. Costul: pe .bg ordinea e
       cea comercială din index, nu ordinea manuală a colecției. Compromis acceptat
       deliberat: un preț în moneda greșită e o eroare, o ordine ușor diferită nu. */
    if (currentMarket() !== "ro") {
      cadePeTypesense();
      return;
    }
    fetch("/collections/" + encodeURIComponent(handle) + "/products.json?limit=" + RESULT_LIMIT, { signal: signal })
      .then(function (res) {
        if (!res.ok) throw new Error("colecția " + handle + " a răspuns " + res.status);
        return res.json();
      })
      .then(function (data) {
        if (thisGeneration !== latestGeneration) return;
        var produse = (data && data.products) || [];
        if (!produse.length) throw new Error("colecția " + handle + " e goală");
        /* doar o listă NEGOALĂ dovedește că există colecția (vezi COLECTII_BRAND) */
        if (canonicalVendor) COLECTII_BRAND[handle] = true;

        var rows = produse.map(function (p) {
          var variante = p.variants || [];
          var cumparabile = variante.filter(function (v) { return v.available; });
          var pool = cumparabile.length ? cumparabile : variante;
          var preturi = pool.map(function (v) { return parseFloat(v.price) || 0; });
          var min = preturi.length ? Math.min.apply(null, preturi) : 0;
          var max = preturi.length ? Math.max.apply(null, preturi) : 0;
          var img = (p.images && p.images[0] && p.images[0].src) || "";
          return {
            doc: {
              product_id: p.id,
              title: p.title,
              handle: p.handle,
              image_url: img,
              /* AM26 2026-08-28 — `vendor` LIPSEA din obiectul ăsta sintetic, iar
                 asta rupea eyebrow-ul de brand exact pe interogarea „doar brand"
                 („kef", „bang & olufsen"): `splitBrand()` citeşte `doc.vendor`,
                 primea undefined şi întorcea titlul întreg, deci brandul nu mai
                 urca deasupra modelului. Pe „kef ls" mergea, fiindcă acolo
                 rândurile vin din Typesense, cu documente complete.
                 Raportat de Tudor; cauza confirmată de Codex pe cod.
                 Nu costă nicio cerere în plus: `canonicalVendor` e deja aici. */
              vendor: canonicalVendor || "",
              /* products.json NU expune metafield-uri, deci subtitlul nu vine de
                 aici. Rămâne gol până e hidratat din Typesense mai jos — mai bine
                 lipsă decât greşit. */
              subtitle: "",
              /* Tagurile (listă în products.json) — de aici citește etichetaBf() insigna BF (21.10.2026). */
              tags: p.tags || [],
            },
            price: min,
            hasRange: max > min,
            inStock: cumparabile.length > 0,
          };
        });

        /* `handle` se trimite MEREU, nu doar vendorul — pe calea de CATEGORIE
           („cabluri", „subwoofere") `canonicalVendor` e null, iar renderer-ul cere
           `vendor || handle` ca să deseneze rândul „Vezi toate". Fără handle,
           condiția era falsă și rândul dispărea tăcut exact pe categorii, deși
           codul de randare îl suporta de la început (are și ramura cu `titlu`).
           Semnalat de Tudor pe 2026-08-26: „la cabluri și cabluri subwoofer nu
           scrie jos să se vadă toate cablurile". */
        var brandLink = { vendor: canonicalVendor, handle: handle, titlu: CATEGORY_TITLES[handle] || "", total: 0 };
        /* RAFT marcă × categorie („accesorii pick up kanto" → accesorii-pick-up-kanto-audio): eticheta
           descrie RAFTUL, nu brandul. Înainte arăta „Vezi toate cele 46 produse Kanto Audio" deşi linkul
           ducea la cele 7 accesorii (08.10.2026, cerut de Tudor). Numărul şi titlul vin din colecţia-ţintă
           (`/collections/<h>.json`, ramura de categorie de mai jos) — NU dintr-un filtru pe tip: unele rute
           duc la colecţii de GRUP sau cu alt set de tipuri (ex. „casti ifi" → amplificatoare), unde un
           număr derivat din tip ar fi greşit (Codex, 08.10). `canonicalVendor` rămâne pentru hidratarea
           subtitlurilor şi COLECTII_BRAND; doar eticheta trece pe categorie. */
        /* `eRaft` = handle-ul categoriei detectate; titlul de rezervă (dacă colecţia nu răspunde) urmează
           convenţia titlurilor de colecţii. Titlul colecţiei, când vine, îl înlocuieşte (tradus pe piaţă). */
        if (eRaft) brandLink = { vendor: null, handle: handle, total: 0,
          titlu: ((CATEGORY_TITLES[eRaft] || "") + " " + canonicalVendor).trim() };
        /* AM26 2026-08-28 — subtitlurile lipseau pe interogarea „doar brand",
           fiindcă rândurile de aici vin din `/collections/<brand>/products.json`,
           iar acela NU expune metafield-uri (deci nici `custom.product_subtitle`).
           Le aducem din Typesense, unde există, ţintit pe exact produsele deja
           alese — `filter_by: product_id:[...]` — ca hidratarea să NU poată
           schimba ce produse se afişează sau în ce ordine. Ordinea rămâne cea a
           colecţiei, care e tot rostul acestei căi de cod.

           Pornită AICI, înaintea lanţului de mai jos, ca să plece în paralel cu
           cererea de total: aşteptarea e maximul celor două, nu suma.
           `include_fields` ţine răspunsul mic. Eşec tolerat: dacă pică, rândurile
           rămân fără subtitlu, adică exact starea de dinainte — niciodată cu
           subtitlu greşit. */
        var hidratareSubtitluri = Promise.resolve();
        if (canonicalVendor) {
          var idsProduse = rows
            .map(function (r) { return r.doc.product_id; })
            .filter(function (id) { return id != null; });
          if (idsProduse.length) {
            var paramsHidratare = brandSearchParams(
              canonicalVendor,
              "product_id:[" + idsProduse.join(",") + "]",
              idsProduse.length
            );
            paramsHidratare.set("include_fields", "product_id,subtitle");
            hidratareSubtitluri = fetchJson(brandSearchFetchUrl(paramsHidratare), signal)
              .then(function (dateHidratare) {
                var dupaId = {};
                (dateHidratare.grouped_hits || []).forEach(function (grup) {
                  var d = grup.hits && grup.hits[0] && grup.hits[0].document;
                  if (d && d.product_id != null) dupaId[String(d.product_id)] = d;
                });
                rows.forEach(function (r) {
                  var d = dupaId[String(r.doc.product_id)];
                  if (d && d.subtitle) r.doc.subtitle = d.subtitle;
                });
              })
              .catch(function () { /* fără subtitluri: rândul rămâne ca înainte */ });
          }
        }

        /* Totalul se cere doar pentru BRAND: `brandSearchParams(null, …)` ar
           produce filtrul „vendor:=`null`" și ar întoarce 0 — pe categorie
           numărul vine din colecție, mai jos. */
        (canonicalVendor && !eRaft
          ? fetchJson(brandSearchFetchUrl(brandSearchParams(canonicalVendor, "", 1)), signal)
              .then(function (allData) { brandLink.total = allData.found || 0; })
          : fetch("/collections/" + encodeURIComponent(handle) + ".json", { signal: signal })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (c) {
                if (c && c.collection && typeof c.collection.products_count === "number") {
                  brandLink.total = c.collection.products_count;
                  if ((eRaft || !brandLink.titlu) && c.collection.title) brandLink.titlu = c.collection.title;
                }
              }))
          .catch(function () { /* fără total: eticheta cade pe „Vezi toate produsele X" */ })
          .then(function () { return hidratareSubtitluri; })
          .then(function () {
            if (thisGeneration !== latestGeneration) return;
            themeSearchSuppressed(true);
            renderResults(container, rows, query, brandLink);
          });
      })
      .catch(function (err) {
        if (err.name === "AbortError") return; // superseded de o tastă nouă
        if (thisGeneration !== latestGeneration) return;
        /* Colecția lipsește, e goală, sau storefront-ul n-a răspuns. Cădem pe
           Typesense: căutarea de brand pentru brand, cea de text pentru categorie. */
        console.warn("typesense-search: ordinea din colecție indisponibilă, folosesc Typesense", err);
        cadePeTypesense();
      });
  }

  function runSearch(query) {
    var container = document.getElementById("livesearch");
    if (!container) return;

    if (currentRequestController) {
      currentRequestController.abort();
    }
    currentRequestController = new AbortController();
    var thisGeneration = ++latestGeneration;
    var signal = currentRequestController.signal;

    /* Categoria se verifică ÎNAINTEA brandului. Nu se suprapun azi (niciun brand nu
       se cheamă „doze"), dar ordinea e explicită ca să nu depindă de noroc dacă
       apare vreodată un brand cu nume de categorie. */
    var categoryHandle = detectCategoryIntent(query);
    if (categoryHandle) {
      runCollectionSearch(query, categoryHandle, null, container, thisGeneration, signal);
      return;
    }
    var canonicalVendor = detectBrandIntent(query);
    /* Un brand cu substituție configurată NU trece prin ruta de colecție:
       colecțiile lor nu există pe .ro (verificat 2026-08-26 — cambridge-audio,
       ruark-audio, eversolo, argon-audio dau toate 404), iar aliasurile scurte
       adăugate în aceeași zi („cambridge", „ruark", „argon", „evers") tocmai le-au
       făcut recunoscute ca brand-intent. Fără linia asta, cel mai căutat termen
       cu zero rezultate din magazin ar fi ajuns direct într-un 404.
       runTextSearch le duce pe drumul normal, care se termină în renderWeak() —
       adică exact în alternativele configurate. */
    if (canonicalVendor && !BRAND_SUBSTITUTIONS[canonicalVendor]) {
      runCollectionSearch(query, brandCollectionHandle(canonicalVendor), canonicalVendor, container, thisGeneration, signal);
      return;
    }
    /* Interogare MIXTĂ cu o colecție marcă × categorie proprie (T2-005):
       „boxe kef" arată raftul boxe-kef, nu o listă de text. Fără intrare în
       BRAND_CATEGORY_COLLECTIONS se cade pe runTextSearch, ca înainte — deci
       Enter și dropdown-ul ajung în ACELAȘI loc, în loc să se contrazică. */
    var scopeMixt = findVendorScope(stripStopwords(query));
    if (scopeMixt && !BRAND_SUBSTITUTIONS[scopeMixt.vendor]) {
      var catMixt = detectCategoryIntent(scopeMixt.rest);
      var handleMixt = brandCategoryCollectionHandle(scopeMixt.vendor, catMixt);
      if (handleMixt) {
        runCollectionSearch(query, handleMixt, scopeMixt.vendor, container, thisGeneration, signal, catMixt || true);
        return;
      }
    }
    runTextSearch(query, container, thisGeneration, signal);
  }

  /* The theme normally sets these open/close classes in its own `keyup` handler —
   * which we now stopImmediatePropagation() away, so we must set them ourselves or
   * the dropdown never opens. Mirrors custom-async.js:2752-2757 exactly. */
  function setDropdownOpen(open) {
    var form = document.getElementById("search");
    var html = document.documentElement;
    if (!form) return;
    if (open) {
      form.classList.add("full", "has-text", "not-empty");
      html.classList.add("search-full", "search-full-mode");
    } else {
      form.classList.remove("full", "has-text", "not-empty");
      html.classList.remove("search-full", "search-full-mode");
    }
  }

  function onInput(event) {
    var value = event.target.value.trim();
    clearTimeout(debounceTimer);
    setDropdownOpen(value.length > 0);
    if (value.length < MIN_QUERY_LENGTH) {
      // Invalidate any in-flight request too, not just the visible DOM — otherwise
      // a response that arrives after this clear can silently repopulate the box
      // with now-irrelevant results (e.g. user backspaced below MIN_QUERY_LENGTH
      // while a request was still in flight).
      latestGeneration++;
      if (currentRequestController) {
        currentRequestController.abort();
        currentRequestController = null;
      }
      var container = document.getElementById("livesearch");
      if (container) container.innerHTML = "";
      return;
    }
    debounceTimer = setTimeout(function () {
      runSearch(value);
    }, DEBOUNCE_MS);
  }

  function attach() {
    /* Re-assert suppression on every attach() call, not just the first: the header
     * (and its #search form) can be re-rendered by other theme JS, which would drop
     * the class. Guarded on typesenseHealthy so that after a Typesense failure this
     * poll does NOT immediately re-suppress and undo the fallback. */
    if (typesenseHealthy) themeSearchSuppressed(true);
    injectDropdownCss();

    var input = document.getElementById("search_main");
    if (!input || input.dataset.typesenseBound) return;
    input.dataset.typesenseBound = "1";

    /* Capture phase + stopImmediatePropagation on BOTH events the theme could use.
     * `no-autocomplete` above is the real fix (it stops the theme's fetch at the
     * source); this is defence-in-depth for the window where the header was just
     * re-rendered and the class has not been re-applied yet. Binding the theme's
     * own event (`keyup`) matters — that is the one the original implementation
     * missed. Nothing here touches custom-async.js; removing this script restores
     * the theme's native search exactly as before. */
    ["input", "keyup"].forEach(function (eventName) {
      input.addEventListener(
        eventName,
        function (e) {
          /* PANĂ TYPESENSE → predăm tastele temei (2026-08-30).
             Pe 30.08 clusterul a fost suspendat și dropdownul a rămas GOL pe live,
             deși codul de fail-open exista și `themeSearchSuppressed(false)` chiar
             se executase: scoate clasa `no-autocomplete`, dar acest listener
             continua să cheme `stopImmediatePropagation()` NECONDIȚIONAT, deci
             evenimentul nu ajungea niciodată la handlerul temei. Ridica bariera și
             ținea ușa închisă. Găsit de Codex la review, 30.08. */
          if (!typesenseHealthy) {
            failOpenToTheme();
            scheduleTypesenseRecoveryProbe(String(e.target.value || "").trim());
            return; // fără stopImmediatePropagation: tema își primește evenimentul
          }
          e.stopImmediatePropagation();
          onInput(e);
        },
        true
      );
    });

    /* ENTER pe un nume de brand EXACT duce la colecția brandului, nu la /search.
       Cerut de Tudor (2026-08-20): „când dau enter nu mă duce la colecția bună".

       De ce contează mai mult decât pare: /search?q=KEF folosește căutarea NATIVĂ
       Shopify, nu Typesense. Măsurat atunci — ignoră complet scorul (Q1 Meta primul,
       LS Luxe nici în primele 10 din 119), arată 11 produse RESIGILATE în rezultate
       (contrar deciziei lui Bogdan din 2026-08-06, „resigilatele nu trebuie să apară
       în liste de produse nicăieri") și listează Kanto/Klipsch/Wharfedale în filtrul
       de producător, fiindcă se caută în text liber. Colecția e răspunsul corect la
       „arată-mi KEF-urile" din toate cele trei puncte de vedere.

       Se aplică DOAR la potrivire exactă de brand — aceeași funcție care decide și
       conținutul dropdown-ului. „kef ls50" sau „kef ci200rr" merg mai departe la
       /search, neschimbat: acolo omul caută un produs, nu un raft.

       `keydown` în capturã, ca restul fișierului, ca să ajungem înaintea handler-ului
       de submit al temei. Modificatorii (Ctrl/Cmd/Shift/Alt) sunt lăsați în pace —
       cine ține Ctrl vrea taburi noi, nu navigare. */
    input.addEventListener(
      "keydown",
      function (e) {
        if (e.key !== "Enter" || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        var val = input.value || "";
        /* Categoria întâi, din același motiv ca în runSearch. */
        var cat = detectCategoryIntent(val);
        var vendor = cat ? null : detectBrandIntent(val);
        /* Extins 2026-08-26, după măsurarea paginii native de la Enter: interogările
           MIXTE („boxe klipsch", „casti b&w") cădeau pe /search, care ignoră scorul
           comercial, amestecă resigilatele cu produsele noi și returnează zgomot
           (măsurat: „telecomanda b&o" → brasston/blantr/bronze printre telecomenzi;
           „fiio" → căști Audio-Technica). Dropdown-ul rezolvase deja cazul prin
           findVendorScope(); Enter îl folosește acum pe același drum.

           DOAR când restul interogării e o categorie recunoscută, nu orice text:
           „boxe klipsch" = raftul Klipsch, dar „klipsch rp-8000f" e o căutare de
           produs și trebuie să rămână căutare. Fără colecție de brand cunoscută
           (VENDOR_COLLECTION_OVERRIDES / handle derivat) nu redirectăm — un 404 e
           mai rău decât o listă imperfectă. */
        /* `catDinScope` reține categoria din interogarea mixtă, ca să putem
           alege colecția marcă × categorie. Înainte se păstra doar vendorul,
           iar categoria — informația care face diferența între „raftul KEF" și
           „boxele KEF" — se arunca. */
        var catDinScope = null;
        if (!cat && !vendor) {
          var scope = findVendorScope(stripStopwords(val));
          if (scope) {
            var restCat = detectCategoryIntent(scope.rest);
            if (restCat) {
              vendor = scope.vendor;
              catDinScope = restCat;
            }
          }
        }
        /* Aceeași gardă ca în runSearch: brandurile cu substituție n-au colecție
           pe .ro (404 verificat). Enter pe ele lasă dropdown-ul să-și facă treaba
           și pagina nativă să răspundă, în loc să ducă într-o pagină inexistentă. */
        if (vendor && BRAND_SUBSTITUTIONS[vendor]) return;
        if (!cat && !vendor) return;
        /* Marcă × categorie are prioritate când există: „boxe kef" -> boxe-kef,
           nu kef. Fără intrare în listă, `destinatie` rămâne null și se cade
           exact pe comportamentul dinainte. */
        var destinatie = brandCategoryCollectionHandle(vendor, catDinScope);
        /* `boxe-toate` nu EXISTĂ ca pagină (verificat: 404). E doar intenţia
           „boxe, larg". Fără marcă — „boxe" simplu — destinaţia rămâne raftul
           general de boxe pasive, exact ca înainte. */
        var catReal = cat === "boxe-toate" ? "boxe-pasive" : cat;
        if (!destinatie && catReal) destinatie = catReal;
        if (!destinatie) {
          /* Doar ruta de BRAND are handle derivat din nume (vezi COLECTII_BRAND);
             categoriile și perechile marcă × categorie sunt colecții reale, verificate
             de auditul nocturn. Colecție inexistentă → căutarea normală a site-ului. */
          var hBrand = brandCollectionHandle(vendor);
          if (COLECTII_BRAND[hBrand] === false) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          if (COLECTII_BRAND[hBrand] === true) {
            window.location.href = "/collections/" + encodeURIComponent(hBrand);
            return;
          }
          /* Un singur drum în curs: Enter apăsat repetat cât durează verificarea ar
             porni altfel mai multe verificări și mai multe trimiteri (Codex, 06.10). */
          if (enterInCurs) return;
          enterInCurs = true;
          var form = input.form;
          verificaColectieBrand(hBrand).then(function (exista) {
            /* eliberat înainte de navigare: dacă un handler al temei oprește trimiterea,
               următorul Enter trebuie să funcționeze */
            enterInCurs = false;
            if (exista === true) {
              window.location.href = "/collections/" + encodeURIComponent(hBrand);
            } else if (form && form.requestSubmit) {
              form.requestSubmit();
            } else {
              window.location.href = linkCautare(val);
            }
          });
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = "/collections/" + encodeURIComponent(destinatie);
      },
      true
    );
  }

  /* ── Pagina de rezultate de la Enter (2026-08-29, Tudor) ────────────────────
     Dropdown-ul era reparat, pagina de la Enter nu. Măsurat înainte de fixul ăsta:

       /search?q=hd599     → Pro-Ject BT Box E HD, un cablu, un receiver Onkyo…
                             casca HD 599 nu apărea DELOC
       /search?q=momentum 5 → MOMENTUM **4** Wireless primul

     Adică jumătate din drumul spre cumpărare mergea pe search-ul vechi.

     DE CE NU O PAGINĂ NOUĂ, randată de noi. Măsurat pe cele patru interogări:
     pagina nativă CONȚINE deja produsele bune în 3 din 4 cazuri (5–11 carduri) —
     greșită e ORDINEA, nu setul. Iar cardurile temei duc culori, quick-buy,
     recenzii, filtre și sortare; refăcute de mână ar fi însemnat o regresie mare
     ca să reparăm o problemă mică. Deci: cardurile rămân ale temei, ordinea devine
     a noastră. Același mecanism pe care îl folosește deja am-typesense-filters.js
     pe colecții (`.l4cl > li[data-product-id]`).

     CE NU FACE, ca să fie spus: reordonează DOAR pagina 1, și doar când clientul
     n-a ales el o sortare. Un produs pe care Typesense îl vrea primul dar care e
     pe pagina 3 a rezultatelor native nu poate fi adus — pentru asta ar trebui
     chiar pagina randată de noi. Pe interogările de model, unde contează, totalul
     e oricum sub o pagină. */
  var GRILA_REZULTATE = ".l4cl";

  /** Interogarea paginii de rezultate, citită DOAR din URL — fără DOM, ca cererea
   *  să poată porni la parsarea scriptului, nu la DOMContentLoaded. Cardurile se
   *  mișcă oricum după ce pagina s-a randat; cu cât cererea pleacă mai devreme, cu
   *  atât fereastra în care se vede ordinea veche e mai scurtă. */
  function interogareaPaginii() {
    if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(location.pathname)) return null;
    var p = new URLSearchParams(location.search);
    var q = (p.get("q") || "").trim();
    if (!q) return null;
    /* Sortare aleasă de om — nu i-o luăm. La fel pagina 2+: reordonarea ei ar
       amesteca două ordini diferite în aceeași listă. */
    if (p.get("sort_by")) return null;
    if (Number(p.get("page") || "1") !== 1) return null;
    return q;
  }

  function grilaPaginii() {
    var grila = document.querySelector(GRILA_REZULTATE);
    if (!grila) return null;
    var carduri = [].slice.call(grila.querySelectorAll(":scope > li[data-product-id]"));
    if (carduri.length < 2) return null;
    return { grila: grila, carduri: carduri };
  }

  /** Rândurile pe care le-ar da căutarea noastră pentru interogarea asta.
   *  O SINGURĂ cerere, fără filtru de stoc: `in_stock_rank` din sort_by ține
   *  oricum epuizatele mai jos, iar aici nu randăm noi lista, doar o ordonăm. */
  function randuriPentruPagina(q) {
    var trimis = stripStopwords(q);
    /* Brand de DOUĂ+ cuvinte scris întreg („kanto mounts", „jbl synthesis"): filtrul pe brandul
       întreg ÎNAINTEA despărțirii. findVendorScope verifică doar spanuri mai scurte decât
       interogarea, deci la exact două tokenuri ar fi întors aliasul de un cuvânt („kanto" →
       Kanto Audio) + restul („mounts") — semnalat de Codex, 08.10.2026. Interogările de un
       singur cuvânt rămân pe drumul de dinainte (fără filtru de vendor). */
    var brandIntreg = trimis.split(/\s+/).length >= 2 ? detectBrandIntent(trimis) : null;
    var scope = brandIntreg ? null : findVendorScope(trimis);
    var url = brandIntreg
      ? searchUrl(trimis, false, null, brandIntreg)
      : scope
      ? searchUrl(scope.rest, false, null, scope.vendor)
      : searchUrl(trimis, false, null);
    return fetchJson(url, null).then(function (data) {
      /* toProductRows() colapsează familiile la ieșire — regulă de DROPDOWN. Pe o
         listare o vrem desfăcută la loc, altfel doar liderul familiei primește
         poziție și celelalte configurații cad la coadă.
         Prins pe „beolab 8": Beolab 19 ieșea al doilea, între două Beolab 8. */
      var rows = desfaToateFamiliile(toProductRows(data));
      /* Codul de model scris lipit, la fel ca în dropdown: dacă tot ce am găsit
         doar menționează interogarea, forma despărțită e răspunsul. Aici se
         schimbă URL-ul, nu doar lista — altfel pagina ar rămâne pe o interogare
         despre care tocmai am aflat că nu are răspuns. Acoperă și intrările din
         afara site-ului (semn de carte, link), nu doar tastarea în casetă. */
      var spart = modelSpart(trimis);
      if (spart && spart !== trimis && numaiMentiuni(rows)) {
        return { redirect: spart };
      }
      return { rows: ordoneazaRanduri(rows, q) };
    });
  }

  /** Inversul colapsării: fiecare familie își recapătă membrii. Pentru rândurile
   *  fără membri (familie de unul singur) nu se schimbă nimic. */
  function desfaToateFamiliile(rows) {
    var out = [];
    (rows || []).forEach(function (r) {
      if (r && r.membri && r.membri.length > 1) out = out.concat(r.membri);
      else out.push(r);
    });
    return out;
  }

  /* Cererea pleacă ACUM, la parsarea scriptului; DOM-ul se ia mai târziu. */
  var randuriPagina = null;
  (function () {
    var q = interogareaPaginii();
    if (!q) return;
    /* `catch` ATAȘAT AICI, nu doar la consum: promisiunea trăiește până la
       DOMContentLoaded, iar o respingere fără handler în fereastra aia iese ca
       „unhandled rejection". Ordonarea e un plus — dacă pică, pagina rămâne exact
       cum a servit-o Shopify. */
    randuriPagina = randuriPentruPagina(q).catch(function (err) {
      console.error("typesense-search: ordonarea paginii de rezultate a eșuat", err);
      return { rows: [] };
    });
  })();

  function reordoneazaPaginaDeRezultate() {
    if (!randuriPagina) return;
    randuriPagina
      .then(function (out) {
        var ctx = grilaPaginii();
        if (out.redirect) {
          var u = new URL(location.href);
          u.searchParams.set("q", out.redirect);
          location.replace(u.toString());
          return;
        }
        if (!ctx) return;
        var dupaId = {};
        ctx.carduri.forEach(function (li) {
          dupaId[li.getAttribute("data-product-id")] = li;
        });
        var folosit = {}, noua = [];
        out.rows.forEach(function (r) {
          var id = r.doc && r.doc.product_id != null ? String(r.doc.product_id) : null;
          if (!id || folosit[id] || !dupaId[id]) return;
          folosit[id] = 1;
          noua.push(dupaId[id]);
        });
        /* Cardurile pe care căutarea noastră nu le cunoaște NU se ascund — rămân,
           în ordinea lor, după cele ordonate. Shopify poate ști ceva ce noi nu
           știm, iar o listă mai scurtă e o regresie sigură. */
        ctx.carduri.forEach(function (li) {
          if (!folosit[li.getAttribute("data-product-id")]) noua.push(li);
        });
        var schimbat = noua.some(function (li, i) { return li !== ctx.carduri[i]; });
        if (!schimbat) return; // aceeași ordine: nu mișcăm DOM-ul degeaba
        var frag = document.createDocumentFragment();
        noua.forEach(function (li) { frag.appendChild(li); });
        ctx.grila.insertBefore(frag, ctx.grila.firstChild);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      attach();
      reordoneazaPaginaDeRezultate();
    });
  } else {
    attach();
    reordoneazaPaginaDeRezultate();
  }
  // The header (and its search form) can be re-rendered by other theme JS (e.g. after
  // a section reload) — a short poll re-attaches if the input was replaced. Cheap and
  // bounded, matching the defensive style of the theme's own async loaders.
  setInterval(attach, 2000);
})();
